import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { Observable, BehaviorSubject, from, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { OfflineManagerService } from './offline-manager.service';
import { OfflineApiService } from './offline-api.service';
import { ApiService } from './api.service';
import { SharedDataService } from './shared-data.service';
import { EnumService } from './enum.service';
import { UtilService } from './util.service';
import { FilehandlerService } from './filehandler.service';
import { StaticDataService } from './static-data.service';
import { Capacitor } from '@capacitor/core';
import * as JSZip from 'jszip';
import * as moment from 'moment';
import { ToastController } from '@ionic/angular';
import { Response } from '../_models';
import { DeviceEntityDetail } from '../_models/deviceEntityDetail';
import { DeviceDetailData } from '../_models/offline/DeviceDetailData';
import * as signalR from '@microsoft/signalr';

export interface SyncStatus {
  isSyncing: boolean;
  progress: number;
  status: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundSyncService {
  /**
   * Retry a promise-returning function with exponential backoff
   * @param fn Function to retry
   * @param maxRetries Maximum number of retries (default: 2)
   * @param initialDelay Initial delay in ms (default: 2000)
   * @returns Promise result
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 2,
    initialDelay: number = 2000
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        
        // Check if error is retryable (network errors, 5xx errors)
        const isRetryable = this.isRetryableError(error);
        
        if (!isRetryable || attempt === maxRetries) {
          throw error; // Don't retry non-retryable errors or if max retries reached
        }
        
        // Calculate delay with exponential backoff
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`🔄 [RETRY] Attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  /**
   * Sync state tracking methods
   */
  private readonly SYNC_STATE_KEY = 'background_sync_state';
  
  private saveSyncState(state: string): void {
    try {
      localStorage.setItem(this.SYNC_STATE_KEY, state);
      console.log(`💾 [SYNC STATE] Saved state: ${state}`);
    } catch (error) {
      console.error('❌ [SYNC STATE] Error saving state:', error);
    }
  }

  private getSyncState(): string | null {
    try {
      return localStorage.getItem(this.SYNC_STATE_KEY);
    } catch (error) {
      console.error('❌ [SYNC STATE] Error reading state:', error);
      return null;
    }
  }

  private clearSyncState(): void {
    try {
      localStorage.removeItem(this.SYNC_STATE_KEY);
      console.log('💾 [SYNC STATE] Cleared state');
    } catch (error) {
      console.error('❌ [SYNC STATE] Error clearing state:', error);
    }
  }

  /**
   * Check if an error is retryable (network errors, 5xx server errors)
   */
  private isRetryableError(error: any): boolean {
    // Network errors (no HTTP status)
    if (!error?.status && !error?.http_status) {
      return true;
    }
    
    // HTTP 5xx server errors
    const status = error?.status || error?.http_status;
    if (status >= 500 && status < 600) {
      return true;
    }
    
    // HTTP 0 (network failure)
    if (status === 0) {
      return true;
    }
    
    // Don't retry 4xx errors (client errors like 404, 401, 400)
    return false;
  }

  private syncStatus$ = new BehaviorSubject<SyncStatus>({
    isSyncing: false,
    progress: 0,
    status: 'idle',
  });

  private isSyncing = false;
  private isBackgroundSync = false; // Flag to indicate background sync (no full screen loader)
  private lastSyncTimestamp: string = null;
  private pendingChangesInterval: any = null; // Interval for periodic pending changes check
  private signalRConnection: signalR.HubConnection = null;
  private signalRInitRetryCount: number = 0;
  private readonly MAX_SIGNALR_RETRIES = 10; // Max retries for SignalR initialization

  constructor(
    private http: HttpClient,
    private offlineManager: OfflineManagerService,
    private offlineApi: OfflineApiService,
    private apiService: ApiService,
    private sharedDataService: SharedDataService,
    private filehandlerService: FilehandlerService,
    private utilService: UtilService,
    private toastController: ToastController
  ) {
    // Load last sync timestamp
    const lastSync = localStorage.getItem(
      EnumService.LocalStorageKeys.SYNC_DATE_TIME
    );
    if (lastSync) {
      this.lastSyncTimestamp = lastSync;
    }

    // Initialize SignalR connection when online
    this.initializeSignalR();
  }

  /**
   * Observable for sync status
   */
  getSyncStatus(): Observable<SyncStatus> {
    return this.syncStatus$.asObservable();
  }

  /**
   * Check if database is empty
   * Public method that can be called from outside the service
   */
  async isDatabaseEmpty(): Promise<boolean> {
    try {
      console.log('🔍 [DATABASE CHECK] Checking if database is empty...');
      const result: any = await this.offlineManager.dbQuery(
        'SELECT COUNT(*) as count FROM DeviceDetails'
      );
      const count =
        result.rows && result.rows.length > 0
          ? result.rows.item(0).count
          : result[0]?.count || 0;
      console.log('🔍 [DATABASE CHECK] DeviceDetails count:', count);
      return count === 0;
    } catch (error) {
      console.error('🔍 [DATABASE CHECK] Error checking database:', error);
      // If table doesn't exist or database not ready, consider it empty
      return true;
    }
  }

  /**
   * Start background sync - automatically determines full or incremental
   */
  private async validateDedicatedModeEntityAfterSync(): Promise<void> {
    if (
      !this.sharedDataService.dedicatedMode ||
      !this.sharedDataService.dedicatedModeLocationUse
    ) {
      return;
    }

    try {
      let assignedEntities = this.sharedDataService
        .dedicatedModeAssignedEntities as DeviceEntityDetail[];

      const dbEntities = (await this.offlineManager.getAssignedEnitities()) as DeviceEntityDetail[];
      if (dbEntities?.length) {
        assignedEntities = dbEntities;
        this.sharedDataService.dedicatedModeAssignedEntities = dbEntities;
        localStorage.setItem(
          EnumService.LocalStorageKeys.DEDICATED_MODE_ASSIGNED_ENTITIES,
          JSON.stringify(dbEntities)
        );
      }

      this.sharedDataService.redirectToChooseLocationIfCurrentEntityRemoved(
        assignedEntities
      );
    } catch (error) {
      console.error(
        '❌ [SYNC] Error validating dedicated mode entity after sync:',
        error
      );
    }
  }

  async startBackgroundSync(): Promise<void> {
    const offlineEnabledFlag = localStorage.getItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY);
    if (offlineEnabledFlag === 'false') {
      console.log('🔄 [BACKGROUND SYNC] isOfflineEnabled is false — skipping sync');
      return;
    }

    if (this.isSyncing) {
      console.log('Sync already in progress');
      return;
    }

    // Check network
    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      console.log('No network connection, skipping sync');
      return;
    }

    // Check if sync is already in progress BEFORE setting the flag
    if (this.isSyncing) {
      console.log('🔄 [BACKGROUND SYNC] Sync already in progress, skipping startBackgroundSync');
      return;
    }

    this.isSyncing = true;
    
    // Save sync state at start (before any operations)
    // This allows us to detect incomplete sync even if API call fails
    this.saveSyncState('sync_started');
    
    this.updateSyncStatus({
      isSyncing: true,
      progress: 0,
      status: 'checking_database',
    });

    try {
      // Fetch remote CI/CO changes ONCE, before pushing anything.
      // This snapshot must not include our own offline CO/CI pushes — if we fetched it after
      // syncPendingCheckOuts, the CO we just pushed could appear (without deviceUniqueID on the
      // server response) and be misread as a "remote CO from another device", causing the
      // subsequent CI to be incorrectly skipped.
      const remoteCiCoChanges = await this.fetchRemoteCiCoChanges();

      // Sync pending checkouts FIRST so server check-in state is cleared before re-check-ins
      console.log('📤 [SYNC] Starting sync of pending checkouts...');
      try {
        await this.syncPendingCheckOuts(remoteCiCoChanges);
        console.log('📤 [SYNC] Completed sync of pending checkouts');
      } catch (checkoutSyncError) {
        console.error('❌ [SYNC] Error during checkout sync (non-blocking):', checkoutSyncError);
      }

      // Sync pending check-ins after checkouts so the server is ready to accept re-check-ins
      console.log('📤 [SYNC] Starting sync of pending check-ins...');
      try {
        await this.syncPendingCheckIns(remoteCiCoChanges);
        console.log('📤 [SYNC] Completed sync of pending check-ins');
      } catch (checkInSyncError) {
        console.error('❌ [SYNC] Error during check-in sync (non-blocking):', checkInSyncError);
      }

      // Sync pending sign-offs (before full sync operations)
      console.log('📤 [SYNC] Starting sync of pending sign-offs...');
      try {
        await this.syncPendingSignOffs();
        console.log('📤 [SYNC] Completed sync of pending sign-offs');
      } catch (signOffSyncError) {
        console.error('❌ [SYNC] Error during sign-off sync (non-blocking):', signOffSyncError);
        // Don't throw - allow main sync to complete even if sign-off sync fails
      }


      const isEmpty = await this.isDatabaseEmpty();

      if (isEmpty) {
        // For initial sync (empty database), use performFullSync which deletes all and inserts all
        // performFullSyncInBackground is for when GetPendingChanges is not empty (comparison-based)
        // Ensure isBackgroundSync is false for initial sync (so loaders work correctly)
        this.isBackgroundSync = false;
        await this.performFullSync();
        // After full sync, database is no longer empty, so start periodic check
        this.startPeriodicPendingChangesCheck();
      } else {
        // Database has data - check GetPendingChanges first
        // If GetPendingChanges is not empty, use comparison-based sync (performFullSyncInBackground)
        // Otherwise, use incremental sync (performIncrementalSync)
        const deviceId = this.sharedDataService.deviceUID;
        const lastSyncDate = this.lastSyncTimestamp || moment().subtract(1, 'day').toISOString();

        try {
          console.log('🔄 [BACKGROUND SYNC] Checking GetPendingChanges before deciding sync method...');
          let pendingChanges: any = await this.retryWithBackoff(async () => {
            return await this.offlineApi
              .getPendingChanges(deviceId, lastSyncDate)
              .toPromise();
          });
          
          // Normalize the response structure
          if (pendingChanges) {
            if (pendingChanges.statusCode !== undefined && pendingChanges.StatusCode === undefined) {
              pendingChanges.StatusCode = pendingChanges.statusCode;
            }
            if (pendingChanges.result !== undefined && pendingChanges.Result === undefined) {
              pendingChanges.Result = pendingChanges.result;
            }
          }
          
          if (
            pendingChanges &&
            pendingChanges.StatusCode === EnumService.ApiResponseCode.RequestSuccessful &&
            pendingChanges.Result
          ) {
            const result = pendingChanges.Result || {};
            const changes = result.changes || [];
            
            if (changes.length > 0) {
              console.log(`🔄 [BACKGROUND SYNC] GetPendingChanges returned ${changes.length} changes, using full sync (initial sync API without comparison)`);
              // Use full sync (same as initial sync) when GetPendingChanges is not empty
              // This will use GET /DeviceSync/offline/ and do a full sync without comparison
              this.isBackgroundSync = true; // Set flag for background sync (header loader)
              await this.performFullSync();
            } else {
              console.log('🔄 [BACKGROUND SYNC] GetPendingChanges returned no changes, using incremental sync');
              // Use incremental sync when GetPendingChanges is empty
              await this.performIncrementalSync();
            }
          } else {
            console.log('🔄 [BACKGROUND SYNC] GetPendingChanges failed or invalid response, using incremental sync');
            // Fallback to incremental sync if GetPendingChanges fails
            await this.performIncrementalSync();
          }
        } catch (pendingChangesError) {
          console.error('🔄 [BACKGROUND SYNC] Error checking GetPendingChanges, falling back to incremental sync:', pendingChangesError);
          // Fallback to incremental sync if GetPendingChanges check fails
          await this.performIncrementalSync();
        }
        
        // After sync, database still has data, so start/restart periodic check
        this.startPeriodicPendingChangesCheck();
      }

      await this.validateDedicatedModeEntityAfterSync();

      this.updateSyncStatus({
        isSyncing: false,
        progress: 100,
        status: 'completed',
      });

      // lastSyncTimestamp is already set to serverTime by performFullSync / performIncrementalSync.
      // Persist it to localStorage so it survives app restarts.
      if (this.lastSyncTimestamp) {
        localStorage.setItem(
          EnumService.LocalStorageKeys.SYNC_DATE_TIME,
          this.lastSyncTimestamp
        );
      }

      // Clear sync state on successful completion (for both full and incremental sync)
      this.clearSyncState();

      // Reload offlineDeviceDetailData so offline sign-off can access timeDifference
      this.offlineManager.getDeviceDetail().then((detail: DeviceDetailData) => {
        this.offlineManager.offlineDeviceDetailData = detail;
      }).catch(() => {});
    } catch (error) {
      console.error('❌ [SYNC] Background sync error:', error);
      // Note: syncPendingCheckOuts/CheckIns/SignOffs were already called in the try block above.
      // Do NOT call them again here — a second syncPendingCheckIns call causes duplicate CI
      // pushes, which triggers 412 "already checked in" from the server and prevents
      // subsequent users in the queue from being processed.
      
      // Update sync status (no need to hide loader since we're using header loader)
      this.updateSyncStatus({
        isSyncing: false,
        progress: 0,
        status: 'failed',
        error: error?.message || 'Sync failed',
      });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Perform full sync (when database is empty)
   * Uses getSyncData with SyncDataRequest for initial data load
   */
  private async performFullSync(): Promise<void> {
    // Loading indicator is already shown in startBackgroundSync() when isEmpty is true
    // No need to show it again here to avoid duplicate loaders
    
    // Note: sync_started state is already saved in startBackgroundSync()
    // This ensures we can detect incomplete sync even if API call fails
    
    this.updateSyncStatus({
      isSyncing: true,
      progress: 10,
      status: 'full_sync_started',
    });

    const deviceId = this.sharedDataService.deviceUID;
    const companyId = this.sharedDataService.dedicatedModeDeviceDetailData?.companyID;

    if (!companyId) {
      // Only hide loading if not a background sync (background sync uses header loader)
      if (!this.isBackgroundSync) {
        this.utilService.hideLoading();
      }
      throw new Error('Company ID not available for sync');
    }

    // Check sync state to see if we can resume from a previous attempt
    const savedState = this.getSyncState();
    console.log('💾 [SYNC STATE] Current state:', savedState || 'none');

    try {
      // Step 1: Get all sync data using SyncDataRequest
      this.updateSyncStatus({
        isSyncing: true,
        progress: 20,
        status: 'downloading_data',
      });

      console.log('📥 [FULL SYNC] Calling getSyncData with SyncDataRequest...');
      let syncData: any;
      let getSyncDataSucceeded = false; // Flag to track if getSyncData succeeded
      
      try {
        syncData = await this.retryWithBackoff(async () => {
          return await this.offlineApi
            .getSyncData({
              deviceId: deviceId,
              companyId: companyId,
              includeLocations: true,
              includeForms: true,
              includeDocuments: true,
            })
            .toPromise();
        });
        console.log('📥 [FULL SYNC] getSyncData response received');
        console.log('📥 [FULL SYNC] Response structure (before normalization):', {
          hasStatusCode: !!syncData?.StatusCode,
          hasStatusCodeLower: !!syncData?.statusCode,
          StatusCode: syncData?.StatusCode,
          statusCode: syncData?.statusCode,
          hasResult: !!syncData?.Result,
          hasResultLower: !!syncData?.result,
          resultKeys: syncData?.Result ? Object.keys(syncData.Result) : syncData?.result ? Object.keys(syncData.result) : [],
          allKeys: syncData ? Object.keys(syncData) : []
        });

        // Keep loading indicator visible - don't hide yet, sync is still in progress
        // It will be hidden after all data is inserted and sync completes

        // Normalize the response structure FIRST - handle both StatusCode/Result and statusCode/result
        if (syncData) {
          // If response has lowercase properties, normalize to uppercase (API returns statusCode/result)
          if (syncData.statusCode !== undefined && syncData.StatusCode === undefined) {
            syncData.StatusCode = syncData.statusCode;
            console.log('📥 [FULL SYNC] Normalized statusCode -> StatusCode:', syncData.StatusCode);
          }
          if (syncData.result !== undefined && syncData.Result === undefined) {
            syncData.Result = syncData.result;
            console.log('📥 [FULL SYNC] Normalized result -> Result, keys:', syncData.Result ? Object.keys(syncData.Result) : []);
          }
        }

        console.log('📥 [FULL SYNC] Response structure (after normalization):', {
          StatusCode: syncData?.StatusCode,
          hasResult: !!syncData?.Result,
          resultKeys: syncData?.Result ? Object.keys(syncData.Result) : []
        });
        
        // Log actual data counts from API response
        if (syncData?.Result) {
          console.log('📥 [FULL SYNC] API Response data counts:', {
            locations: syncData.Result.locations?.length || 0,
            forms: syncData.Result.forms?.length || 0,
            documents: syncData.Result.documents?.length || 0,
            formBuilderDetails: syncData.Result.formBuilderDetails?.length || 0,
            allResultKeys: Object.keys(syncData.Result)
          });
        }

        // Check if response is successful (200 equivalent)
        if (
          syncData &&
          syncData.StatusCode === EnumService.ApiResponseCode.RequestSuccessful &&
          syncData.Result
        ) {
          // getSyncData succeeded - mark flag and continue with new sync flow
          getSyncDataSucceeded = true;
          console.log('✅ [FULL SYNC] getSyncData succeeded, proceeding with new sync flow');
          // Save state: API data fetched successfully
          this.saveSyncState('api_fetched');
        } else {
          // HTTP succeeded but StatusCode indicates failure or no data
          console.log('⚠️ [FULL SYNC] getSyncData returned non-successful StatusCode or no Result:', {
            StatusCode: syncData?.StatusCode,
            expectedStatusCode: EnumService.ApiResponseCode.RequestSuccessful,
            hasResult: !!syncData?.Result
          });
          // Only hide loading if not a background sync (background sync uses header loader)
          if (!this.isBackgroundSync) {
            this.utilService.hideLoading();
          }
          await this.showErrorMessage('Sync failed. Please try again.');
          throw new Error('Failed to get sync data - non-successful StatusCode or no data');
        }
      } catch (apiError) {
        // Only fallback to old sync if getSyncData actually failed (HTTP error or network error)
        // NOT if getSyncData succeeded but later steps failed
        if (!getSyncDataSucceeded) {
          // Only hide loading if not a background sync (background sync uses header loader)
          if (!this.isBackgroundSync) {
            this.utilService.hideLoading();
          }
          
          // COMMENTED OUT: Fallback to old sync API when getSyncData fails
          // If getSyncData fails, fallback to old sync API
          // console.log('⚠️ [FULL SYNC] getSyncData failed, falling back to old sync API...');
          // console.error('getSyncData error:', apiError);
          
          // try {
          //   // performOldSync handles its own loading and toast messages
          //   await this.performOldSync();
          //   return; // Exit early since old sync completed successfully
          // } catch (oldSyncError) {
          //   console.error('Old sync also failed:', oldSyncError);
          //   // performOldSync already shows error message, so just throw
          //   throw oldSyncError;
          // }
          
          // Instead of falling back, just throw the error
          console.error('❌ [FULL SYNC] getSyncData failed:', apiError);
          throw apiError;
        } else {
          // getSyncData succeeded but something else failed - don't fallback to old sync
          console.error('❌ [FULL SYNC] Error occurred after getSyncData succeeded:', apiError);
          throw apiError;
        }
      }
      
      // Only proceed if getSyncData succeeded
      if (!getSyncDataSucceeded || !syncData) {
        throw new Error('getSyncData did not succeed');
      }

      // Step 2: Transform API response to match expected structure
      this.updateSyncStatus({
        isSyncing: true,
        progress: 60,
        status: 'preparing_data',
      });

      const apiResult = syncData.Result;
      if (!apiResult) {
        throw new Error('No data received from sync API');
      }

      // Transform API response structure to match DeviceOfflineDetailViewModels
      // API returns: { locations: [], forms: [], documents: [] }
      // Expected: { deviceLocationList: [], deviceAvailableFormList: [], deviceAvailableDocumentList: [], deviceDetailData: {} }
      
      // Create deviceDetailData from dedicatedModeDeviceDetailData if available
      let deviceDetailData: any = null;
      const dedicatedDeviceData = this.sharedDataService.dedicatedModeDeviceDetailData;
      if (dedicatedDeviceData) {
        deviceDetailData = {
          activationDate: dedicatedDeviceData.activationDate || '',
          companyFolderName: dedicatedDeviceData.companyFolderName || '',
          companyID: dedicatedDeviceData.companyID || 0,
          createdBy: dedicatedDeviceData.createdBy || '',
          createdDate: dedicatedDeviceData.createdDate || '',
          deviceID: dedicatedDeviceData.deviceID || 0,
          deviceName: dedicatedDeviceData.deviceName || '',
          deviceUniqueID: dedicatedDeviceData.deviceUniqueID || '',
          entities: dedicatedDeviceData.entities || null,
          entityNames: dedicatedDeviceData.entityNames || null,
          formattedLastSynchDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          lastSeenDate: dedicatedDeviceData.lastSeenDate || '',
          lastSynchDate: moment().toISOString(),
          modifiedBy: dedicatedDeviceData.modifiedBy || '',
          modifiedDate: dedicatedDeviceData.modifiedDate || '',
          noOfInventoryItems: dedicatedDeviceData.noOfInventoryItems || 0,
          noOfLocations: (apiResult.locations || apiResult.deviceLocationDetailList || [])?.length || 0,
          noOfPorjects: dedicatedDeviceData.noOfPorjects || 0,
          timeDifference: ''
        };
        // TODO: remove ?? false once backend returns isOfflineEnabled in /DeviceSync/offline/
        deviceDetailData.isOfflineEnabled = apiResult.deviceDetailData?.isOfflineEnabled ?? false;
        console.log('📥 [FULL SYNC] Created deviceDetailData from dedicatedModeDeviceDetailData');
      }

      // Transform forms: map entityID + entityType to locationID/projectID/inventoryItemID
      // API returns deviceAvailableFormList, deviceAvailableFormDetailList, or forms
      let transformedForms: any[] = [];
      try {
        const formsArray = apiResult.forms || apiResult.deviceAvailableFormList || apiResult.deviceAvailableFormDetailList || [];
        transformedForms = formsArray.map((form: any) => {
          try {
            const transformed = { ...form };
            
            // Only transform if entityType/entityID exist AND locationID/projectID/inventoryItemID are not already set
            if (form.entityType && form.entityID !== null && form.entityID !== undefined) {
              // Only map entityType/entityID if locationID/projectID/inventoryItemID are not already set
              if (form.locationID === null || form.locationID === undefined || form.locationID === 0) {
                if (form.entityType === 'Location') {
                  transformed.locationID = form.entityID;
                  transformed.projectID = form.projectID || null;
                  transformed.inventoryItemID = form.inventoryItemID || null;
                } else if (form.entityType === 'Project') {
                  transformed.locationID = form.locationID || null;
                  transformed.projectID = form.entityID;
                  transformed.inventoryItemID = form.inventoryItemID || null;
                } else if (form.entityType === 'InventoryItem') {
                  transformed.locationID = form.locationID || null;
                  transformed.projectID = form.projectID || null;
                  transformed.inventoryItemID = form.entityID;
                }
                // If entityType doesn't match, preserve existing values
              }
              // If locationID/projectID/inventoryItemID are already set, preserve them
            }
            // If no entityType/entityID, preserve existing locationID/projectID/inventoryItemID values
            // Ensure they're at least set to 0 or null if completely missing
            if (transformed.locationID === undefined) transformed.locationID = form.locationID || null;
            if (transformed.projectID === undefined) transformed.projectID = form.projectID || null;
            if (transformed.inventoryItemID === undefined) transformed.inventoryItemID = form.inventoryItemID || null;
            
            return transformed;
          } catch (formError) {
            console.error('❌ Error transforming form:', formError, form);
            // Return original form if transformation fails
            return form;
          }
        });
        console.log(`✅ [FULL SYNC] Transformed ${transformedForms.length} forms`);
      } catch (formsError) {
        console.error('❌ [FULL SYNC] Error transforming forms array:', formsError);
        // Fallback to original forms if transformation fails
        transformedForms = apiResult.forms || apiResult.deviceAvailableFormList || apiResult.deviceAvailableFormDetailList || [];
      }
      
      // Transform documents: map entityID + entityType to locationID/projectID/inventoryItemID
      // API returns deviceAvailableDocumentList, deviceAvailableDocumentDetailList, or documents
      let transformedDocuments: any[] = [];
      try {
        const documentsArray = apiResult.documents || apiResult.deviceAvailableDocumentList || apiResult.deviceAvailableDocumentDetailList || [];
        transformedDocuments = documentsArray.map((document: any) => {
          try {
            const transformed = { ...document };
            
            // Extract filename from documentDownloadUrl if it exists and update documentFileName
            if (transformed.documentDownloadUrl) {
              // Extract filename from path (before query params if present)
              const urlPath = transformed.documentDownloadUrl.split('?')[0];
              const fileNameFromUrl = urlPath.split('/').pop();
              if (fileNameFromUrl && fileNameFromUrl !== transformed.documentFileName) {
                console.log(`🔄 [SYNC] Updating documentFileName from URL: ${transformed.documentFileName} -> ${fileNameFromUrl}`);
                transformed.documentFileName = fileNameFromUrl;
              }
            }
            
            // Only transform if entityType/entityID exist AND locationID/projectID/inventoryItemID are not already set
            if (document.entityType && document.entityID !== null && document.entityID !== undefined) {
              // Only map entityType/entityID if locationID/projectID/inventoryItemID are not already set
              if (document.locationID === null || document.locationID === undefined || document.locationID === 0) {
                if (document.entityType === 'Location') {
                  transformed.locationID = document.entityID;
                  transformed.projectID = document.projectID || null;
                  transformed.inventoryItemID = document.inventoryItemID || null;
                } else if (document.entityType === 'Project') {
                  transformed.locationID = document.locationID || null;
                  transformed.projectID = document.entityID;
                  transformed.inventoryItemID = document.inventoryItemID || null;
                } else if (document.entityType === 'InventoryItem') {
                  transformed.locationID = document.locationID || null;
                  transformed.projectID = document.projectID || null;
                  transformed.inventoryItemID = document.entityID;
                }
                // If entityType doesn't match, preserve existing values
              }
              // If locationID/projectID/inventoryItemID are already set, preserve them
            }
            // If no entityType/entityID, preserve existing locationID/projectID/inventoryItemID values
            // Ensure they're at least set to 0 or null if completely missing
            if (transformed.locationID === undefined) transformed.locationID = document.locationID || null;
            if (transformed.projectID === undefined) transformed.projectID = document.projectID || null;
            if (transformed.inventoryItemID === undefined) transformed.inventoryItemID = document.inventoryItemID || null;
            
            return transformed;
          } catch (docError) {
            console.error('❌ Error transforming document:', docError, document);
            // Return original document if transformation fails
            return document;
          }
        });
        console.log(`✅ [FULL SYNC] Transformed ${transformedDocuments.length} documents`);
      } catch (documentsError) {
        console.error('❌ [FULL SYNC] Error transforming documents array:', documentsError);
        // Fallback to original documents if transformation fails
        transformedDocuments = apiResult.documents || apiResult.deviceAvailableDocumentList || apiResult.deviceAvailableDocumentDetailList || [];
      }
      
      const transformedData: any = {
        // Include deviceDetailData if available
        ...(deviceDetailData && { deviceDetailData }),
        // Map locations -> deviceLocationList (API returns locations or deviceLocationDetailList)
        deviceLocationList: apiResult.locations || apiResult.deviceLocationDetailList || [],
        // Map transformed forms -> deviceAvailableFormList
        deviceAvailableFormList: transformedForms,
        // Map transformed documents -> deviceAvailableDocumentList
        deviceAvailableDocumentList: transformedDocuments,
        // Include any other properties that might be present
        ...apiResult
      };

        console.log('📥 [FULL SYNC] Data transformation:', {
          locationsCount: (apiResult.locations || apiResult.deviceLocationDetailList || [])?.length || 0,
          formsCount: (apiResult.forms || apiResult.deviceAvailableFormList || apiResult.deviceAvailableFormDetailList || [])?.length || 0,
          documentsCount: (apiResult.documents || apiResult.deviceAvailableDocumentList || apiResult.deviceAvailableDocumentDetailList || [])?.length || 0,
        transformedLocations: transformedData.deviceLocationList?.length || 0,
        transformedForms: transformedData.deviceAvailableFormList?.length || 0,
        transformedDocuments: transformedData.deviceAvailableDocumentList?.length || 0
      });
      
      // Log sample transformation for debugging
      if (transformedForms.length > 0) {
        const sampleForm = transformedForms[0];
        console.log('📥 [FULL SYNC] Sample transformed form:', {
          formTitle: sampleForm.formTitle,
          entityType: sampleForm.entityType,
          entityID: sampleForm.entityID,
          locationID: sampleForm.locationID,
          projectID: sampleForm.projectID,
          inventoryItemID: sampleForm.inventoryItemID,
          isFolder: sampleForm.isFolder
        });
      }
      if (transformedDocuments.length > 0) {
        const sampleDocument = transformedDocuments[0];
        console.log('📥 [FULL SYNC] Sample transformed document:', {
          documentTitle: sampleDocument.documentTitle,
          entityType: sampleDocument.entityType,
          entityID: sampleDocument.entityID,
          locationID: sampleDocument.locationID,
          projectID: sampleDocument.projectID,
          inventoryItemID: sampleDocument.inventoryItemID,
          isFolder: sampleDocument.isFolder
        });
      }

      // Step 3: Empty all tables and insert data in a transaction
      // IMPORTANT: Both operations are wrapped in a transaction so if insertion fails,
      // the empty operation is also rolled back, preserving existing data
      this.updateSyncStatus({
        isSyncing: true,
        progress: 40,
        status: 'preparing_database',
      });

      try {
        console.log('📥 [FULL SYNC] Starting database operations in transaction...');
        console.log('📥 [FULL SYNC] Data structure check:', {
          hasDeviceDetailData: !!transformedData.deviceDetailData,
          hasDeviceLocationList: !!transformedData.deviceLocationList,
          locationsCount: transformedData.deviceLocationList?.length || 0,
          hasDeviceAvailableFormList: !!transformedData.deviceAvailableFormList,
          formsCount: transformedData.deviceAvailableFormList?.length || 0,
          hasDeviceAvailableDocumentList: !!transformedData.deviceAvailableDocumentList,
          documentsCount: transformedData.deviceAvailableDocumentList?.length || 0,
          allKeys: Object.keys(transformedData)
        });

        // Execute emptyAllTables and insertOfflineData in a transaction
        await this.offlineManager.executeInTransaction(async () => {
          // Save pending offline rows (unsynced CIs, induction archives, sign-offs, etc.)
          // before the wipe so they can be restored after insertOfflineData.
          // This prevents data loss when sync is interrupted with multiple users pending.
          const pendingOfflineData = await this.savePendingOfflineData();

          // Empty all tables (inside transaction)
          await new Promise<void>((emptyResolve) => {
            this.offlineManager.emptyAllTables(() => {
              emptyResolve();
            });
          });

          // Update progress
          this.updateSyncStatus({
            isSyncing: true,
            progress: 50,
            status: 'preparing_database',
          });

          // Insert data (inside transaction)
          this.updateSyncStatus({
            isSyncing: true,
            progress: 70,
            status: 'inserting_data',
          });

          await this.offlineManager.insertOfflineData(transformedData, () => {
            // Callback is called multiple times (once per table) for progress updates
          });

          // Restore any pending offline rows that the server doesn't know about yet.
          // INSERT OR IGNORE preserves original primary keys (server rows get higher
          // auto-increment IDs because sqlite_sequence is not reset by DELETE FROM).
          await this.restorePendingOfflineData(pendingOfflineData);
        });

        const progress = 90;
        this.updateSyncStatus({
          isSyncing: true,
          progress: progress,
          status: 'inserting_data',
        });
        console.log('✅ [FULL SYNC] Data inserted into local database (transaction committed)');
        // Save state: Data inserted successfully
        this.saveSyncState('data_inserted');
      } catch (transactionError) {
        console.error('❌ [FULL SYNC] Error during database transaction:', transactionError);
        console.log('🔄 [FULL SYNC] Transaction rolled back - existing data preserved');
        throw transactionError;
      }

      // Prefetch IOSH reference data if any IOSH forms are available (for offline use)
      try {
        const hasIOSHForm =
          Array.isArray(transformedData?.deviceAvailableFormList) &&
          transformedData.deviceAvailableFormList.some((f: any) =>
            String(f?.formType || '').toLowerCase().includes('iosh')
          );
        const companyID =
          this.sharedDataService?.dedicatedModeDeviceDetailData?.companyID ||
          this.sharedDataService?.getLoggedInUser()?.companyID ||
          0;
        if (hasIOSHForm && companyID) {
          const ioshRes: any = await this.apiService.GetIOSHItemList(companyID).toPromise();
          if (ioshRes?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            const items = ioshRes?.Result || ioshRes;
            await this.offlineManager.saveDeviceIOSHItems(companyID, items);
            console.log('[IOSH] Prefetched and cached IOSH reference data (full sync)');
          }
        }
      } catch (e) {
        console.warn('[IOSH] Prefetch during full sync failed (non-blocking):', e);
      }

      // Step 4: Acknowledge sync
      this.updateSyncStatus({
        isSyncing: true,
        progress: 95,
        status: 'finalizing',
      });

      await this.offlineApi
        .acknowledgeSync(deviceId, moment().toISOString())
        .toPromise();

      // Only hide loading if not a background sync (background sync uses header loader)
      if (!this.isBackgroundSync) {
        this.utilService.hideLoading();
        // Show success message only for non-background syncs
        await this.showSuccessMessage('Sync successful');
      }

      this.updateSyncStatus({
        isSyncing: true,
        progress: 100,
        status: 'completed',
      });

      // Update watermark so subsequent GetPendingChanges calls don't re-return already-processed events.
      this.lastSyncTimestamp = moment().toISOString();
      localStorage.setItem(EnumService.LocalStorageKeys.SYNC_DATE_TIME, this.lastSyncTimestamp);

      // Clear sync state on successful completion
      this.clearSyncState();
    } catch (error) {
      console.error('Full sync error:', error);
      // Don't clear state on error - preserve it for potential retry/resume
      // Only hide loading if not a background sync (background sync uses header loader)
      if (!this.isBackgroundSync) {
        this.utilService.hideLoading();
        // Skip toast for network-abort errors (status 0) — user already sees the offline popup/toast
        if (error?.status !== 0) {
          await this.showErrorMessage(error?.message || 'Sync failed. Please try again.');
        }
      }
      throw error;
    }
  }

  /**
   * Perform comparison-based sync (when GetPendingChanges is not empty)
   * Uses GetSyncData API but compares with existing database records
   * Only performs necessary INSERT/UPDATE/DELETE operations
   */
  private async performComparisonBasedSync(changes?: Array<{
    syncLogID: number;
    changeType: string;
    entityType: string;
    entityID: number;
    createdDate: string;
  }>): Promise<void> {
    this.updateSyncStatus({
      isSyncing: true,
      progress: 10,
      status: 'comparison_sync_started',
    });

    const deviceId = this.sharedDataService.deviceUID;
    const companyId = this.sharedDataService.dedicatedModeDeviceDetailData?.companyID;

    if (!companyId) {
      throw new Error('Company ID not available for sync');
    }

    // Track document IDs that need re-download (from DOCUMENT_UPDATED, DOCUMENT_CREATED, DOCUMENTVERSION_CREATED)
    const documentsToReDownload = new Set<number>();
    if (changes && changes.length > 0) {
      changes.forEach((change) => {
        if (
          change.entityType === 'Document' &&
          (change.changeType === 'DOCUMENT_UPDATED' ||
            change.changeType === 'DOCUMENT_CREATED' ||
            change.changeType === 'DOCUMENTVERSION_CREATED')
        ) {
          documentsToReDownload.add(change.entityID);
          console.log(`📥 [SYNC] Document ${change.entityID} marked for re-download (${change.changeType})`);
        }
      });
    }

    try {
      // Step 1: Get sync data
      // If changes are provided, use POST /DeviceSync/GetSyncData with changes
      // Otherwise, use GET /DeviceSync/offline/ (full sync)
      this.updateSyncStatus({
        isSyncing: true,
        progress: 20,
        status: 'downloading_data',
      });

      // Always use GET /DeviceSync/offline/ to get full dataset
      // Then compare with local database and only update what changed
      console.log('🔄 [COMPARISON SYNC] Calling GET /DeviceSync/offline/ to get full dataset for comparison...');
      if (changes && changes.length > 0) {
        console.log(`🔄 [COMPARISON SYNC] Note: GetPendingChanges returned ${changes.length} changes, but using full sync endpoint for comparison-based update`);
      }
      let syncData: any = await this.retryWithBackoff(async () => {
        return await this.offlineApi
          .getSyncData({
            deviceId: deviceId,
            companyId: companyId,
            includeLocations: true,
            includeForms: true,
            includeDocuments: true,
          })
          .toPromise();
      });

      // Normalize response structure
      if (syncData) {
        if (syncData.statusCode !== undefined && syncData.StatusCode === undefined) {
          syncData.StatusCode = syncData.statusCode;
        }
        if (syncData.result !== undefined && syncData.Result === undefined) {
          syncData.Result = syncData.result;
        }
      }

      if (
        !syncData ||
        syncData.StatusCode !== EnumService.ApiResponseCode.RequestSuccessful ||
        !syncData.Result
      ) {
        throw new Error('Failed to get sync data');
      }

      // Step 2: Query existing records from database
      this.updateSyncStatus({
        isSyncing: true,
        progress: 30,
        status: 'querying_database',
      });

      console.log('🔄 [COMPARISON SYNC] Querying existing records from database...');
      
      // Query existing forms
      const existingFormsQuery = 'SELECT formID, formVersionID, locationID, projectID, inventoryItemID, parentFormFolderID FROM DeviceAvailableForms';
      const existingFormsResult: any = await this.offlineManager.dbQuery(existingFormsQuery, []);
      let existingForms = this.offlineManager.convertToArray(existingFormsResult.rows || []);
      
      // Query existing documents
      const existingDocumentsQuery = 'SELECT documentID, documentVersionID, locationID, projectID, inventoryItemID, parentFolderID FROM DeviceAvailableDocuments';
      const existingDocumentsResult: any = await this.offlineManager.dbQuery(existingDocumentsQuery, []);
      let existingDocuments = this.offlineManager.convertToArray(existingDocumentsResult.rows || []);

      console.log(`🔄 [COMPARISON SYNC] Found ${existingForms.length} existing forms and ${existingDocuments.length} existing documents in database`);
      
      // Debug: Log sample existing forms to check for corrupted data
      if (existingForms.length > 0) {
        const sampleForm = existingForms[0];
        console.log('🔄 [COMPARISON SYNC] Sample existing form from DB:', {
          formID: sampleForm.formID,
          formVersionID: sampleForm.formVersionID,
          locationID: sampleForm.locationID,
          projectID: sampleForm.projectID,
          inventoryItemID: sampleForm.inventoryItemID
        });
      }
      
      // Debug: Check for corrupted forms (formID = 0) and documents (documentID = 0)
      const corruptedForms = existingForms.filter((f: any) => !f.formID || f.formID === 0 || !f.formVersionID || f.formVersionID === 0);
      const corruptedDocuments = existingDocuments.filter((d: any) => !d.documentID || d.documentID === 0 || !d.documentVersionID || d.documentVersionID === 0);
      
      if (corruptedForms.length > 0) {
        console.warn(`⚠️ [COMPARISON SYNC] Found ${corruptedForms.length} corrupted forms in database (formID = 0 or formVersionID = 0). These will be deleted and replaced.`);
        // Delete corrupted forms immediately
        for (const corruptedForm of corruptedForms) {
          try {
            let deleteCondition = `formID = ${corruptedForm.formID || 0} AND formVersionID = ${corruptedForm.formVersionID || 0}`;
            if (corruptedForm.locationID) deleteCondition += ` AND locationID = ${corruptedForm.locationID}`;
            if (corruptedForm.projectID) deleteCondition += ` AND projectID = ${corruptedForm.projectID}`;
            if (corruptedForm.inventoryItemID) deleteCondition += ` AND inventoryItemID = ${corruptedForm.inventoryItemID}`;
            await this.offlineManager.emptyTable('DeviceAvailableForms', deleteCondition);
            console.log(`🗑️ [COMPARISON SYNC] Deleted corrupted form: ${deleteCondition}`);
          } catch (error) {
            console.error(`❌ [COMPARISON SYNC] Error deleting corrupted form:`, error);
          }
        }
        // Remove corrupted forms from existingForms array so they're not used in comparison
        existingForms = existingForms.filter((f: any) => f.formID && f.formID !== 0 && f.formVersionID && f.formVersionID !== 0);
      }
      
      if (corruptedDocuments.length > 0) {
        console.warn(`⚠️ [COMPARISON SYNC] Found ${corruptedDocuments.length} corrupted documents in database (documentID = 0 or documentVersionID = 0). These will be deleted and replaced.`);
        // Delete corrupted documents immediately
        for (const corruptedDoc of corruptedDocuments) {
          try {
            let deleteCondition = `documentID = ${corruptedDoc.documentID || 0} AND documentVersionID = ${corruptedDoc.documentVersionID || 0}`;
            if (corruptedDoc.locationID) deleteCondition += ` AND locationID = ${corruptedDoc.locationID}`;
            if (corruptedDoc.projectID) deleteCondition += ` AND projectID = ${corruptedDoc.projectID}`;
            if (corruptedDoc.inventoryItemID) deleteCondition += ` AND inventoryItemID = ${corruptedDoc.inventoryItemID}`;
            await this.offlineManager.emptyTable('DeviceAvailableDocuments', deleteCondition);
            console.log(`🗑️ [COMPARISON SYNC] Deleted corrupted document: ${deleteCondition}`);
          } catch (error) {
            console.error(`❌ [COMPARISON SYNC] Error deleting corrupted document:`, error);
          }
        }
        // Remove corrupted documents from existingDocuments array so they're not used in comparison
        existingDocuments = existingDocuments.filter((d: any) => d.documentID && d.documentID !== 0 && d.documentVersionID && d.documentVersionID !== 0);
      }
      
      console.log(`🔄 [COMPARISON SYNC] After cleanup: ${existingForms.length} valid forms and ${existingDocuments.length} valid documents in database`);

      // Step 3: Transform API response (same as performFullSync)
      this.updateSyncStatus({
        isSyncing: true,
        progress: 40,
        status: 'preparing_data',
      });

      const apiResult = syncData.Result;
      if (!apiResult) {
        throw new Error('No data received from sync API');
      }

      // Log API response structure for debugging
      console.log('🔄 [COMPARISON SYNC] API Result keys:', Object.keys(apiResult));
      console.log('🔄 [COMPARISON SYNC] API Result data counts:', {
        forms: apiResult.forms?.length || 0,
        deviceAvailableFormList: apiResult.deviceAvailableFormList?.length || 0,
        deviceAvailableFormDetailList: apiResult.deviceAvailableFormDetailList?.length || 0,
        documents: apiResult.documents?.length || 0,
        deviceAvailableDocumentList: apiResult.deviceAvailableDocumentList?.length || 0,
        deviceAvailableDocumentDetailList: apiResult.deviceAvailableDocumentDetailList?.length || 0,
        locations: apiResult.locations?.length || 0,
        deviceLocationDetailList: apiResult.deviceLocationDetailList?.length || 0,
      });

      // Create deviceDetailData
      let deviceDetailData: any = null;
      const dedicatedDeviceData = this.sharedDataService.dedicatedModeDeviceDetailData;
      if (dedicatedDeviceData) {
        deviceDetailData = {
          activationDate: dedicatedDeviceData.activationDate || '',
          companyFolderName: dedicatedDeviceData.companyFolderName || '',
          companyID: dedicatedDeviceData.companyID || 0,
          createdBy: dedicatedDeviceData.createdBy || '',
          createdDate: dedicatedDeviceData.createdDate || '',
          deviceID: dedicatedDeviceData.deviceID || 0,
          deviceName: dedicatedDeviceData.deviceName || '',
          deviceUniqueID: dedicatedDeviceData.deviceUniqueID || '',
          entities: dedicatedDeviceData.entities || null,
          entityNames: dedicatedDeviceData.entityNames || null,
          formattedLastSynchDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          lastSeenDate: dedicatedDeviceData.lastSeenDate || '',
          lastSynchDate: moment().toISOString(),
          modifiedBy: dedicatedDeviceData.modifiedBy || '',
          modifiedDate: dedicatedDeviceData.modifiedDate || '',
          noOfInventoryItems: dedicatedDeviceData.noOfInventoryItems || 0,
          noOfLocations: (apiResult.locations || apiResult.deviceLocationDetailList || [])?.length || 0,
          noOfPorjects: dedicatedDeviceData.noOfPorjects || 0,
          timeDifference: '',
          // TODO: remove ?? false once backend returns isOfflineEnabled in /DeviceSync/offline/
          isOfflineEnabled: apiResult.deviceDetailData?.isOfflineEnabled ?? false,
        };
      }

      // Transform forms - API returns deviceAvailableFormList, deviceAvailableFormDetailList, or forms
      let transformedForms: any[] = [];
      try {
        const formsArray = apiResult.forms || apiResult.deviceAvailableFormList || apiResult.deviceAvailableFormDetailList || [];
        transformedForms = formsArray.map((form: any) => {
          try {
            const transformed = { ...form };
            // Only transform if entityType/entityID exist AND locationID/projectID/inventoryItemID are not already set
            if (form.entityType && form.entityID !== null && form.entityID !== undefined) {
              // Only map entityType/entityID if locationID/projectID/inventoryItemID are not already set
              if (form.locationID === null || form.locationID === undefined || form.locationID === 0) {
                if (form.entityType === 'Location') {
                  transformed.locationID = form.entityID;
                  transformed.projectID = form.projectID || null;
                  transformed.inventoryItemID = form.inventoryItemID || null;
                } else if (form.entityType === 'Project') {
                  transformed.locationID = form.locationID || null;
                  transformed.projectID = form.entityID;
                  transformed.inventoryItemID = form.inventoryItemID || null;
                } else if (form.entityType === 'InventoryItem') {
                  transformed.locationID = form.locationID || null;
                  transformed.projectID = form.projectID || null;
                  transformed.inventoryItemID = form.entityID;
                }
                // If entityType doesn't match, preserve existing values
              }
              // If locationID/projectID/inventoryItemID are already set, preserve them
            }
            // If no entityType/entityID, preserve existing locationID/projectID/inventoryItemID values
            // Ensure they're at least set to 0 or null if completely missing
            if (transformed.locationID === undefined) transformed.locationID = form.locationID || null;
            if (transformed.projectID === undefined) transformed.projectID = form.projectID || null;
            if (transformed.inventoryItemID === undefined) transformed.inventoryItemID = form.inventoryItemID || null;
            
            return transformed;
          } catch (formError) {
            console.error('❌ Error transforming form:', formError, form);
            return form;
          }
        });
      } catch (formsError) {
        console.error('❌ [COMPARISON SYNC] Error transforming forms array:', formsError);
        transformedForms = apiResult.forms || apiResult.deviceAvailableFormList || apiResult.deviceAvailableFormDetailList || [];
      }

      // Transform documents - API returns deviceAvailableDocumentList, deviceAvailableDocumentDetailList, or documents
      let transformedDocuments: any[] = [];
      try {
        const documentsArray = apiResult.documents || apiResult.deviceAvailableDocumentList || apiResult.deviceAvailableDocumentDetailList || [];
        
        // Debug: Log sample documents from API before transformation
        if (documentsArray.length > 0) {
          // Log first document
          const sampleDoc = documentsArray[0];
          console.log('🔄 [COMPARISON SYNC] Sample document from API (before transformation):', {
            documentID: sampleDoc.documentID,
            documentVersionID: sampleDoc.documentVersionID,
            documentTitle: sampleDoc.documentTitle,
            folderID: sampleDoc.folderID,
            isFolder: sampleDoc.isFolder,
            locationID: sampleDoc.locationID,
            projectID: sampleDoc.projectID,
            inventoryItemID: sampleDoc.inventoryItemID,
            entityType: sampleDoc.entityType,
            entityID: sampleDoc.entityID
          });
          
          // Count folders vs actual documents
          const folders = documentsArray.filter((d: any) => (!d.documentID || d.documentID === 0) && d.folderID);
          const actualDocuments = documentsArray.filter((d: any) => d.documentID && d.documentID !== 0);
          console.log(`🔄 [COMPARISON SYNC] Documents breakdown: ${folders.length} folders, ${actualDocuments.length} actual documents`);
          
          // Log sample actual document if any
          if (actualDocuments.length > 0) {
            const sampleActualDoc = actualDocuments[0];
            console.log('🔄 [COMPARISON SYNC] Sample ACTUAL document (not folder) from API:', {
              documentID: sampleActualDoc.documentID,
              documentVersionID: sampleActualDoc.documentVersionID,
              documentTitle: sampleActualDoc.documentTitle,
              folderID: sampleActualDoc.folderID,
              parentFolderID: sampleActualDoc.parentFolderID,
              isFolder: sampleActualDoc.isFolder,
              locationID: sampleActualDoc.locationID,
              projectID: sampleActualDoc.projectID,
              inventoryItemID: sampleActualDoc.inventoryItemID
            });
          } else {
            console.warn('⚠️ [COMPARISON SYNC] No actual documents found in API response - only folders!');
          }
        }
        
        transformedDocuments = documentsArray.map((document: any) => {
          try {
            const transformed = { ...document };
            
            // Ensure documentID and documentVersionID are properly set (handle case sensitivity)
            if (!transformed.documentID && document.documentId !== undefined) {
              transformed.documentID = document.documentId;
            }
            if (!transformed.documentVersionID && document.documentVersionId !== undefined) {
              transformed.documentVersionID = document.documentVersionId;
            }
            
            // Extract filename from documentDownloadUrl if it exists and update documentFileName
            if (transformed.documentDownloadUrl) {
              // Extract filename from path (before query params if present)
              const urlPath = transformed.documentDownloadUrl.split('?')[0];
              const fileNameFromUrl = urlPath.split('/').pop();
              if (fileNameFromUrl && fileNameFromUrl !== transformed.documentFileName) {
                console.log(`🔄 [SYNC] Updating documentFileName from URL: ${transformed.documentFileName} -> ${fileNameFromUrl}`);
                transformed.documentFileName = fileNameFromUrl;
              }
            }
            
            // Only transform if entityType/entityID exist AND locationID/projectID/inventoryItemID are not already set
            if (document.entityType && document.entityID !== null && document.entityID !== undefined) {
              // Only map entityType/entityID if locationID/projectID/inventoryItemID are not already set
              if (document.locationID === null || document.locationID === undefined || document.locationID === 0) {
                if (document.entityType === 'Location') {
                  transformed.locationID = document.entityID;
                  transformed.projectID = document.projectID || null;
                  transformed.inventoryItemID = document.inventoryItemID || null;
                } else if (document.entityType === 'Project') {
                  transformed.locationID = document.locationID || null;
                  transformed.projectID = document.entityID;
                  transformed.inventoryItemID = document.inventoryItemID || null;
                } else if (document.entityType === 'InventoryItem') {
                  transformed.locationID = document.locationID || null;
                  transformed.projectID = document.projectID || null;
                  transformed.inventoryItemID = document.entityID;
                }
                // If entityType doesn't match, preserve existing values
              }
              // If locationID/projectID/inventoryItemID are already set, preserve them
            }
            // If no entityType/entityID, preserve existing locationID/projectID/inventoryItemID values
            // Ensure they're at least set to 0 or null if completely missing
            if (transformed.locationID === undefined) transformed.locationID = document.locationID || null;
            if (transformed.projectID === undefined) transformed.projectID = document.projectID || null;
            if (transformed.inventoryItemID === undefined) transformed.inventoryItemID = document.inventoryItemID || null;
            
            // Debug: Log if documentID or documentVersionID is still 0 after transformation (might be a folder)
            if ((!transformed.documentID || transformed.documentID === 0) && (!transformed.isFolder)) {
              console.warn(`⚠️ [COMPARISON SYNC] Document has documentID = 0 but isFolder = false:`, {
                documentTitle: transformed.documentTitle,
                folderID: transformed.folderID,
                isFolder: transformed.isFolder,
                documentID: transformed.documentID,
                documentVersionID: transformed.documentVersionID
              });
            }
            
            return transformed;
          } catch (docError) {
            console.error('❌ Error transforming document:', docError, document);
            return document;
          }
        });
        
        // Debug: Log sample transformed document
        if (transformedDocuments.length > 0) {
          const sampleTransformed = transformedDocuments[0];
          console.log('🔄 [COMPARISON SYNC] Sample transformed document:', {
            documentID: sampleTransformed.documentID,
            documentVersionID: sampleTransformed.documentVersionID,
            documentTitle: sampleTransformed.documentTitle,
            folderID: sampleTransformed.folderID,
            isFolder: sampleTransformed.isFolder,
            locationID: sampleTransformed.locationID,
            projectID: sampleTransformed.projectID,
            inventoryItemID: sampleTransformed.inventoryItemID
          });
        }
      } catch (documentsError) {
        console.error('❌ [COMPARISON SYNC] Error transforming documents array:', documentsError);
        transformedDocuments = apiResult.documents || apiResult.deviceAvailableDocumentList || apiResult.deviceAvailableDocumentDetailList || [];
      }

      // Step 4: Compare and categorize records
      this.updateSyncStatus({
        isSyncing: true,
        progress: 50,
        status: 'comparing_data',
      });

      console.log('🔄 [COMPARISON SYNC] Comparing API data with database records...');

      // Create maps for quick lookup (using formID/documentID + version + entity context as key)
      const existingFormsMap = new Map<string, any>();
      existingForms.forEach((form: any) => {
        const key = `${form.formID}_${form.formVersionID}_${form.locationID || 0}_${form.projectID || 0}_${form.inventoryItemID || 0}`;
        existingFormsMap.set(key, form);
      });

      const existingDocumentsMap = new Map<string, any>();
      existingDocuments.forEach((doc: any) => {
        // For folders (documentID = 0 and folderID set), use folderID + parentFolderID as key
        // For actual documents, use documentID + documentVersionID as key
        const isFolder = (!doc.documentID || doc.documentID === 0) && doc.folderID;
        let key: string;
        if (isFolder) {
          key = `folder_${doc.folderID}_${doc.parentFolderID || 0}_${doc.locationID || 0}_${doc.projectID || 0}_${doc.inventoryItemID || 0}`;
        } else {
          key = `${doc.documentID}_${doc.documentVersionID}_${doc.locationID || 0}_${doc.projectID || 0}_${doc.inventoryItemID || 0}`;
        }
        existingDocumentsMap.set(key, doc);
      });

      // Categorize forms
      const formsToInsert: any[] = [];
      const formsToUpdate: any[] = [];
      const formKeysToDelete = new Set<string>();

      transformedForms.forEach((form: any) => {
        // Skip invalid forms from API (formID = 0 or formVersionID = 0)
        if (!form.formID || form.formID === 0 || !form.formVersionID || form.formVersionID === 0) {
          console.warn(`⚠️ [COMPARISON SYNC] Skipping invalid form from API:`, {
            formTitle: form.formTitle,
            formID: form.formID,
            formVersionID: form.formVersionID
          });
          return; // Skip this form - it's invalid
        }
        
        const key = `${form.formID}_${form.formVersionID}_${form.locationID || 0}_${form.projectID || 0}_${form.inventoryItemID || 0}`;
        const existing = existingFormsMap.get(key);
        
        // Check if existing form is corrupted (formID = 0 or formVersionID = 0)
        const isCorrupted = existing && (!existing.formID || existing.formID === 0 || !existing.formVersionID || existing.formVersionID === 0);
        
        if (!existing || isCorrupted) {
          // New form or corrupted form needs to be replaced
          if (isCorrupted) {
            console.log(`🔄 [COMPARISON SYNC] Form ${form.formID} (v${form.formVersionID}) is corrupted in DB, will be replaced`);
            formKeysToDelete.add(key); // Delete the corrupted form
          }
          if (!existing) {
            console.log(`🔄 [COMPARISON SYNC] Form not found in DB (will insert): formID=${form.formID}, formVersionID=${form.formVersionID}, locationID=${form.locationID}`);
          }
          formsToInsert.push(form);
        } else if (existing.formVersionID !== form.formVersionID) {
          // Changed form (version changed) - delete old, insert new
          console.log(`🔄 [COMPARISON SYNC] Form version changed: ${form.formID} from v${existing.formVersionID} to v${form.formVersionID}`);
          formsToUpdate.push(form);
          formKeysToDelete.add(key);
        } else {
          // Form exists and version matches - unchanged
          console.log(`🔄 [COMPARISON SYNC] Form unchanged: ${form.formID} v${form.formVersionID}`);
        }
        // If exists and version matches, skip (unchanged)
      });

      // Find forms to delete (in DB but not in API)
      existingForms.forEach((form: any) => {
        const key = `${form.formID}_${form.formVersionID}_${form.locationID || 0}_${form.projectID || 0}_${form.inventoryItemID || 0}`;
        const inApi = transformedForms.some((f: any) => {
          const apiKey = `${f.formID}_${f.formVersionID}_${f.locationID || 0}_${f.projectID || 0}_${f.inventoryItemID || 0}`;
          return apiKey === key;
        });
        if (!inApi) {
          formKeysToDelete.add(key);
        }
      });

      // Categorize documents
      const documentsToInsert: any[] = [];
      const documentsToUpdate: any[] = [];
      const documentKeysToDelete = new Set<string>();

      transformedDocuments.forEach((document: any) => {
        // Skip folders (documents with documentID = 0 and folderID set) - they're handled separately
        const isFolder = (!document.documentID || document.documentID === 0) && document.folderID;
        
        // For folders, use folderID + parentFolderID + locationID as key
        // For actual documents, use documentID + documentVersionID + locationID as key
        let key: string;
        if (isFolder) {
          key = `folder_${document.folderID}_${document.parentFolderID || 0}_${document.locationID || 0}_${document.projectID || 0}_${document.inventoryItemID || 0}`;
        } else {
          // Actual document - must have valid documentID and documentVersionID
          if (!document.documentID || document.documentID === 0 || !document.documentVersionID || document.documentVersionID === 0) {
            console.warn(`⚠️ [COMPARISON SYNC] Skipping document with invalid IDs:`, {
              documentTitle: document.documentTitle,
              documentID: document.documentID,
              documentVersionID: document.documentVersionID,
              folderID: document.folderID,
              isFolder: document.isFolder
            });
            return; // Skip this document - it's invalid
          }
          key = `${document.documentID}_${document.documentVersionID}_${document.locationID || 0}_${document.projectID || 0}_${document.inventoryItemID || 0}`;
        }
        
        const existing = existingDocumentsMap.get(key);
        
        // Debug logging for folders
        if (isFolder) {
          if (!existing) {
            console.log(`🔄 [COMPARISON SYNC] Folder not found in DB (will insert): folderID=${document.folderID}, parentFolderID=${document.parentFolderID}, locationID=${document.locationID}, key=${key}`);
          } else {
            console.log(`🔄 [COMPARISON SYNC] Folder already exists in DB (will skip): folderID=${document.folderID}, parentFolderID=${document.parentFolderID}, locationID=${document.locationID}`);
          }
        }
        
        // Check if existing document is corrupted (documentID = 0 or documentVersionID = 0) - but only for actual documents, not folders
        const isCorrupted = !isFolder && existing && (!existing.documentID || existing.documentID === 0 || !existing.documentVersionID || existing.documentVersionID === 0);
        
        if (isFolder && existing) {
          // Folder already exists in DB, skip (no need to update folders)
          console.log(`🔄 [COMPARISON SYNC] Skipping folder - already exists: folderID=${document.folderID}, parentFolderID=${document.parentFolderID}, locationID=${document.locationID}`);
          return; // Skip this folder
        }
        
        if (!existing || isCorrupted) {
          // New document or corrupted document needs to be replaced
          if (isCorrupted) {
            console.log(`🔄 [COMPARISON SYNC] Document ${document.documentID} (v${document.documentVersionID}) is corrupted in DB, will be replaced`);
            documentKeysToDelete.add(key); // Delete the corrupted document
          }
          documentsToInsert.push(document);
        } else if (!isFolder && existing.documentVersionID !== document.documentVersionID) {
          // Changed document (version changed) - delete old, insert new (only for actual documents, not folders)
          console.log(`🔄 [COMPARISON SYNC] Document version changed: ${document.documentID} from v${existing.documentVersionID} to v${document.documentVersionID}`);
          documentsToUpdate.push(document);
          documentKeysToDelete.add(key);
        } else if (!isFolder) {
          // Document exists and version matches - unchanged
          console.log(`🔄 [COMPARISON SYNC] Document unchanged: ${document.documentID} v${document.documentVersionID}`);
        }
        // If exists and version matches (or is folder), skip (unchanged)
      });

      // Find documents to delete (in DB but not in API)
      existingDocuments.forEach((doc: any) => {
        // Determine if DB document is a folder
        const isDbItemFolder = (!doc.documentID || doc.documentID === 0) && doc.folderID;
        
        // Create key using same format as in existingDocumentsMap
        let key: string;
        if (isDbItemFolder) {
          key = `folder_${doc.folderID}_${doc.parentFolderID || 0}_${doc.locationID || 0}_${doc.projectID || 0}_${doc.inventoryItemID || 0}`;
        } else {
          key = `${doc.documentID}_${doc.documentVersionID}_${doc.locationID || 0}_${doc.projectID || 0}_${doc.inventoryItemID || 0}`;
        }
        
        // Check if this document/folder exists in API response
        const inApi = transformedDocuments.some((d: any) => {
          const isApiItemFolder = (!d.documentID || d.documentID === 0) && d.folderID;
          let apiKey: string;
          if (isApiItemFolder) {
            apiKey = `folder_${d.folderID}_${d.parentFolderID || 0}_${d.locationID || 0}_${d.projectID || 0}_${d.inventoryItemID || 0}`;
          } else {
            apiKey = `${d.documentID}_${d.documentVersionID}_${d.locationID || 0}_${d.projectID || 0}_${d.inventoryItemID || 0}`;
          }
          return apiKey === key;
        });
        
        if (!inApi) {
          documentKeysToDelete.add(key);
        }
      });

      console.log(`🔄 [COMPARISON SYNC] Forms: ${formsToInsert.length} new, ${formsToUpdate.length} changed, ${formKeysToDelete.size} to delete`);
      console.log(`🔄 [COMPARISON SYNC] Documents: ${documentsToInsert.length} new, ${documentsToUpdate.length} changed, ${documentKeysToDelete.size} to delete`);

      // Step 5: Perform necessary database operations
      this.updateSyncStatus({
        isSyncing: true,
        progress: 60,
        status: 'updating_database',
      });

      // Delete changed/removed forms
      for (const key of formKeysToDelete) {
        const [formID, formVersionID, locationID, projectID, inventoryItemID] = key.split('_');
        let deleteCondition = `formID = ${formID} AND formVersionID = ${formVersionID}`;
        if (locationID !== '0' && locationID !== 'null') deleteCondition += ` AND locationID = ${locationID}`;
        else deleteCondition += ` AND (locationID = 0 OR locationID IS NULL)`;
        if (projectID !== '0' && projectID !== 'null') deleteCondition += ` AND projectID = ${projectID}`;
        else deleteCondition += ` AND (projectID = 0 OR projectID IS NULL)`;
        if (inventoryItemID !== '0' && inventoryItemID !== 'null') deleteCondition += ` AND inventoryItemID = ${inventoryItemID}`;
        else deleteCondition += ` AND (inventoryItemID = 0 OR inventoryItemID IS NULL)`;
        
        try {
          await this.offlineManager.emptyTable('DeviceAvailableForms', deleteCondition);
        } catch (error) {
          console.error(`❌ [COMPARISON SYNC] Error deleting form ${key}:`, error);
        }
      }

      // Delete changed/removed documents
      for (const key of documentKeysToDelete) {
        let deleteCondition: string;
        
        // Check if this is a folder key (starts with "folder_")
        if (key.startsWith('folder_')) {
          // Folder key format: folder_folderID_parentFolderID_locationID_projectID_inventoryItemID
          const parts = key.split('_');
          const folderID = parts[1];
          const parentFolderID = parts[2];
          const locationID = parts[3];
          const projectID = parts[4];
          const inventoryItemID = parts[5];
          
          deleteCondition = `folderID = ${folderID} AND parentFolderID = ${parentFolderID || 0}`;
          if (locationID !== '0' && locationID !== 'null') deleteCondition += ` AND locationID = ${locationID}`;
          else deleteCondition += ` AND (locationID = 0 OR locationID IS NULL)`;
          if (projectID !== '0' && projectID !== 'null') deleteCondition += ` AND projectID = ${projectID}`;
          else deleteCondition += ` AND (projectID = 0 OR projectID IS NULL)`;
          if (inventoryItemID !== '0' && inventoryItemID !== 'null') deleteCondition += ` AND inventoryItemID = ${inventoryItemID}`;
          else deleteCondition += ` AND (inventoryItemID = 0 OR inventoryItemID IS NULL)`;
        } else {
          // Document key format: documentID_documentVersionID_locationID_projectID_inventoryItemID
          const [documentID, documentVersionID, locationID, projectID, inventoryItemID] = key.split('_');
          deleteCondition = `documentID = ${documentID} AND documentVersionID = ${documentVersionID}`;
          if (locationID !== '0' && locationID !== 'null') deleteCondition += ` AND locationID = ${locationID}`;
          else deleteCondition += ` AND (locationID = 0 OR locationID IS NULL)`;
          if (projectID !== '0' && projectID !== 'null') deleteCondition += ` AND projectID = ${projectID}`;
          else deleteCondition += ` AND (projectID = 0 OR projectID IS NULL)`;
          if (inventoryItemID !== '0' && inventoryItemID !== 'null') deleteCondition += ` AND inventoryItemID = ${inventoryItemID}`;
          else deleteCondition += ` AND (inventoryItemID = 0 OR inventoryItemID IS NULL)`;
        }
        
        try {
          await this.offlineManager.emptyTable('DeviceAvailableDocuments', deleteCondition);
        } catch (error) {
          console.error(`❌ [COMPARISON SYNC] Error deleting document ${key}:`, error);
        }
      }

      // Insert new and updated forms/documents
      const dataToInsert: any = {
        ...(deviceDetailData && { deviceDetailData }),
        deviceLocationList: apiResult.locations || [],
        deviceAvailableFormList: [...formsToInsert, ...formsToUpdate],
        deviceAvailableDocumentList: [...documentsToInsert, ...documentsToUpdate],
      };

      this.updateSyncStatus({
        isSyncing: true,
        progress: 80,
        status: 'inserting_data',
      });

      console.log(`🔄 [COMPARISON SYNC] Inserting ${dataToInsert.deviceAvailableFormList.length} forms and ${dataToInsert.deviceAvailableDocumentList.length} documents...`);
      
      // Debug: Log sample form data being inserted
      if (dataToInsert.deviceAvailableFormList.length > 0) {
        const sampleForm = dataToInsert.deviceAvailableFormList[0];
        console.log('🔄 [COMPARISON SYNC] Sample form being inserted:', {
          formID: sampleForm.formID,
          formVersionID: sampleForm.formVersionID,
          locationID: sampleForm.locationID,
          projectID: sampleForm.projectID,
          inventoryItemID: sampleForm.inventoryItemID,
          formTitle: sampleForm.formTitle,
          hasEntityType: !!sampleForm.entityType,
          entityType: sampleForm.entityType,
          entityID: sampleForm.entityID
        });
      }
      
      // Debug: Log sample document data being inserted
      if (dataToInsert.deviceAvailableDocumentList.length > 0) {
        const sampleDoc = dataToInsert.deviceAvailableDocumentList[0];
        console.log('🔄 [COMPARISON SYNC] Sample document being inserted:', {
          documentID: sampleDoc.documentID,
          documentVersionID: sampleDoc.documentVersionID,
          locationID: sampleDoc.locationID,
          projectID: sampleDoc.projectID,
          inventoryItemID: sampleDoc.inventoryItemID,
          documentTitle: sampleDoc.documentTitle,
          hasEntityType: !!sampleDoc.entityType,
          entityType: sampleDoc.entityType,
          entityID: sampleDoc.entityID
        });
      }
      
      // Set documents to re-download before inserting
      (this.offlineManager as any).documentsToReDownload = documentsToReDownload;
      
      await this.offlineManager.insertOfflineData(dataToInsert, () => {
        // Progress callback
      });
      
      // Clear after insert
      (this.offlineManager as any).documentsToReDownload = new Set();

      // Prefetch IOSH reference data if any IOSH forms are available (incremental sync)
      try {
        const hasIOSHForm =
          Array.isArray(dataToInsert?.deviceAvailableFormList) &&
          dataToInsert.deviceAvailableFormList.some((f: any) =>
            String(f?.formType || '').toLowerCase().includes('iosh')
          );
        const companyID =
          this.sharedDataService?.dedicatedModeDeviceDetailData?.companyID ||
          this.sharedDataService?.getLoggedInUser()?.companyID ||
          0;
        if (hasIOSHForm && companyID) {
          const ioshRes: any = await this.apiService.GetIOSHItemList(companyID).toPromise();
          if (ioshRes?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            const items = ioshRes?.Result || ioshRes;
            await this.offlineManager.saveDeviceIOSHItems(companyID, items);
            console.log('[IOSH] Prefetched and cached IOSH reference data (incremental sync)');
          }
        }
      } catch (e) {
        console.warn('[IOSH] Prefetch during incremental sync failed (non-blocking):', e);
      }

      // Step 6: Acknowledge sync
      this.updateSyncStatus({
        isSyncing: true,
        progress: 95,
        status: 'finalizing',
      });

      await this.offlineApi
        .acknowledgeSync(deviceId, moment().toISOString())
        .toPromise();

      this.updateSyncStatus({
        isSyncing: true,
        progress: 100,
        status: 'completed',
      });

      console.log(`✅ [COMPARISON SYNC] Sync completed successfully`);
    } catch (error) {
      console.error('❌ [COMPARISON SYNC] Error during comparison-based sync:', error);
      throw error;
    }
  }

  /**
   * Perform incremental sync (when database has data)
   */
  private async performIncrementalSync(): Promise<void> {
    this.updateSyncStatus({
      isSyncing: true,
      progress: 10,
      status: 'incremental_sync_started',
    });

      const deviceId = this.sharedDataService.deviceUID;
      const lastSyncDate = this.lastSyncTimestamp || moment().subtract(1, 'day').toISOString();

      try {
      // Step 1: Get pending changes
      this.updateSyncStatus({
        isSyncing: true,
        progress: 20,
        status: 'checking_changes',
      });

      let pendingChanges: any = await this.offlineApi
        .getPendingChanges(deviceId, lastSyncDate)
        .toPromise();

      // Normalize the response structure - handle both StatusCode/Result and statusCode/result
      if (pendingChanges) {
        // If response has lowercase properties, normalize to uppercase (API returns statusCode/result)
        if (pendingChanges.statusCode !== undefined && pendingChanges.StatusCode === undefined) {
          pendingChanges.StatusCode = pendingChanges.statusCode;
          console.log('📥 [INCREMENTAL SYNC] Normalized statusCode -> StatusCode:', pendingChanges.StatusCode);
        }
        if (pendingChanges.result !== undefined && pendingChanges.Result === undefined) {
          pendingChanges.Result = pendingChanges.result;
          console.log('📥 [INCREMENTAL SYNC] Normalized result -> Result');
        }
      }

      if (
        pendingChanges.StatusCode !==
        EnumService.ApiResponseCode.RequestSuccessful
      ) {
        throw new Error('Failed to get pending changes');
      }

      // The result structure is { changes: [], serverTime: "..." }
      // So we need to access result.changes, not result directly
      const result = pendingChanges.Result || {};
      const changes = result.changes || [];
      const serverTime = result.serverTime;
      
      if (changes.length === 0) {
        console.log('No changes to sync');
        // Update lastSyncTimestamp with serverTime even if no changes
        if (serverTime) {
          this.lastSyncTimestamp = serverTime;
          localStorage.setItem(
            EnumService.LocalStorageKeys.SYNC_DATE_TIME,
            this.lastSyncTimestamp
          );
        }
        this.updateSyncStatus({
          isSyncing: true,
          progress: 100,
          status: 'no_changes',
        });
        return;
      }

      console.log(`📥 [INCREMENTAL SYNC] Found ${changes.length} changes to sync`);

      // Step 2: Call POST /DeviceSync/GetSyncData with changes
      this.updateSyncStatus({
        isSyncing: true,
        progress: 40,
        status: 'downloading_changes',
      });

      console.log(`📥 [INCREMENTAL SYNC] Calling POST /DeviceSync/GetSyncData with ${changes.length} changes`);

      // Transform changes to match backend format
      const syncChanges = changes.map((change: any) => ({
        syncLogID: change.syncLogID || change.SyncLogID,
        changeType: change.changeType || change.ChangeType,
        entityType: change.entityType || change.EntityType,
        entityID: change.entityID || change.EntityID,
        createdDate: change.createdDate || change.CreatedDate
      }));

      // Call POST /DeviceSync/GetSyncData with changes
      const syncData: any = await this.retryWithBackoff(async () => {
        return await this.offlineApi
          .getSyncData({ changes: syncChanges })
          .toPromise();
      });

      console.log('📥 [INCREMENTAL SYNC] getSyncData response received');
      console.log('📥 [INCREMENTAL SYNC] Raw response structure:', {
        hasStatusCode: !!syncData?.StatusCode,
        hasStatusCodeLower: !!syncData?.statusCode,
        hasResult: !!syncData?.Result,
        hasResultLower: !!syncData?.result,
        resultKeys: syncData?.Result ? Object.keys(syncData.Result) : syncData?.result ? Object.keys(syncData.result) : []
      });

      // Normalize the response structure - handle both StatusCode/Result and statusCode/result
      if (syncData) {
        if (syncData.statusCode !== undefined && syncData.StatusCode === undefined) {
          syncData.StatusCode = syncData.statusCode;
          console.log('📥 [INCREMENTAL SYNC] Normalized statusCode -> StatusCode:', syncData.StatusCode);
        }
        if (syncData.result !== undefined && syncData.Result === undefined) {
          syncData.Result = syncData.result;
          console.log('📥 [INCREMENTAL SYNC] Normalized result -> Result');
        }
      }

      if (
        syncData.StatusCode !==
        EnumService.ApiResponseCode.RequestSuccessful
      ) {
        throw new Error('Failed to get sync data');
      }

      // Step 3: Update local database with changes
      this.updateSyncStatus({
        isSyncing: true,
        progress: 60,
        status: 'applying_changes',
      });

      const data = syncData.Result || syncData.result;
      if (data) {
        console.log('📥 [INCREMENTAL SYNC] Response data keys:', Object.keys(data));
        console.log('📥 [INCREMENTAL SYNC] Response data:', {
          hasForms: !!data.forms,
          formsCount: data.forms?.length || 0,
          hasFormBuilderDetails: !!data.formBuilderDetails,
          formBuilderDetailsCount: data.formBuilderDetails?.length || 0,
          hasDocuments: !!data.documents,
          documentsCount: data.documents?.length || 0,
          hasLocations: !!data.locations,
          locationsCount: data.locations?.length || 0
        });

        // The backend returns: { forms: [], formBuilderDetails: [], documents: [], locations: [], ... }
        // Transform the data to match expected structure
        const transformedData: any = {
          // Map locations if present
          deviceLocationList: data.locations || data.deviceLocationDetailList || [],
          // Map forms if present
          deviceAvailableFormList: data.forms || data.deviceAvailableFormDetailList || [],
          // Map documents if present
          deviceAvailableDocumentList: data.documents || data.deviceAvailableDocumentDetailList || [],
          // Map form builder details (backend returns as formBuilderDetails)
          deviceFormBuilderDetailsList: data.formBuilderDetails || data.deviceFormBuilderDetailsList || data.deviceFormBuilderDetailList || [],
          // Include any other properties
          ...data
        };

        // Create deviceFormDetailsList from forms array (needed for getFormDetail query)
        // The backend doesn't return this separately, so we create it from forms metadata
        // NOTE: Property name must be deviceFormDetailsList (plural "Details") to match insertOfflineData expectation
        if (data.forms && data.forms.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          console.log(`📥 [INCREMENTAL SYNC] Creating form details from ${data.forms.length} forms, locationUse:`, locationUse);
          
          transformedData.deviceFormDetailsList = data.forms.map((form: any) => {
            // Normalize parentFormFolderID: null/0/'' should be 0 for consistency with query
            let parentFormFolderID = form.parentFormFolderID || form.parentFormFolderId;
            if (parentFormFolderID === null || parentFormFolderID === undefined || parentFormFolderID === '' || parentFormFolderID === '0') {
              parentFormFolderID = 0;
            }
            
            const formDetail: any = {
              formID: form.formID || form.formId || 0,
              formVersionID: form.formVersionID || form.formVersionId || 0,
              formVersionNo: form.formVersionNo || 0,
              formTitle: form.formTitle || '',
              formType: form.formType || '',
              formFolderID: form.formFolderID || form.formFolderId || 0,
              parentFormFolderID: parentFormFolderID,
              formattedDescription: form.formattedDescription || form.description || '',
              formattedInstructions: form.formattedInstructions || form.instructions || '',
              isDigitalSignOff: form.isDigitalSignOff || false,
              isSignatureSignOff: form.isSignatureSignOff || false,
              isPhotoSignOff: form.isPhotoSignOff || false,
              isReviewal: form.isReviewal || false,
              isEmailReviewer: form.isEmailReviewer || false,
              isSmsReviewer: form.isSmsReviewer || false,
              isSystemReviewer: form.isSystemReviewer || false,
              reviewerId: form.reviewerId || form.reviewerID || '',
              locationID: locationUse?.locationID || 0,
              projectID: locationUse?.projectID || 0,
              inventoryItemID: locationUse?.inventoryItemID || 0
            };
            
            console.log(`📥 [INCREMENTAL SYNC] Created form detail:`, {
              formID: formDetail.formID,
              formVersionID: formDetail.formVersionID,
              formVersionNo: formDetail.formVersionNo,
              parentFormFolderID: formDetail.parentFormFolderID,
              locationID: formDetail.locationID,
              projectID: formDetail.projectID,
              inventoryItemID: formDetail.inventoryItemID
            });
            
            return formDetail;
          });
          console.log(`📥 [INCREMENTAL SYNC] Created ${transformedData.deviceFormDetailsList.length} form details from forms array`);
        } else {
          transformedData.deviceFormDetailsList = data.deviceFormDetailsList || data.deviceFormDetailList || [];
          console.log(`📥 [INCREMENTAL SYNC] Using existing deviceFormDetailsList: ${transformedData.deviceFormDetailsList.length} items`);
        }

        // Transform forms: map entityID + entityType to locationID/projectID/inventoryItemID
        // Also normalize parentFormFolderID for consistency
        if (transformedData.deviceAvailableFormList && transformedData.deviceAvailableFormList.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          transformedData.deviceAvailableFormList = transformedData.deviceAvailableFormList.map((form: any) => {
            const transformed = { ...form };
            
            // Map entity context from dedicatedModeLocationUse if available
            if (locationUse) {
              transformed.locationID = locationUse.locationID || 0;
              transformed.projectID = locationUse.projectID || 0;
              transformed.inventoryItemID = locationUse.inventoryItemID || 0;
            } else {
              transformed.locationID = 0;
              transformed.projectID = 0;
              transformed.inventoryItemID = 0;
            }
            
            // Normalize parentFormFolderID: null/undefined/''/'0' should be 0 for consistency
            let parentFormFolderID = transformed.parentFormFolderID ?? transformed.parentFormFolderId ?? null;
            if (parentFormFolderID === null || parentFormFolderID === undefined || parentFormFolderID === '' || parentFormFolderID === '0') {
              parentFormFolderID = 0;
            }
            transformed.parentFormFolderID = parentFormFolderID;
            
            return transformed;
          });
        }

        // Transform documents: map entityID + entityType to locationID/projectID/inventoryItemID
        // Also normalize parentFolderID and add missing required fields with defaults
        if (transformedData.deviceAvailableDocumentList && transformedData.deviceAvailableDocumentList.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          console.log(`📥 [INCREMENTAL SYNC] Transforming ${transformedData.deviceAvailableDocumentList.length} documents, locationUse:`, locationUse);
          if (transformedData.deviceAvailableDocumentList.length > 0) {
            console.log(`📥 [INCREMENTAL SYNC] Sample document before transformation:`, {
              documentID: transformedData.deviceAvailableDocumentList[0].documentID,
              documentTitle: transformedData.deviceAvailableDocumentList[0].documentTitle,
              folderID: transformedData.deviceAvailableDocumentList[0].folderID,
              locationID: transformedData.deviceAvailableDocumentList[0].locationID,
              projectID: transformedData.deviceAvailableDocumentList[0].projectID,
              inventoryItemID: transformedData.deviceAvailableDocumentList[0].inventoryItemID,
              hasLocationID: transformedData.deviceAvailableDocumentList[0].locationID !== undefined && transformedData.deviceAvailableDocumentList[0].locationID !== null
            });
          }
          
          // Create a map of documentID -> {parentFolderID, locationID} from existing documents before deletion
          // Query without locationID filter first to find the document regardless of location context
          // This is important because documents might have been inserted with locationID = 0 initially
          const documentParentFolderMap = new Map<number, number>();
          const documentLocationMap = new Map<number, number>(); // Store locationID to preserve company-wide documents
          for (const document of transformedData.deviceAvailableDocumentList) {
            const documentID = document.documentID;
            if (documentID) {
              try {
                // First, try to find the document with locationID filter (preferred)
                let existingQuery = `SELECT parentFolderID, locationID FROM DeviceAvailableDocuments WHERE documentID = ${documentID}`;
                if (locationUse?.locationID) {
                  existingQuery += ` AND locationID = ${locationUse.locationID}`;
                } else if (locationUse?.projectID) {
                  existingQuery += ` AND projectID = ${locationUse.projectID}`;
                } else if (locationUse?.inventoryItemID) {
                  existingQuery += ` AND inventoryItemID = ${locationUse.inventoryItemID}`;
                }
                existingQuery += ` LIMIT 1`;
                
                let existingResult: any = await this.offlineManager.dbQuery(existingQuery, []);
                
                // If not found with location filter, try without location filter (document might have locationID = 0)
                if (!existingResult?.rows?.length || existingResult.rows.length === 0) {
                  const fallbackQuery = `SELECT parentFolderID, locationID FROM DeviceAvailableDocuments WHERE documentID = ${documentID} LIMIT 1`;
                  existingResult = await this.offlineManager.dbQuery(fallbackQuery, []);
                  if (existingResult?.rows?.length > 0) {
                    console.log(`📁 [INCREMENTAL SYNC] Found document ${documentID} without location filter (locationID: ${this.offlineManager.convertToArray(existingResult.rows)[0].locationID})`);
                  }
                }
                
                if (existingResult?.rows?.length > 0) {
                  const existingDoc = this.offlineManager.convertToArray(existingResult.rows)[0];
                  if (existingDoc?.parentFolderID !== null && existingDoc?.parentFolderID !== undefined && existingDoc?.parentFolderID !== 0) {
                    documentParentFolderMap.set(documentID, existingDoc.parentFolderID);
                    console.log(`📁 [INCREMENTAL SYNC] Found existing parentFolderID for documentID ${documentID} (locationID: ${existingDoc.locationID}): ${existingDoc.parentFolderID}`);
                  } else {
                    console.log(`📁 [INCREMENTAL SYNC] Document ${documentID} exists but parentFolderID is null/0: ${existingDoc?.parentFolderID}`);
                  }
                  // Store locationID to preserve company-wide documents (locationID: 0)
                  if (existingDoc?.locationID !== undefined && existingDoc?.locationID !== null) {
                    documentLocationMap.set(documentID, existingDoc.locationID);
                    console.log(`📁 [INCREMENTAL SYNC] Storing existing locationID for documentID ${documentID}: ${existingDoc.locationID}`);
                  }
                } else {
                  console.log(`📁 [INCREMENTAL SYNC] No existing document found for documentID ${documentID}`);
                }
              } catch (error) {
                console.error(`❌ [INCREMENTAL SYNC] Error querying existing parentFolderID for documentID ${documentID}:`, error);
              }
            }
          }
          
          transformedData.deviceAvailableDocumentList = transformedData.deviceAvailableDocumentList.map((document: any) => {
            const transformed: any = { ...document };
            
            // Map entity context from dedicatedModeLocationUse if available
            // BUT preserve locationID from existing document in DB if it was company-wide (locationID: 0)
            // Documents can be company-wide (locationID: 0) even if they're in a location-specific folder
            // Priority: 1) existing locationID from DB (if company-wide), 2) locationID from GetSyncData, 3) locationUse.locationID
            if (documentLocationMap.has(transformed.documentID)) {
              // Preserve the existing locationID from the database (especially for company-wide documents)
              const existingLocationID = documentLocationMap.get(transformed.documentID);
              transformed.locationID = existingLocationID;
              console.log(`📁 [INCREMENTAL SYNC] Using existing locationID ${existingLocationID} from DB for documentID ${transformed.documentID}`);
            } else if (locationUse) {
              // For new documents (not in documentLocationMap), always use locationUse values
              // This ensures new documents are associated with the current location context
              // Only preserve locationID from GetSyncData if it's explicitly set (not undefined/null)
              if (transformed.locationID !== undefined && transformed.locationID !== null && transformed.locationID !== '') {
                // GetSyncData explicitly provided locationID - preserve it
                console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - Preserving locationID from GetSyncData: ${transformed.locationID}`);
              } else {
                // New document or locationID not in response - use locationUse
                transformed.locationID = locationUse.locationID || 0;
                console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - Setting locationID from locationUse: ${transformed.locationID}`);
              }
              
              if (transformed.projectID === undefined || transformed.projectID === null || transformed.projectID === '') {
                transformed.projectID = locationUse.projectID || 0;
              }
              if (transformed.inventoryItemID === undefined || transformed.inventoryItemID === null || transformed.inventoryItemID === '') {
                transformed.inventoryItemID = locationUse.inventoryItemID || 0;
              }
            } else {
              // If no locationUse, preserve what's in the document or default to 0
              transformed.locationID = transformed.locationID !== undefined && transformed.locationID !== null && transformed.locationID !== '' ? transformed.locationID : 0;
              transformed.projectID = transformed.projectID !== undefined && transformed.projectID !== null && transformed.projectID !== '' ? transformed.projectID : 0;
              transformed.inventoryItemID = transformed.inventoryItemID !== undefined && transformed.inventoryItemID !== null && transformed.inventoryItemID !== '' ? transformed.inventoryItemID : 0;
              console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - No locationUse, using defaults: locationID=${transformed.locationID}, projectID=${transformed.projectID}, inventoryItemID=${transformed.inventoryItemID}`);
            }
            
            // Map folderID to parentFolderID if parentFolderID is not provided
            // GetSyncData returns folderID but DeviceAvailableDocuments expects parentFolderID
            // Priority: 1) parentFolderID from GetSyncData, 2) folderID from GetSyncData, 3) existing parentFolderID from DB, 4) 0
            let parentFolderID = transformed.parentFolderID ?? transformed.parentFolderId ?? null;
            console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - Initial parentFolderID from GetSyncData:`, parentFolderID);
            
            if (parentFolderID === null || parentFolderID === undefined) {
              // If parentFolderID is not set, use folderID (which might be the parent folder)
              parentFolderID = transformed.folderID ?? null;
              console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - Using folderID as parentFolderID:`, parentFolderID);
            }
            
            // If still null/0, check if we have an existing parentFolderID for this document
            if ((parentFolderID === null || parentFolderID === undefined || parentFolderID === '' || parentFolderID === '0' || parentFolderID === 0) && documentParentFolderMap.has(transformed.documentID)) {
              parentFolderID = documentParentFolderMap.get(transformed.documentID);
              console.log(`📁 [INCREMENTAL SYNC] Using existing parentFolderID ${parentFolderID} from DB for documentID ${transformed.documentID}`);
            }
            
            // Normalize: null/undefined/''/'0' should be 0 for consistency
            if (parentFolderID === null || parentFolderID === undefined || parentFolderID === '' || parentFolderID === '0') {
              parentFolderID = 0;
            }
            transformed.parentFolderID = parentFolderID;
            
            // Also ensure folderID is set correctly - if parentFolderID is 0, folderID should also be 0 or null
            // This ensures consistency with initial sync documents
            if (parentFolderID === 0) {
              transformed.folderID = transformed.folderID ?? null;
            } else {
              // If parentFolderID is set, folderID should match it (folderID is the folder this document is in)
              transformed.folderID = transformed.folderID ?? parentFolderID;
            }
            
            console.log(`📁 [INCREMENTAL SYNC] Document ${transformed.documentID} - Final parentFolderID:`, transformed.parentFolderID, `folderID:`, transformed.folderID);
            
            // Add missing required fields with defaults (GetSyncData returns minimal structure)
            transformed.documentFileIconURL = transformed.documentFileIconURL || transformed.documentFileIconUrl || '';
            transformed.isDigitalSignOff = transformed.isDigitalSignOff ?? false;
            transformed.isSignatureSignOff = transformed.isSignatureSignOff ?? false;
            transformed.isPhotoSignOff = transformed.isPhotoSignOff ?? false;
            transformed.isSystemReviewer = transformed.isSystemReviewer ?? false;
            transformed.isEmailReviewer = transformed.isEmailReviewer ?? false;
            transformed.isSmsReviewer = transformed.isSmsReviewer ?? false;
            transformed.createdDate = transformed.createdDate || '';
            transformed.expirationDate = transformed.expirationDate || '';
            transformed.formattedCreatedDate = transformed.formattedCreatedDate || '';
            transformed.timeDifference = transformed.timeDifference || '';
            transformed.reviewerId = transformed.reviewerId || transformed.reviewerID || '';
            
            // Ensure documentVersionNo is set (default to 0 if not provided)
            transformed.documentVersionNo = transformed.documentVersionNo ?? 0;
            
            console.log(`📥 [INCREMENTAL SYNC] Transformed document:`, {
              documentID: transformed.documentID,
              documentVersionID: transformed.documentVersionID,
              documentVersionNo: transformed.documentVersionNo,
              folderID: transformed.folderID,
              parentFolderID: transformed.parentFolderID,
              locationID: transformed.locationID,
              projectID: transformed.projectID,
              inventoryItemID: transformed.inventoryItemID,
              documentTitle: transformed.documentTitle,
              isNewDocument: !documentLocationMap.has(transformed.documentID),
              locationUseAvailable: !!locationUse,
              locationUseLocationID: locationUse?.locationID,
              hasRequiredFields: {
                documentFileIconURL: !!transformed.documentFileIconURL,
                isDigitalSignOff: transformed.isDigitalSignOff !== undefined,
                isSignatureSignOff: transformed.isSignatureSignOff !== undefined,
                isPhotoSignOff: transformed.isPhotoSignOff !== undefined
              }
            });
            
            return transformed;
          });
        }

        // Transform form details: ensure location context is set
        if (transformedData.deviceFormDetailsList && transformedData.deviceFormDetailsList.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          transformedData.deviceFormDetailsList = transformedData.deviceFormDetailsList.map((formDetail: any) => {
            const transformed = { ...formDetail };
            
            if (locationUse) {
              transformed.locationID = locationUse.locationID || 0;
              transformed.projectID = locationUse.projectID || 0;
              transformed.inventoryItemID = locationUse.inventoryItemID || 0;
            } else {
              transformed.locationID = transformed.locationID || 0;
              transformed.projectID = transformed.projectID || 0;
              transformed.inventoryItemID = transformed.inventoryItemID || 0;
            }
            
            return transformed;
          });
        }

        console.log(`📥 [INCREMENTAL SYNC] Data to insert:`, {
          locations: transformedData.deviceLocationList?.length || 0,
          forms: transformedData.deviceAvailableFormList?.length || 0,
          documents: transformedData.deviceAvailableDocumentList?.length || 0,
          formDetails: transformedData.deviceFormDetailsList?.length || 0,
          formBuilderDetails: transformedData.deviceFormBuilderDetailsList?.length || 0
        });
        
        // Log document details before deletion
        if (transformedData.deviceAvailableDocumentList && transformedData.deviceAvailableDocumentList.length > 0) {
          console.log(`📥 [INCREMENTAL SYNC] Documents to sync:`, transformedData.deviceAvailableDocumentList.map((d: any) => ({
            documentID: d.documentID,
            documentVersionID: d.documentVersionID,
            documentTitle: d.documentTitle,
            parentFolderID: d.parentFolderID,
            locationID: d.locationID
          })));
        }

        // Delete old form data before inserting new data to ensure clean updates
        // Only delete for the specific location/folder combination to avoid deleting forms for other locations
        // Delete ALL versions of the form for this location, since we're getting the latest version
        if (transformedData.deviceAvailableFormList && transformedData.deviceAvailableFormList.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          const uniqueFormIDs = new Set<number>();
          
          // Collect unique formIDs to avoid duplicate deletions
          for (const form of transformedData.deviceAvailableFormList) {
            if (form.formID) {
              uniqueFormIDs.add(form.formID);
            }
          }
          
          for (const formID of uniqueFormIDs) {
            try {
              // Build location condition
              let locationCondition = '';
              if (locationUse?.locationID) {
                locationCondition = ` AND locationID = ${locationUse.locationID}`;
              } else if (locationUse?.projectID) {
                locationCondition = ` AND projectID = ${locationUse.projectID}`;
              } else if (locationUse?.inventoryItemID) {
                locationCondition = ` AND inventoryItemID = ${locationUse.inventoryItemID}`;
              }
              
              // Find the form to get parentFormFolderID (should already be normalized by transformation above)
              const form = transformedData.deviceAvailableFormList.find((f: any) => f.formID === formID);
              if (!form) continue;
              
              // Delete ALL versions of this form from DeviceAvailableForms for this location
              // Delete by formID and location only (not by folder) to catch all versions including old ones
              // This ensures we remove old versions that might have different formVersionID or parentFormFolderID
              if (locationCondition) {
                const formCondition = `formID = ${formID}${locationCondition}`;
                await this.offlineManager.emptyTable('DeviceAvailableForms', formCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableForms: ${formCondition}`);
              } else {
                // If no location condition, delete by formID and formVersionID only (less safe but better than nothing)
                const formVersionID = form.formVersionID || form.formVersionId;
                if (formVersionID) {
                  const formCondition = `formID = ${formID} AND formVersionID = ${formVersionID}`;
                  await this.offlineManager.emptyTable('DeviceAvailableForms', formCondition);
                  console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableForms: ${formCondition}`);
                }
              }
              
              // Delete ALL versions of form details for this location
              // Use the same condition as insertData uses: formID, locationID/projectID/inventoryItemID
              // Don't include formVersionID/formVersionNo to delete all versions
              if (locationCondition) {
                const formDetailCondition = `formID = ${formID}${locationCondition}`;
                await this.offlineManager.emptyTable('DeviceFormDetails', formDetailCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceFormDetails: ${formDetailCondition}`);
              } else {
                // If no location condition, delete by formID and formVersionID/formVersionNo
                const formVersionID = form.formVersionID || form.formVersionId;
                const formVersionNo = form.formVersionNo || 0;
                if (formVersionID) {
                  const formDetailCondition = `formID = ${formID} AND formVersionID = ${formVersionID} AND formVersionNo = ${formVersionNo}`;
                  await this.offlineManager.emptyTable('DeviceFormDetails', formDetailCondition);
                  console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceFormDetails: ${formDetailCondition}`);
                }
              }
              
              // Delete old form builder details for this form version (builder details are global)
              // Only delete the specific version we're updating
              const formVersionID = form.formVersionID || form.formVersionId;
              if (formVersionID) {
                await this.offlineManager.emptyTable('DeviceFormBuilderDetails', `formId = ${formID} AND formVersionId = ${formVersionID}`);
              }
              
              console.log(`✅ [INCREMENTAL SYNC] Deleted old form data for formID: ${formID}, locationID: ${locationUse?.locationID || 'N/A'}`);
            } catch (deleteError) {
              console.error(`❌ [INCREMENTAL SYNC] Error deleting old form ${formID}:`, deleteError);
              // Continue even if deletion fails - insertData will handle updates
            }
          }
        }

        // Delete old document data before inserting new data to ensure clean updates
        // Only delete for the specific location/folder combination to avoid deleting documents for other locations
        if (transformedData.deviceAvailableDocumentList && transformedData.deviceAvailableDocumentList.length > 0) {
          const locationUse = this.sharedDataService.dedicatedModeLocationUse;
          const uniqueDocumentIDs = new Set<number>();
          const uniqueFolderIDs = new Set<number>();
          
          // Collect unique documentIDs and folderIDs to avoid duplicate deletions
          for (const document of transformedData.deviceAvailableDocumentList) {
            if (document.documentID) {
              uniqueDocumentIDs.add(document.documentID);
            }
            if (document.folderID) {
              uniqueFolderIDs.add(document.folderID);
            }
          }
          
          // Build location condition
          let locationCondition = '';
          if (locationUse?.locationID) {
            locationCondition = ` AND locationID = ${locationUse.locationID}`;
          } else if (locationUse?.projectID) {
            locationCondition = ` AND projectID = ${locationUse.projectID}`;
          } else if (locationUse?.inventoryItemID) {
            locationCondition = ` AND inventoryItemID = ${locationUse.inventoryItemID}`;
          }
          
          // Delete documents
          // IMPORTANT: Delete by documentID + documentVersionID regardless of location/parentFolderID
          // because old documents might have different locationID (0 vs actual location) or parentFolderID values
          for (const documentID of uniqueDocumentIDs) {
            try {
              // Find the document
              const document = transformedData.deviceAvailableDocumentList.find((d: any) => d.documentID === documentID);
              if (!document) continue;
              
              const documentVersionID = document.documentVersionID;
              
              // Debug: Check what documents exist in database before deletion
              const debugQuery = `SELECT documentID, documentVersionID, documentVersionNo, parentFolderID, locationID, projectID, inventoryItemID, documentTitle FROM DeviceAvailableDocuments WHERE documentID = ${documentID}`;
              try {
                const debugResult: any = await this.offlineManager.dbQuery(debugQuery, []);
                if (debugResult?.rows?.length > 0) {
                  const existingDocs = this.offlineManager.convertToArray(debugResult.rows);
                  console.log(`🔍 [INCREMENTAL SYNC] Existing documents in DB for documentID ${documentID}:`, existingDocs.map((d: any) => ({
                    documentID: d.documentID,
                    documentVersionID: d.documentVersionID,
                    documentVersionNo: d.documentVersionNo,
                    parentFolderID: d.parentFolderID,
                    locationID: d.locationID,
                    documentTitle: d.documentTitle
                  })));
                } else {
                  console.log(`🔍 [INCREMENTAL SYNC] No existing documents found in DB for documentID ${documentID}`);
                }
              } catch (debugError) {
                console.error(`🔍 [INCREMENTAL SYNC] Debug query error:`, debugError);
              }
              
              // Delete ALL versions of this document by documentID + documentVersionID
              // This ensures we catch old versions regardless of locationID or parentFolderID differences
              if (documentVersionID) {
                // Delete by documentID and documentVersionID (most specific - catches exact version)
                const documentCondition = `documentID = ${documentID} AND documentVersionID = ${documentVersionID}`;
                await this.offlineManager.emptyTable('DeviceAvailableDocuments', documentCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableDocuments: ${documentCondition}`);
                
                // Verify deletion worked
                const verifyDeleteQuery = `SELECT documentID, documentVersionID, documentTitle, parentFolderID, locationID FROM DeviceAvailableDocuments WHERE documentID = ${documentID} AND documentVersionID = ${documentVersionID}`;
                try {
                  const verifyDeleteResult: any = await this.offlineManager.dbQuery(verifyDeleteQuery, []);
                  if (verifyDeleteResult?.rows?.length > 0) {
                    const remainingDocs = this.offlineManager.convertToArray(verifyDeleteResult.rows);
                    console.warn(`⚠️ [INCREMENTAL SYNC] Document still exists after deletion!`, remainingDocs);
                  } else {
                    console.log(`✅ [INCREMENTAL SYNC] Verified document deleted: documentID=${documentID}, documentVersionID=${documentVersionID}`);
                  }
                } catch (verifyError) {
                  console.error(`❌ [INCREMENTAL SYNC] Error verifying deletion:`, verifyError);
                }
                
                // Also delete by documentID only (to catch any other versions of this document)
                // This handles cases where documentVersionID might have changed
                try {
                  const documentConditionAllVersions = `documentID = ${documentID}`;
                  await this.offlineManager.emptyTable('DeviceAvailableDocuments', documentConditionAllVersions);
                  console.log(`🗑️ [INCREMENTAL SYNC] Also deleted all versions of documentID: ${documentConditionAllVersions}`);
                } catch (e) {
                  // Ignore - the main deletion above should handle most cases
                }
              } else {
                // If no documentVersionID, delete by documentID only
                const documentCondition = `documentID = ${documentID}`;
                await this.offlineManager.emptyTable('DeviceAvailableDocuments', documentCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableDocuments (no versionID): ${documentCondition}`);
              }
              
              console.log(`✅ [INCREMENTAL SYNC] Deleted old document data for documentID: ${documentID}, documentVersionID: ${documentVersionID || 'N/A'}`);
            } catch (deleteError) {
              console.error(`❌ [INCREMENTAL SYNC] Error deleting old document ${documentID}:`, deleteError);
              // Continue even if deletion fails - insertData will handle updates
            }
          }
          
          // Delete folders
          for (const folderID of uniqueFolderIDs) {
            try {
              // Find the folder
              const folder = transformedData.deviceAvailableDocumentList.find((d: any) => d.folderID === folderID);
              if (!folder) continue;
              
              // Delete folder from DeviceAvailableDocuments
              // Use the same condition as insertData uses: folderID, parentFolderID, locationID/projectID/inventoryItemID
              if (locationCondition) {
                const folderCondition = `folderID = ${folderID}${locationCondition}`;
                await this.offlineManager.emptyTable('DeviceAvailableDocuments', folderCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableDocuments folder: ${folderCondition}`);
              } else {
                const folderCondition = `folderID = ${folderID}`;
                await this.offlineManager.emptyTable('DeviceAvailableDocuments', folderCondition);
                console.log(`🗑️ [INCREMENTAL SYNC] Deleted DeviceAvailableDocuments folder: ${folderCondition}`);
              }
              
              console.log(`✅ [INCREMENTAL SYNC] Deleted old folder data for folderID: ${folderID}, locationID: ${locationUse?.locationID || 'N/A'}`);
            } catch (deleteError) {
              console.error(`❌ [INCREMENTAL SYNC] Error deleting old folder ${folderID}:`, deleteError);
              // Continue even if deletion fails - insertData will handle updates
            }
          }
        }

        // Use existing insertOfflineData method to update database
        // This will handle updates/inserts for all entity types
        await this.offlineManager.insertOfflineData(transformedData, () => {
          // Callback for progress updates
        });

        console.log(`✅ [INCREMENTAL SYNC] Updated local database with sync data`);
      } else {
        console.warn(`⚠️ [INCREMENTAL SYNC] No data received from GetSyncData`);
      }

      // Step 4: Acknowledge sync and update lastSyncTimestamp
      this.updateSyncStatus({
        isSyncing: true,
        progress: 90,
        status: 'finalizing',
      });

      // Get serverTime from GetSyncData response if available, otherwise use GetPendingChanges serverTime
      const syncDataServerTime = syncData?.Result?.serverTime || syncData?.result?.serverTime;
      const finalServerTime = syncDataServerTime || serverTime;
      
      // Use serverTime from response if available, otherwise use current time
      const syncTimestamp = finalServerTime || moment().toISOString();
      
      await this.offlineApi
        .acknowledgeSync(deviceId, syncTimestamp)
        .toPromise();

      // Update lastSyncTimestamp with serverTime from response
      if (finalServerTime) {
        this.lastSyncTimestamp = finalServerTime;
        localStorage.setItem(
          EnumService.LocalStorageKeys.SYNC_DATE_TIME,
          this.lastSyncTimestamp
        );
        console.log(`✅ [INCREMENTAL SYNC] Updated lastSyncTimestamp to: ${finalServerTime}`);
      }

      this.updateSyncStatus({
        isSyncing: true,
        progress: 100,
        status: 'completed',
      });
    } catch (error) {
      console.error('Incremental sync error:', error);
      throw error;
    }
  }

  /**
   * Normalize form answer payload shape before saveFormAnswers.
   */
  private normalizeFormAnswerPayload(formAnswerData: any, fallbackEntityIds: any): any {
    const parseField = (field: any, defaultValue: any = null) => {
      if (field === null || field === undefined || field === '') return defaultValue;
      if (Array.isArray(field)) return field;
      if (typeof field === 'string') {
        try {
          return JSON.parse(field);
        } catch (e) {
          return defaultValue;
        }
      }
      return field;
    };

    const toNumber = (value: any, defaultValue = 0) => {
      const parsed = Number(value);
      return isNaN(parsed) ? defaultValue : parsed;
    };

    let entityId = formAnswerData?.entityId || formAnswerData?.entityID || '';
    if (!entityId) {
      if (fallbackEntityIds?.locationID) {
        entityId = `L|${fallbackEntityIds.locationID}`;
      } else if (fallbackEntityIds?.projectID) {
        entityId = `P|${fallbackEntityIds.projectID}`;
      } else if (fallbackEntityIds?.inventoryItemID) {
        entityId = `I|${fallbackEntityIds.inventoryItemID}`;
      }
    }

    const formattedSections = parseField(formAnswerData?.formattedSections, []);
    if (Array.isArray(formattedSections)) {
      formattedSections.forEach((section: any) => {
        const normalizeScore = (val: any) => {
          const n = Number(val);
          return isNaN(n) ? 0 : n;
        };
        section.formScore = normalizeScore(section.formScore);
        section.maxFormScore = normalizeScore(section.maxFormScore);
        section.formPerScore = normalizeScore(section.formPerScore);
        section.perSectionScore = normalizeScore(section.perSectionScore);
        section.maximumSectionScore = normalizeScore(section.maximumSectionScore);
        section.achievedSectionScore = normalizeScore(section.achievedSectionScore);
      });
    }

    return {
      formId: toNumber(formAnswerData?.formId || formAnswerData?.formID, 0),
      formVersionId: toNumber(
        formAnswerData?.formVersionId || formAnswerData?.formVersionID,
        0
      ),
      formTypeId: toNumber(formAnswerData?.formTypeId || formAnswerData?.formTypeID, 0),
      userId: String(
        formAnswerData?.userId ||
          this.sharedDataService.getLoggedInUser()?.userId ||
          ''
      ),
      companyId: toNumber(formAnswerData?.companyId || formAnswerData?.companyID, 0),
      selectedLanguageID: toNumber(
        formAnswerData?.selectedLanguageID || formAnswerData?.selectedLanguageId,
        0
      ),
      entityId: String(entityId || ''),
      guest_FirstName: String(formAnswerData?.guest_FirstName || ''),
      guest_MiddleName: String(formAnswerData?.guest_MiddleName || ''),
      guest_LastName: String(formAnswerData?.guest_LastName || ''),
      guest_Phone: String(formAnswerData?.guest_Phone || ''),
      questionAnswers: parseField(formAnswerData?.questionAnswers, []),
      accidentReportQuestionAnswers: parseField(
        formAnswerData?.accidentReportQuestionAnswers,
        []
      ),
      riskAssessmentAnswers: parseField(formAnswerData?.riskAssessmentAnswers, []),
      havAnswerDetail: parseField(formAnswerData?.havAnswerDetail, null),
      workPermitAnswer: parseField(formAnswerData?.workPermitAnswer, null),
      formattedSections,
      workPermitDetails: parseField(formAnswerData?.workPermitDetails, null),
      accidentReport: parseField(formAnswerData?.accidentReport, null),
      riskAssessmentAnswerDetails: parseField(
        formAnswerData?.riskAssessmentAnswerDetails,
        { taskAnswers: [] }
      ),
      ioshRiskAssessmentAnswerDetails: parseField(
        formAnswerData?.ioshRiskAssessmentAnswerDetails,
        { hazardAnswers: [] }
      ),
    };
  }

  /**
   * Sync pending offline check-ins by replaying induction forms and then calling InsertCheckInDetails.
   */
  private async syncPendingCheckIns(remoteCiCoChanges: any[] = []): Promise<void> {
    try {
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        return;
      }

      const deviceId = this.sharedDataService.dedicatedModeDeviceDetailData?.deviceID;
      if (!deviceId) {
        return;
      }

      // Reuse existing offline data preparation (uploads local files and replaces file IDs).
      const preparedData: any = await this.offlineManager.prepareJsonFileForPost(
        deviceId,
        this.offlineApi
      );

      console.log(`🔍 [CI-SYNC] prepareJsonFileForPost result: isNull=${preparedData === null}, checkInDetails=${preparedData?.checkInDetails?.length ?? 'undefined'}, checkInDetails_Guest=${preparedData?.checkInDetails_Guest?.length ?? 'undefined'}, checkOutDetails=${preparedData?.checkOutDetails?.length ?? 'undefined'}`);

      const userCheckins: any[] = Array.isArray(preparedData?.checkInDetails)
        ? preparedData.checkInDetails
        : [];
      const guestCheckins: any[] = Array.isArray(preparedData?.checkInDetails_Guest)
        ? preparedData.checkInDetails_Guest
        : [];

      console.log(`🔍 [CI-SYNC] userCheckins=${userCheckins.length}, guestCheckins=${guestCheckins.length}`);

      if (userCheckins.length === 0 && guestCheckins.length === 0) {
        console.log(`🔍 [CI-SYNC] No pending checkins found — exiting early`);
        return;
      }

      // remoteCiCoChanges is passed in from the caller (fetched before syncPendingCheckOuts ran),
      // so our own CO push does not appear in it.
      console.log(`🔍 [CI-SYNC] remoteCiCoChanges received: ${remoteCiCoChanges.length}`, remoteCiCoChanges.map(c => ({ action: c.action, userId: c.userId, entityType: c.entityType, entityID: c.entityID, deviceUniqueID: c.deviceUniqueID, createdDate: c.createdDate })));

      // Build a map of the most recent offline CHECKOUT date per user+entity.
      // Used for local-clock comparison before falling back to remote conflict check.
      const allOfflineCheckouts: any[] = (await this.offlineManager.getOfflineUserCheckoutDetails() as any[]) || [];
      console.log(`🔍 [CI-SYNC] allOfflineCheckouts count: ${allOfflineCheckouts.length}`, allOfflineCheckouts.map(co => ({ deviceUserCheckinDetailId: co.deviceUserCheckinDetailId, userId: co.userId, checkOutDate: co.checkOutDate, userCheckInDetailID: co.userCheckInDetailID })));
      const latestCheckoutDateByKey = new Map<string, string>();
      for (const co of allOfflineCheckouts) {
        const key = this.getOfflineEntityKey(co);
        if (!key || !co.checkOutDate) continue;
        const existing = latestCheckoutDateByKey.get(key);
        if (!existing || new Date(co.checkOutDate) > new Date(existing)) {
          latestCheckoutDateByKey.set(key, co.checkOutDate);
        }
      }

      console.log(`🔍 [CI-SYNC] userCheckins count: ${userCheckins.length}`, userCheckins.map(ci => ({ offlineUserCheckInDetailID: ci.offlineUserCheckInDetailID, userId: ci.userId, checkInDate: ci.checkInDate })));
      for (const checkIn of userCheckins) {
        try {
        const fallbackEntityIds = {
          locationID: checkIn.locationID || 0,
          projectID: checkIn.projectID || 0,
          inventoryItemID: checkIn.inventoryItemID || 0,
        };

        const inductionFormList: any[] = [];
        let groupUniqueID = '';
        let createdActivityList: any[] = [];

        // Always submit induction form answers regardless of CI conflict outcome.
        const formAnswerList = Array.isArray(checkIn.formAnswerData)
          ? checkIn.formAnswerData
          : [];
        for (const formAnswerData of formAnswerList) {
          const payload = this.normalizeFormAnswerPayload(
            formAnswerData,
            fallbackEntityIds
          );
          if (!payload.formId || !payload.formVersionId) {
            continue;
          }

          const saveFormResponse: any = await this.apiService
            .saveFormAnswers(payload)
            .toPromise();

          if (saveFormResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            const result = saveFormResponse.Result || {};
            inductionFormList.push({
              formId: payload.formId,
              inductionFormContent: result.formAnswerHtmlString || '',
              answerNotificationList: result.answerNotificationList || [],
              groupUniqueID: result.groupUniqueID || '',
              createdActivityList: result.questionLogicActivityList || [],
            });
            groupUniqueID = result.groupUniqueID || groupUniqueID;
            createdActivityList = result.questionLogicActivityList || createdActivityList;
          }
        }

        // Determine whether to skip insertCheckInDetails.
        // Priority 1: local device clock comparison (same clock → no skew).
        // Priority 2: remote conflict check (only when no local CO exists for this user+entity).
        const ciKey = this.getOfflineEntityKey({
          userId: checkIn.userId,
          locationID: checkIn.locationID,
          projectID: checkIn.projectID,
          inventoryItemID: checkIn.inventoryItemID,
        });
        const latestLocalCheckout = ciKey ? latestCheckoutDateByKey.get(ciKey) : null;

        console.log(`🔍 [CI-SYNC] Processing checkIn: offlineUserCheckInDetailID=${checkIn.offlineUserCheckInDetailID}, userId=${checkIn.userId}, checkInDate=${checkIn.checkInDate}, ciKey=${ciKey}, latestLocalCheckout=${latestLocalCheckout}`);

        let shouldSkipCheckin = false;
        if (latestLocalCheckout) {
          const isPairedOfflineCheckout = allOfflineCheckouts.some(
            (co: any) => co.deviceUserCheckinDetailId == checkIn.offlineUserCheckInDetailID && co.checkOutDate
          );
          console.log(`🔍 [CI-SYNC] latestLocalCheckout exists: ${latestLocalCheckout} | isPairedOfflineCheckout: ${isPairedOfflineCheckout} | checkOutDate > checkInDate: ${new Date(latestLocalCheckout) > new Date(checkIn.checkInDate)}`);
          if (!isPairedOfflineCheckout && new Date(latestLocalCheckout) > new Date(checkIn.checkInDate)) {
            shouldSkipCheckin = true;
          }
        } else {
          // No local CO for this user+entity → fall back to remote conflict check.
          const ciLocId = Number(checkIn.locationID);
          const ciProjId = Number(checkIn.projectID);
          const ciInvId = Number(checkIn.inventoryItemID);
          shouldSkipCheckin = remoteCiCoChanges.some((c: any) => {
            if (c.action !== 'CHECKOUT' || c.userId !== checkIn.userId) return false;
            const entityMatches =
              (ciLocId > 0 && c.entityType === 'Location' && Number(c.entityID) === ciLocId) ||
              (ciProjId > 0 && c.entityType === 'Project' && Number(c.entityID) === ciProjId) ||
              (ciInvId > 0 && c.entityType === 'InventoryItem' && Number(c.entityID) === ciInvId);
            if (!entityMatches) return false;
            return new Date(c.createdDate).getTime() > new Date(checkIn.checkInDate).getTime();
          });
        }

        if (shouldSkipCheckin) {
          console.log(`📤 [SYNC] Skipping insertCheckInDetails for userId=${checkIn.userId} — ${latestLocalCheckout ? 'local CHECKOUT is more recent' : 'remote CHECKOUT is more recent'}. Induction forms already submitted.`);
          await this.offlineManager.emptyTable(
            'DeviceUserCheckinDetails',
            `deviceUserCheckinDetailId = ${checkIn.offlineUserCheckInDetailID}`
          );
          continue;
        }

        console.log(`🔍 [CI-SYNC] ✅ shouldSkipCheckin=false for userId=${checkIn.userId} — proceeding to insertCheckInDetails`);

        const signOffDate = moment(checkIn.checkInDate).isValid()
          ? moment(checkIn.checkInDate).format('DD.MM.YYYY HH:mm:ss')
          : moment().utc().format('DD.MM.YYYY HH:mm:ss');

        const checkInPayload: any = {
          userId: checkIn.userId || '',
          userPhoto: checkIn.userPhoto || '',
          checkInLatitude: checkIn.checkInLatitude || '',
          checkInLongitude: checkIn.checkInLongitude || '',
          isGuestReturning: false,
          projectID: checkIn.projectID || 0,
          inventoryItemID: checkIn.inventoryItemID || 0,
          locationID: checkIn.locationID || 0,
          companyID: checkIn.companyID || 0,
          inductionFormList,
          inductionFormContent: '',
          answerNotificationList: '',
          groupUniqueID,
          createdActivityList,
          digitalInkSignature: checkIn.digitalInkSignature || '',
          userSignaturePhoto: checkIn.userSignaturePhoto || '',
          signOffDate,
          checkInDate: signOffDate,
        };

        console.log(`🚀 [CI-SYNC] CALLING insertCheckInDetails for userId=${checkIn.userId} signOffDate=${signOffDate}`);
        const response: any = await this.apiService
          .insertCheckInDetails(checkInPayload)
          .toPromise();

        console.log(`🔍 [CI-SYNC] insertCheckInDetails response StatusCode=${response?.StatusCode} for userId=${checkIn.userId}`);

        const ciSucceeded = response?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful;
        // 412 = "already checked in" — CI was pushed in a previous sync but the local row was
        // not cleaned up (e.g. sync was interrupted). Treat it like success: the CI is on the
        // server, so we still need to push any paired CO using the server's current CI ID.
        const ciAlreadyOnServer = response?.StatusCode === 412;

        if (ciSucceeded || ciAlreadyOnServer) {
          const checkoutRows: any[] = (await this.offlineManager.getOfflineUserCheckoutDetails() as any[]) || [];
          const pendingCheckout = checkoutRows.find(r => r.deviceUserCheckinDetailId == checkIn.offlineUserCheckInDetailID);
          console.log(`🔍 [CI-SYNC] After CI push (ciSucceeded=${ciSucceeded}, ciAlreadyOnServer=${ciAlreadyOnServer}) — checkoutRows: ${checkoutRows.length}, looking for deviceUserCheckinDetailId=${checkIn.offlineUserCheckInDetailID}, pendingCheckout found: ${!!pendingCheckout}`, pendingCheckout ? { deviceUserCheckinDetailId: pendingCheckout.deviceUserCheckinDetailId, userCheckInDetailID: pendingCheckout.userCheckInDetailID, checkOutDate: pendingCheckout.checkOutDate } : null);

          if (pendingCheckout) {
            // When CI was already on server (412), local row still has userCheckInDetailID=0 —
            // resolve the server's current open CI ID before pushing the CO.
            let userCheckInDetailID = this.parseCheckoutUserCheckInDetailId(pendingCheckout.userCheckInDetailID);
            console.log(`🔍 [CI-SYNC] Paired CO — raw userCheckInDetailID=${pendingCheckout.userCheckInDetailID}, parsed=${userCheckInDetailID}`);
            if (userCheckInDetailID == null) {
              userCheckInDetailID = await this.resolveMissingUserCheckoutCheckInDetailId(pendingCheckout);
              console.log(`🔍 [CI-SYNC] Paired CO — resolved userCheckInDetailID=${userCheckInDetailID}`);
            }
            if (userCheckInDetailID != null) {
              try {
                const checkoutResponse: any = await this.apiService.insertCheckOutDetails({
                  userCheckInDetailID,
                  userId: pendingCheckout.userId || '',
                  checkOutLatitude: this.parseCheckoutCoordinate(pendingCheckout.checkOutLatitude),
                  checkOutLongitude: this.parseCheckoutCoordinate(pendingCheckout.checkOutLongitude),
                  checkOutDate: moment(pendingCheckout.checkOutDate).isValid()
                    ? moment(pendingCheckout.checkOutDate).format('DD.MM.YYYY HH:mm:ss')
                    : moment().utc().format('DD.MM.YYYY HH:mm:ss'),
                }).toPromise();

                console.log(`🔍 [CI-SYNC] Paired CO response StatusCode=${checkoutResponse?.StatusCode}`);
                if (checkoutResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
                  await this.offlineManager.removeEvacuationForCompanyUserAtEntity(
                    pendingCheckout.userId || '',
                    {
                      locationID: pendingCheckout.locationID || 0,
                      projectID: pendingCheckout.projectID || 0,
                      inventoryItemID: pendingCheckout.inventoryItemID || 0,
                    } as DeviceEntityDetail
                  );
                }
              } catch {}
            }
          }

          await this.offlineManager.emptyTable(
            'DeviceUserCheckinDetails',
            `deviceUserCheckinDetailId = ${checkIn.offlineUserCheckInDetailID}`
          );
          // Delete CI-origin induction archive rows (signOffDetailId=0 = written during offline CI,
          // not personal sign-offs). Documents are already cleared by emptyAllTables; only forms persist.
          const archivedFormsCond = `locationID = ${checkIn.locationID || 0} AND projectID = ${checkIn.projectID || 0} AND inventoryItemID = ${checkIn.inventoryItemID || 0} AND (signOffDetailId IS NULL OR signOffDetailId = 0 OR signOffDetailId = '')`;
          await this.offlineManager.emptyTable('DeviceArchivedForms', archivedFormsCond);
        }
        } catch (userCheckInError) {
          console.error(`❌ [CI-SYNC] Failed to sync checkIn for userId=${checkIn.userId} offlineId=${checkIn.offlineUserCheckInDetailID}:`, userCheckInError?.message || userCheckInError);
        }
      }

      for (const checkIn of guestCheckins) {
        try {
        const fallbackEntityIds = {
          locationID: checkIn.locationID || 0,
          projectID: checkIn.projectID || 0,
          inventoryItemID: checkIn.inventoryItemID || 0,
        };
        const entityId = fallbackEntityIds.locationID
          ? `L|${fallbackEntityIds.locationID}`
          : fallbackEntityIds.projectID
            ? `P|${fallbackEntityIds.projectID}`
            : `I|${fallbackEntityIds.inventoryItemID}`;

        // Mirror online guest flow pre-calls before SaveFormAnswers.
        const previousEntity = this.sharedDataService.dedicatedModeLocationUse;
        try {
          this.sharedDataService.dedicatedModeLocationUse = {
            locationID: fallbackEntityIds.locationID || 0,
            projectID: fallbackEntityIds.projectID || 0,
            inventoryItemID: fallbackEntityIds.inventoryItemID || 0,
          } as any;
          await this.apiService.getGuestUserDetailByPhone(checkIn.guestPhone || '').toPromise();
          await this.apiService.getCheckInDetails_Guest(
            checkIn.guestPhone || '',
            checkIn.guestFirsName || '',
            checkIn.guestMiddleName || '',
            checkIn.guestLastName || '',
            entityId
          ).toPromise();
        } catch (preCallError) {
          continue;
        } finally {
          this.sharedDataService.dedicatedModeLocationUse = previousEntity;
        }

        const inductionFormList: any[] = [];
        let groupUniqueID = '';
        let createdActivityList: any[] = [];

        const formAnswerList = Array.isArray(checkIn.formAnswerData)
          ? checkIn.formAnswerData
          : [];
        for (const formAnswerData of formAnswerList) {
          const payload = this.normalizeFormAnswerPayload(
            formAnswerData,
            fallbackEntityIds
          );
          if (!payload.formId || !payload.formVersionId) {
            continue;
          }

          try {
            const formDetailRes: any = await this.apiService
              .getDedicatedModeSignOffFormDetail(payload.formId)
              .toPromise();
            if (formDetailRes?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
              const serverFormVersionId = formDetailRes?.Result?.formData?.formVersionID || 0;
              if (serverFormVersionId > 0) {
                payload.formVersionId = serverFormVersionId;
              }
            }
          } catch {}

          const saveFormResponse: any = await this.apiService
            .saveFormAnswers(payload)
            .toPromise();

          if (saveFormResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            const result = saveFormResponse.Result || {};
            inductionFormList.push({
              formId: payload.formId,
              inductionFormContent: result.formAnswerHtmlString || '',
              answerNotificationList: result.answerNotificationList || [],
              groupUniqueID: result.groupUniqueID || '',
              createdActivityList: result.questionLogicActivityList || [],
            });
            groupUniqueID = result.groupUniqueID || groupUniqueID;
            createdActivityList = result.questionLogicActivityList || createdActivityList;
          }
        }

        const signOffDate = moment(checkIn.checkInDate).isValid()
          ? moment(checkIn.checkInDate).format('DD.MM.YYYY HH:mm:ss')
          : moment().utc().format('DD.MM.YYYY HH:mm:ss');

        const checkInPayload: any = {
          companyID: checkIn.companyID || 0,
          locationID: checkIn.locationID || 0,
          projectID: checkIn.projectID || 0,
          inventoryItemID: checkIn.inventoryItemID || 0,
          checkInLatitude: checkIn.checkInLatitude || '',
          checkInLongitude: checkIn.checkInLongitude || '',
          isGuestReturning: checkIn.isGuestReturning || false,
          guestPhone: checkIn.guestPhone || '',
          guestFirsName: checkIn.guestFirsName || '',
          guestMiddleName: checkIn.guestMiddleName || '',
          guestLastName: checkIn.guestLastName || '',
          guestPhoto: checkIn.guestPhoto || '',
          digitalInkSignature: checkIn.digitalInkSignature || '',
          userSignaturePhoto: checkIn.userSignaturePhoto || '',
          inductionFormList,
          inductionFormContent: '',
          answerNotificationList: '',
          groupUniqueID,
          createdActivityList,
          signOffDate,
          checkInDate: signOffDate,
        };

        const response: any = await this.apiService
          .insertCheckInDetailsGuest(checkInPayload)
          .toPromise();

        console.log('[EVAC] sync:checkin | guestPhone:', checkIn.guestPhone, '| API status:', response?.StatusCode)
        if (response?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
          // Paired guest CO: if the guest also checked out while offline, push the CO now
          // (before deleting the row) using the server CI ID we just obtained.
          let pairedCOPushed = false;
          try {
            const guestCheckoutRows: any[] = (await this.offlineManager.getOfflineGuestCheckoutDetails() as any[]) || [];
            const pairedGuestCO = guestCheckoutRows.find(r => r.deviceGuestUserCheckinDetailId == checkIn.offlineUserCheckInDetailID);
            if (pairedGuestCO) {
              let coUserCheckInDetailID = this.parseCheckoutUserCheckInDetailId(pairedGuestCO.userCheckInDetailID);
              if (coUserCheckInDetailID == null) {
                coUserCheckInDetailID = await this.resolveGuestCheckoutUserCheckInDetailId(pairedGuestCO);
              }
              console.log(`🔍 [CI-SYNC] Paired guest CO — guestPhone=${checkIn.guestPhone}, resolved coUserCheckInDetailID=${coUserCheckInDetailID}`);
              if (coUserCheckInDetailID != null) {
                const coResponse: any = await this.apiService.insertCheckOutDetails_Guest({
                  userCheckInDetailID: coUserCheckInDetailID,
                  guestPhone: pairedGuestCO.guestPhone != null ? String(pairedGuestCO.guestPhone) : '',
                  guestFirsName: pairedGuestCO.guestFirsName || '',
                  guestMiddleName: pairedGuestCO.guestMiddleName || '',
                  guestLastName: pairedGuestCO.guestLastName || '',
                  checkOutLatitude: this.parseCheckoutCoordinate(pairedGuestCO.checkOutLatitude),
                  checkOutLongitude: this.parseCheckoutCoordinate(pairedGuestCO.checkOutLongitude),
                  checkOutDate: moment(pairedGuestCO.checkOutDate).isValid()
                    ? moment(pairedGuestCO.checkOutDate).format('DD.MM.YYYY HH:mm:ss')
                    : moment().utc().format('DD.MM.YYYY HH:mm:ss'),
                }).toPromise();
                console.log(`🔍 [CI-SYNC] Paired guest CO response: StatusCode=${coResponse?.StatusCode}`);
                if (coResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
                  pairedCOPushed = true;
                  const guestFullName = [pairedGuestCO.guestFirsName, pairedGuestCO.guestMiddleName, pairedGuestCO.guestLastName].filter(Boolean).join(' ');
                  await this.offlineManager.removeEvacuationForGuestAtEntity(
                    guestFullName.trim(),
                    { locationID: pairedGuestCO.locationID || 0, projectID: pairedGuestCO.projectID || 0, inventoryItemID: pairedGuestCO.inventoryItemID || 0 } as DeviceEntityDetail
                  ).catch(() => {});
                }
              }
            }
          } catch (pairedCOError) {
            console.error(`❌ [CI-SYNC] Failed to push paired guest CO for guestPhone=${checkIn.guestPhone}:`, pairedCOError?.message || pairedCOError);
          }

          await this.offlineManager.emptyTable(
            'DeviceGuestUserCheckinDetails',
            `deviceGuestUserCheckinDetailId = ${checkIn.offlineUserCheckInDetailID}`
          );
          const guestArchivedFormsCond = `locationID = ${checkIn.locationID || 0} AND projectID = ${checkIn.projectID || 0} AND inventoryItemID = ${checkIn.inventoryItemID || 0} AND (signOffDetailId IS NULL OR signOffDetailId = 0 OR signOffDetailId = '')`;
          await this.offlineManager.emptyTable('DeviceArchivedForms', guestArchivedFormsCond);
          if (!pairedCOPushed) {
            await this.offlineManager.upsertEvacuationAfterOnlineGuestCheckIn({
              guestFirsName: checkIn.guestFirsName || '',
              guestMiddleName: checkIn.guestMiddleName || '',
              guestLastName: checkIn.guestLastName || '',
              guestPhotoImageVideoFileId: checkIn.guestPhotoImageVideoFileId || 0,
              locationID: checkIn.locationID || 0,
              projectID: checkIn.projectID || 0,
              inventoryItemID: checkIn.inventoryItemID || 0,
            }).catch(() => {});
          }
        }
        } catch (guestCheckInError) {
          console.error(`❌ [CI-SYNC] Failed to sync guest checkIn for guestPhone=${checkIn.guestPhone} offlineId=${checkIn.offlineUserCheckInDetailID}:`, guestCheckInError?.message || guestCheckInError);
        }
      }
    } catch {
      // Non-blocking: do not throw so the rest of sync can continue.
    }
  }

  /**
   * Sync pending offline checkouts via InsertCheckOutDetails / InsertCheckOutDetails_Guest.
   * Rows come from the same queries used for bulk offline zip (getOffline*CheckoutDetails).
   */
  private async fetchRemoteCiCoChanges(): Promise<any[]> {
    try {
      const deviceUniqueId = this.sharedDataService.deviceUID;
      const lastSyncDate = moment().subtract(24, 'hours').toISOString();
      console.log(`🔍 [REMOTE-CICO] Fetching getPendingChanges — deviceUniqueId=${deviceUniqueId}, lastSyncDate=${lastSyncDate}`);
      let pendingChanges: any = await this.offlineApi.getPendingChanges(deviceUniqueId, lastSyncDate).toPromise();
      if (pendingChanges && typeof pendingChanges === 'string') {
        try { pendingChanges = JSON.parse(pendingChanges); } catch (_) {}
      }
      if (pendingChanges) {
        if (pendingChanges.statusCode !== undefined && pendingChanges.StatusCode === undefined) {
          pendingChanges.StatusCode = pendingChanges.statusCode;
        }
        if (pendingChanges.result !== undefined && pendingChanges.Result === undefined) {
          pendingChanges.Result = pendingChanges.result;
        }
      }
      const allChanges: any[] = pendingChanges?.Result?.changes || [];
      const ciCoChanges = allChanges.filter((c: any) => c.changeType === 'CHECKIN_STATUS_UPDATED');
      console.log(`🔍 [REMOTE-CICO] StatusCode=${pendingChanges?.StatusCode}, total changes=${allChanges.length}, CHECKIN_STATUS_UPDATED=${ciCoChanges.length}`);
      ciCoChanges.forEach((c: any) => {
        console.log(`🔍 [REMOTE-CICO] change: action=${c.action}, userId=${c.userId}, entityType=${c.entityType}, entityID=${c.entityID}, deviceUniqueID=${c.deviceUniqueID}, createdDate=${c.createdDate}, sameDevice=${c.deviceUniqueID?.toUpperCase() === deviceUniqueId?.toUpperCase()}`);
      });
      if (pendingChanges?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
        const filtered = ciCoChanges.filter(
          (c: any) => c.deviceUniqueID?.toUpperCase() !== deviceUniqueId?.toUpperCase()
        );
        console.log(`🔍 [REMOTE-CICO] After same-device filter: ${filtered.length} remote changes kept`);
        return filtered;
      }
    } catch (e) {
      console.error(`❌ [REMOTE-CICO] fetchRemoteCiCoChanges error:`, e);
    }
    return [];
  }

  private async syncPendingCheckOuts(remoteCiCoChanges: any[] = []): Promise<void> {
    try {
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        return;
      }

      const deviceId = this.sharedDataService.dedicatedModeDeviceDetailData?.deviceID;
      if (!deviceId) {
        return;
      }

      const userRows: any[] =
        ((await this.offlineManager.getOfflineUserCheckoutDetails()) as any[]) || [];
      const guestRows: any[] =
        ((await this.offlineManager.getOfflineGuestCheckoutDetails()) as any[]) || [];

      console.log(`🔍 [CO-SYNC] userRows: ${userRows.length}`, userRows.map(r => ({ deviceUserCheckinDetailId: r.deviceUserCheckinDetailId, userId: r.userId, userCheckInDetailID: r.userCheckInDetailID, checkOutDate: r.checkOutDate })));
      console.log(`🔍 [CO-SYNC] guestRows: ${guestRows.length}`);

      if (userRows.length === 0 && guestRows.length === 0) {
        return;
      }

      // remoteCiCoChanges is passed in from the caller (fetched before this function runs),
      // so it only contains genuine remote changes from other devices.


      // Build a map of the most recent offline CHECKIN date per user+entity.
      // Used to skip COs where a more recent offline CI already exists on this device.
      const offlineCheckins: any[] = (await this.offlineManager.getOfflineUserCheckinDetails() as any[]) || [];
      console.log(`🔍 [CO-SYNC] offlineCheckins (pending CI rows): ${offlineCheckins.length}`, offlineCheckins.map(ci => ({ deviceUserCheckinDetailId: ci.deviceUserCheckinDetailId, userId: ci.userId, checkInDate: ci.checkInDate, userCheckInDetailID: ci.userCheckInDetailID, isOfflineDone: ci.isOfflineDone })));
      const latestCheckinDateByKey = new Map<string, string>();
      for (const ci of offlineCheckins) {
        const key = this.getOfflineEntityKey(ci);
        if (!key || !ci.checkInDate) continue;
        const existing = latestCheckinDateByKey.get(key);
        if (!existing || new Date(ci.checkInDate) > new Date(existing)) {
          latestCheckinDateByKey.set(key, ci.checkInDate);
        }
      }

      for (const row of userRows) {
        // If a more recent offline CHECKIN exists for the same user+entity on this device,
        // this CHECKOUT is no longer the last operation — skip pushing it and clear the record.
        // Exception: same-device CI→CO pairs share the same row (deviceUserCheckinDetailId),
        // so the "newer CI" IS this row's own checkin. The checkout date is also stored as
        // midnight (date-only), making it appear older than the checkin time — never delete it.
        const coKey = this.getOfflineEntityKey(row);
        const latestCheckin = coKey ? latestCheckinDateByKey.get(coKey) : null;
        const isPairedOfflineCheckin = offlineCheckins.some(
          (ci: any) => ci.deviceUserCheckinDetailId == row.deviceUserCheckinDetailId
        );
        console.log(`🔍 [CO-SYNC] userId=${row.userId} deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId} — coKey=${coKey}, latestCheckin=${latestCheckin}, isPairedOfflineCheckin=${isPairedOfflineCheckin}`);
        if (isPairedOfflineCheckin) {
          // This CO's CI has not been synced yet (same local row). Let syncPendingCheckIns
          // push the CI first and then handle the CO with the correct server CI ID.
          console.log(`🔍 [CO-SYNC] Skipping paired offline CI→CO for userId=${row.userId} deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId} — will be handled by syncPendingCheckIns`);
          continue;
        }
        // Check remote: if the server has a newer CI for this user+entity (from another device)
        // and there is no pending offline CI on this device, the CO is stale — skip it.
        // We do NOT skip when a newer *offline* CI exists on this device: in that case the CO
        // needs to be pushed first so the pending CI can succeed afterward.
        if (!latestCheckin && row.checkOutDate) {
          const rowLocId = Number(row.locationID);
          const rowProjId = Number(row.projectID);
          const rowInvId = Number(row.inventoryItemID);
          const hasNewerRemoteCheckin = remoteCiCoChanges.some((c: any) => {
            if (c.action !== 'CHECKIN' || c.userId !== row.userId) return false;
            const entityMatches =
              (rowLocId > 0 && c.entityType === 'Location' && Number(c.entityID) === rowLocId) ||
              (rowProjId > 0 && c.entityType === 'Project' && Number(c.entityID) === rowProjId) ||
              (rowInvId > 0 && c.entityType === 'InventoryItem' && Number(c.entityID) === rowInvId);
            if (!entityMatches) return false;
            return new Date(c.createdDate).getTime() > new Date(row.checkOutDate).getTime();
          });
          if (hasNewerRemoteCheckin) {
            await this.offlineManager.emptyTable(
              'DeviceUserCheckinDetails',
              `deviceUserCheckinDetailId = ${row.deviceUserCheckinDetailId}`
            ).catch(() => {});
            continue;
          }
        }

        let userCheckInDetailID = this.parseCheckoutUserCheckInDetailId(row.userCheckInDetailID);
        console.log(`🔍 [CO-SYNC] userId=${row.userId} deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId} — raw userCheckInDetailID=${row.userCheckInDetailID}, parsed=${userCheckInDetailID}`);
        if (userCheckInDetailID == null) {
          userCheckInDetailID = await this.resolveMissingUserCheckoutCheckInDetailId(row);
          console.log(`🔍 [CO-SYNC] userId=${row.userId} — resolved userCheckInDetailID=${userCheckInDetailID}`);
        }
        if (userCheckInDetailID == null) {
          console.log(`🔍 [CO-SYNC] userId=${row.userId} deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId} — no userCheckInDetailID resolved, SKIPPING checkout (will be handled by syncPendingCheckIns if CI is also pending)`);
          continue;
        }

        try {
          const response: any = await this.apiService
            .insertCheckOutDetails({
              userCheckInDetailID,
              userId: row.userId || '',
              checkOutLatitude: this.parseCheckoutCoordinate(row.checkOutLatitude),
              checkOutLongitude: this.parseCheckoutCoordinate(row.checkOutLongitude),
              checkOutDate: moment(row.checkOutDate).isValid()
                ? moment(row.checkOutDate).format('DD.MM.YYYY HH:mm:ss')
                : moment().utc().format('DD.MM.YYYY HH:mm:ss'),
            })
            .toPromise();

          console.log(`🔍 [CO-SYNC] insertCheckOutDetails response StatusCode=${response?.StatusCode} for userId=${row.userId} deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId}`);
          if (response?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            // Skip the DeviceUserCheckinDetails cleanup inside removeEvacuationForCompanyUserAtEntity
            // when a pending offline CI exists for the same user+entity — that DELETE would wipe the
            // new offline CI row (it matches: same userId, same entity, checkOutDate IS NULL).
            const hasPendingOfflineCI = offlineCheckins.some(
              (ci: any) =>
                String(ci.userId) === String(row.userId) &&
                (Number(ci.locationID) || 0) === (Number(row.locationID) || 0) &&
                (Number(ci.projectID) || 0) === (Number(row.projectID) || 0) &&
                (Number(ci.inventoryItemID) || 0) === (Number(row.inventoryItemID) || 0)
            );
            if (!hasPendingOfflineCI) {
              await this.offlineManager.removeEvacuationForCompanyUserAtEntity(
                row.userId || '',
                {
                  locationID: row.locationID || 0,
                  projectID: row.projectID || 0,
                  inventoryItemID: row.inventoryItemID || 0,
                } as DeviceEntityDetail
              );
            } else {
              console.log(`🔍 [CO-SYNC] Skipping removeEvacuationForCompanyUserAtEntity for userId=${row.userId} — pending offline CI exists, will be cleaned up after CI sync`);
            }
            console.log(`🔍 [CO-SYNC] Deleting CO row deviceUserCheckinDetailId=${row.deviceUserCheckinDetailId}`);
            await this.offlineManager.emptyTable(
              'DeviceUserCheckinDetails',
              `deviceUserCheckinDetailId = ${row.deviceUserCheckinDetailId}`
            );
          }
        } catch (coErr) {
          console.error(`❌ [CO-SYNC] insertCheckOutDetails threw for userId=${row.userId}:`, coErr);
        }
      }

      for (const row of guestRows) {
        let userCheckInDetailID = this.parseCheckoutUserCheckInDetailId(row.userCheckInDetailID);
        if (userCheckInDetailID == null) {
          userCheckInDetailID = await this.resolveGuestCheckoutUserCheckInDetailId(row);
        }
        console.log('[EVAC] sync:checkout | guestPhone:', row.guestPhone, '| resolved ID:', userCheckInDetailID)
        if (userCheckInDetailID == null) {
          // Cannot resolve — the offline CI may not have been synced yet.
          // Skip (do not delete) so syncPendingCheckIns can push the CI first and then
          // handle this CO inline with the correct server ID.
          continue;
        }

        try {
          const response: any = await this.apiService
            .insertCheckOutDetails_Guest({
              userCheckInDetailID,
              guestPhone: row.guestPhone != null ? String(row.guestPhone) : '',
              guestFirsName: row.guestFirsName || '',
              guestMiddleName: row.guestMiddleName || '',
              guestLastName: row.guestLastName || '',
              checkOutLatitude: this.parseCheckoutCoordinate(row.checkOutLatitude),
              checkOutLongitude: this.parseCheckoutCoordinate(row.checkOutLongitude),
              checkOutDate: moment(row.checkOutDate).isValid()
                ? moment(row.checkOutDate).format('DD.MM.YYYY HH:mm:ss')
                : moment().utc().format('DD.MM.YYYY HH:mm:ss'),
            })
            .toPromise();

          console.log('[EVAC] sync:checkout | guestPhone:', row.guestPhone, '| API status:', response?.StatusCode)
          if (response?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
            const guestFullName =
              (row.guestFirsName || '') +
              ' ' +
              (row.guestMiddleName ? row.guestMiddleName + ' ' : '') +
              (row.guestLastName || '');
            await this.offlineManager.removeEvacuationForGuestAtEntity(
              guestFullName.trim(),
              {
                locationID: row.locationID || 0,
                projectID: row.projectID || 0,
                inventoryItemID: row.inventoryItemID || 0,
              } as DeviceEntityDetail
            );
            await this.offlineManager.emptyTable(
              'DeviceGuestUserCheckinDetails',
              `deviceGuestUserCheckinDetailId = ${row.deviceGuestUserCheckinDetailId}`
            );
          }
        } catch (e) {
        }
      }
    } catch {}
  }

  /** Server check-in detail id for InsertCheckOutDetails (numeric). */
  private parseCheckoutUserCheckInDetailId(val: any): number | null {
    if (val === null || val === undefined || val === '') {
      return null;
    }
    const n = Number(val);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Backfill missing local userCheckInDetailID from server active check-ins, then persist to SQLite.
   */
  private async resolveMissingUserCheckoutCheckInDetailId(row: any): Promise<number | null> {
    if (!row?.userId) {
      return null;
    }

    try {
      const response: any = await this.apiService
        .getUserCurrentCheckingDetails(row.userId)
        .toPromise();

      const currentCheckIns: any[] = Array.isArray(response?.Result) ? response.Result : [];
      const openCheckIns = currentCheckIns.filter((item) => this.isOpenServerCheckIn(item));
      const openCheckInsForEntity = openCheckIns.filter((item) =>
        this.isSameCheckoutEntity(item, row)
      );

      const matchedCheckIn =
        openCheckInsForEntity[0] || (openCheckIns.length === 1 ? openCheckIns[0] : null);
      const resolvedId = this.parseCheckoutUserCheckInDetailId(
        matchedCheckIn?.userCheckInDetailID
      );

      if (resolvedId == null) {
        return null;
      }

      await this.offlineManager.dbQuery(
        'UPDATE DeviceUserCheckinDetails SET userCheckInDetailID = ? WHERE deviceUserCheckinDetailId = ?',
        [resolvedId, row.deviceUserCheckinDetailId]
      );
      row.userCheckInDetailID = resolvedId;
      return resolvedId;
    } catch (error) {
      return null;
    }
  }

  private async resolveGuestCheckoutUserCheckInDetailId(row: any): Promise<number | null> {
    const entityId = row.locationID > 0
      ? `L|${row.locationID}`
      : row.projectID > 0
        ? `P|${row.projectID}`
        : row.inventoryItemID > 0
          ? `I|${row.inventoryItemID}`
          : null;
    console.log('[EVAC] sync:checkout:resolve | guestPhone:', row.guestPhone, '| entityId:', entityId)
    if (!entityId || !row.guestPhone) {
      console.warn('[EVAC] sync:checkout:resolve | FAILED to resolve ID: missing entityId or guestPhone')
      return null;
    }
    const phone = String(row.guestPhone);
    const companyFolderName = this.sharedDataService.dedicatedModeDeviceDetailData?.companyFolderName || '';
    const url = `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.GetCheckInDetails_Guest}` +
      `?phone=${encodeURIComponent(phone)}` +
      `&firstName=${encodeURIComponent(row.guestFirsName || '')}` +
      `&middleName=${encodeURIComponent(row.guestMiddleName || '')}` +
      `&lastName=${encodeURIComponent(row.guestLastName || '')}` +
      `&entityId=${entityId}`;
    try {
      await this.http.get(url, { headers: { userID: phone, companyFolderName } }).toPromise();
      return null;
    } catch (error) {
      const validationErrors = error?.error?.ResponseException?.ValidationErrors;
      if (validationErrors?.length > 0) {
        const fieldsInfo = validationErrors[0].Field.split('#');
        if (fieldsInfo.length > 1) {
          const resolvedId = this.parseCheckoutUserCheckInDetailId(fieldsInfo[1]);
          console.log('[EVAC] sync:checkout:resolve | resolved ID:', resolvedId)
          return resolvedId;
        }
      }
      console.warn('[EVAC] sync:checkout:resolve | FAILED to resolve ID:', error?.message || error)
      return null;
    }
  }

  private isOpenServerCheckIn(item: any): boolean {
    const checkOutDate = item?.checkOutDate;
    return (
      checkOutDate === null ||
      checkOutDate === undefined ||
      checkOutDate === '' ||
      checkOutDate === StaticDataService.userDefaultDate
    );
  }

  private isSameCheckoutEntity(serverItem: any, localRow: any): boolean {
    const localLocationID = Number(localRow?.locationID || 0);
    const localProjectID = Number(localRow?.projectID || 0);
    const localInventoryItemID = Number(localRow?.inventoryItemID || 0);
    const serverLocationID = Number(serverItem?.locationID || 0);
    const serverProjectID = Number(serverItem?.projectID || 0);
    const serverInventoryItemID = Number(serverItem?.inventoryItemID || 0);

    if (localLocationID > 0) {
      return serverLocationID === localLocationID;
    }
    if (localProjectID > 0) {
      return serverProjectID === localProjectID;
    }
    if (localInventoryItemID > 0) {
      return serverInventoryItemID === localInventoryItemID;
    }

    return (
      serverLocationID === localLocationID &&
      serverProjectID === localProjectID &&
      serverInventoryItemID === localInventoryItemID
    );
  }

  private parseCheckoutCoordinate(val: any): number | undefined {
    if (val === null || val === undefined || val === '') {
      return undefined;
    }
    const n = typeof val === 'number' ? val : parseFloat(String(val));
    return Number.isFinite(n) ? n : undefined;
  }

  /**
   * Sync pending sign-offs (offline submissions)
   * Uses the same online APIs (saveFormAnswers + insertPersonalModeSignOffDetails) 
   * that are used when submitting forms online
   */
  async syncPendingSignOffs(): Promise<void> {
    try {
      console.log('📤 [SIGN-OFF SYNC] Starting sync of pending sign-offs...');
      
      // Check network status
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        console.log('📤 [SIGN-OFF SYNC] No network connection, skipping sign-off sync');
        return;
      }
      console.log('📤 [SIGN-OFF SYNC] Network is connected, proceeding with sync');

      // Get all pending offline sign-offs
      const pendingSignOffsResult: any = await this.offlineManager.getOfflineSignoffDetails();
      const pendingSignOffs: any[] = Array.isArray(pendingSignOffsResult) ? pendingSignOffsResult : [];
      
      console.log(`📤 [SIGN-OFF SYNC] Retrieved sign-offs from database:`, pendingSignOffs.length);
      
      if (!pendingSignOffs || pendingSignOffs.length === 0) {
        console.log('📤 [SIGN-OFF SYNC] No pending sign-offs to sync');
        return;
      }

      console.log(`📤 [SIGN-OFF SYNC] Found ${pendingSignOffs.length} pending sign-off(s) to sync`);
      console.log(`📤 [SIGN-OFF SYNC] Sign-off details:`, JSON.stringify(pendingSignOffs, null, 2));

      // Update sync status to show header loader
      this.updateSyncStatus({
        isSyncing: true,
        progress: 0,
        status: 'signoff_sync_started',
      });

      // Process each sign-off individually using online APIs
      let processedCount = 0;
      for (const signOff of pendingSignOffs) {
        try {
          // Skip if already submitting to prevent duplicate submissions
          if (signOff.isSubmitting === 1 || signOff.isSubmitting === true) {
            console.log(`⏭️ [SIGN-OFF SYNC] Skipping sign-off ${signOff.signOffDetailId} - already submitting`);
            continue;
          }

          // Step 1: Get form answer data if formSubmitDataId exists
          let formAnswerResult: any = null;
          let formAnswerData: any = null; // Declare outside if block for use in sign-off post data
          console.log(`📤 [SIGN-OFF SYNC] Processing sign-off ${signOff.signOffDetailId}, formSubmitDataId: ${signOff.formSubmitDataId}, formVersionID: ${signOff.formVersionID}, documentID: ${signOff.documentID}`);
          
          // Determine if this is a document sign-off (formVersionID === 0 and documentID > 0) or form sign-off
          const isDocumentSignOff = (signOff.formVersionID === 0 || signOff.formVersionID === null) && 
                                    signOff.documentID > 0 && 
                                    (!signOff.formSubmitDataId || signOff.formSubmitDataId === 0);

          const formSubmittedOnline =
            signOff.formSubmittedOnline === 1 ||
            signOff.formSubmittedOnline === '1' ||
            signOff.formSubmittedOnline === true;
          let hybridSignOffPayload: any = null;
          if (formSubmittedOnline && signOff.signOffPayload) {
            try {
              hybridSignOffPayload =
                typeof signOff.signOffPayload === 'string'
                  ? JSON.parse(signOff.signOffPayload)
                  : signOff.signOffPayload;
            } catch (e) {
              console.warn(
                `⚠️ [SIGN-OFF SYNC] Failed to parse hybrid signOffPayload for sign-off ${signOff.signOffDetailId}:`,
                e
              );
            }
          }

          // Network-cut sign-offs: full payload snapshot was saved to skip saveFormAnswers on replay
          let networkCutPayload: any = null;
          if (signOff.fullPayloadJson) {
            try {
              networkCutPayload = typeof signOff.fullPayloadJson === 'string'
                ? JSON.parse(signOff.fullPayloadJson)
                : signOff.fullPayloadJson;
              console.log(`📤 [SIGN-OFF SYNC] Network-cut sign-off ${signOff.signOffDetailId} — found stored full payload, will skip saveFormAnswers`);
            } catch (e) {
              console.warn(`⚠️ [SIGN-OFF SYNC] Failed to parse fullPayloadJson for sign-off ${signOff.signOffDetailId}:`, e);
            }
          }

          const hasLocalFormSubmitData =
            signOff.formSubmitDataId &&
            signOff.formSubmitDataId !== 0 &&
            signOff.formSubmitDataId !== '0';

          if (hasLocalFormSubmitData && !formSubmittedOnline) {
            console.log(`📤 [SIGN-OFF SYNC] Retrieving form answer data for formSubmitDataId: ${signOff.formSubmitDataId}`);
            formAnswerData = await this.offlineManager.getOfflineSubmittedFormData(
              signOff.formSubmitDataId
            );
            
            console.log(`📤 [SIGN-OFF SYNC] Form answer data retrieved:`, formAnswerData ? 'Found' : 'Not found');
            
            // Log form data structure to check if images are saved
            if (formAnswerData) {
              console.log(`📸 [IMAGE DEBUG] Form answer data structure check:`, {
                hasFormattedSections: !!formAnswerData.formattedSections,
                formattedSectionsType: typeof formAnswerData.formattedSections,
                hasQuestionAnswers: !!formAnswerData.questionAnswers,
                questionAnswersType: typeof formAnswerData.questionAnswers,
                hasAccidentReportQuestionAnswers: !!formAnswerData.accidentReportQuestionAnswers,
                accidentReportQuestionAnswersType: typeof formAnswerData.accidentReportQuestionAnswers
              });
              
              // Extract and log ALL imageVideoFileIds from retrieved form data
              try {
                const retrievedImageVideoFileIds: number[] = [];
                
                // Check formattedSections
                let formattedSections = formAnswerData.formattedSections;
                if (typeof formattedSections === 'string') {
                  formattedSections = JSON.parse(formattedSections);
                }
                if (Array.isArray(formattedSections)) {
                  formattedSections.forEach((section: any, sectionIndex: number) => {
                    if (section.answerData && Array.isArray(section.answerData)) {
                      section.answerData.forEach((answerData: any, answerIndex: number) => {
                        if (answerData?.answerData?.imageVideoFileId) {
                          retrievedImageVideoFileIds.push(answerData.answerData.imageVideoFileId);
                          console.log(`📥 [RETRIEVE DEBUG] Found imageVideoFileId in formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, answerData.answerData.imageVideoFileId);
                        }
                      });
                    }
                  });
                }
                
                // Check questionAnswers
                let questionAnswers = formAnswerData.questionAnswers;
                if (typeof questionAnswers === 'string') {
                  questionAnswers = JSON.parse(questionAnswers);
                }
                if (Array.isArray(questionAnswers)) {
                  questionAnswers.forEach((qa: any, qaIndex: number) => {
                    if (qa?.imageVideoFileId) {
                      retrievedImageVideoFileIds.push(qa.imageVideoFileId);
                      console.log(`📥 [RETRIEVE DEBUG] Found imageVideoFileId in questionAnswers[${qaIndex}]:`, qa.imageVideoFileId);
                    }
                  });
                }
                
                // Check accidentReportQuestionAnswers
                let accidentReportQuestionAnswers = formAnswerData.accidentReportQuestionAnswers;
                if (typeof accidentReportQuestionAnswers === 'string') {
                  accidentReportQuestionAnswers = JSON.parse(accidentReportQuestionAnswers);
                }
                if (Array.isArray(accidentReportQuestionAnswers)) {
                  accidentReportQuestionAnswers.forEach((qa: any, qaIndex: number) => {
                    if (qa?.accidentAttachmentFileId) {
                      retrievedImageVideoFileIds.push(qa.accidentAttachmentFileId);
                      console.log(`📥 [RETRIEVE DEBUG] Found accidentAttachmentFileId in accidentReportQuestionAnswers[${qaIndex}]:`, qa.accidentAttachmentFileId);
                    }
                  });
                }
                
                console.log(`📥 [RETRIEVE DEBUG] Total imageVideoFileIds retrieved from database:`, retrievedImageVideoFileIds.length, 'IDs:', retrievedImageVideoFileIds);
              } catch (e) {
                console.warn(`📥 [RETRIEVE DEBUG] Error parsing form data for imageVideoFileIds:`, e);
              }
            }
          }

          // Step 2: Upload signature files and form photo/video attachments
          let uploadedSignatureFiles: any = {};
          const imageVideoFileIdsToUpload = new Set<number>();
          
          // Collect signature file IDs
          if (signOff.userSignaturePhotoImageVideoFileId) {
            imageVideoFileIdsToUpload.add(signOff.userSignaturePhotoImageVideoFileId);
            if (isDocumentSignOff) {
              console.log(`📸 [DOCUMENT SIGN-OFF] Added userSignaturePhotoImageVideoFileId: ${signOff.userSignaturePhotoImageVideoFileId}`);
            }
          }
            if (signOff.digitalInkSignatureImageVideoFileId) {
            imageVideoFileIdsToUpload.add(signOff.digitalInkSignatureImageVideoFileId);
            if (isDocumentSignOff) {
              console.log(`📸 [DOCUMENT SIGN-OFF] Added digitalInkSignatureImageVideoFileId: ${signOff.digitalInkSignatureImageVideoFileId}`);
            }
          }
          
          if (isDocumentSignOff) {
            console.log(`📸 [DOCUMENT SIGN-OFF] Total signature files to upload: ${imageVideoFileIdsToUpload.size}`);
          }
          
          // Extract imageVideoFileId values from form data (question attachments)
          if (formAnswerData) {
            console.log(`📸 [IMAGE DEBUG] Starting extraction of imageVideoFileIds from form data...`);
            try {
              // Parse formattedSections if it's a string
              let formattedSections = formAnswerData.formattedSections;
              if (typeof formattedSections === 'string') {
                formattedSections = JSON.parse(formattedSections);
              }
              
              if (Array.isArray(formattedSections)) {
                formattedSections.forEach((section: any, sectionIndex: number) => {
                  if (section.answerData && Array.isArray(section.answerData)) {
                    section.answerData.forEach((answerData: any, answerIndex: number) => {
                      if (answerData?.answerData?.imageVideoFileId) {
                        const imageIdValue = answerData.answerData.imageVideoFileId;
                        // Handle comma-separated imageVideoFileIds (multiple files for same question)
                        if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                          const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id);
                          console.log(`📸 [IMAGE DEBUG] Found multiple imageVideoFileIds in formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, imageIds);
                          imageIds.forEach(id => {
                            const parsedId = parseInt(id, 10);
                            if (!isNaN(parsedId)) {
                              imageVideoFileIdsToUpload.add(parsedId);
                            }
                          });
                        } else {
                          const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                          if (!isNaN(imageId)) {
                            console.log(`📸 [IMAGE DEBUG] Found imageVideoFileId in formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, imageId);
                            imageVideoFileIdsToUpload.add(imageId);
                          }
                        }
                      }
                    });
                  }
                });
              }
              
              // Also check questionAnswers array if it exists
              let questionAnswers = formAnswerData.questionAnswers;
              if (typeof questionAnswers === 'string') {
                questionAnswers = JSON.parse(questionAnswers);
              }
              
              if (Array.isArray(questionAnswers)) {
                questionAnswers.forEach((qa: any, qaIndex: number) => {
                  if (qa?.imageVideoFileId) {
                    const imageIdValue = qa.imageVideoFileId;
                    // Handle comma-separated imageVideoFileIds (multiple files for same question)
                    if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                      const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id);
                      console.log(`📸 [IMAGE DEBUG] Found multiple imageVideoFileIds in questionAnswers[${qaIndex}]:`, imageIds);
                      imageIds.forEach(id => {
                        const parsedId = parseInt(id, 10);
                        if (!isNaN(parsedId)) {
                          imageVideoFileIdsToUpload.add(parsedId);
                        }
                      });
                    } else {
                      const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                      if (!isNaN(imageId)) {
                        console.log(`📸 [IMAGE DEBUG] Found imageVideoFileId in questionAnswers[${qaIndex}]:`, imageId);
                        imageVideoFileIdsToUpload.add(imageId);
                      }
                    }
                  }
                });
              }
              
              // Check accidentReportQuestionAnswers
              let accidentReportQuestionAnswers = formAnswerData.accidentReportQuestionAnswers;
              if (typeof accidentReportQuestionAnswers === 'string') {
                accidentReportQuestionAnswers = JSON.parse(accidentReportQuestionAnswers);
              }
              
              if (Array.isArray(accidentReportQuestionAnswers)) {
                accidentReportQuestionAnswers.forEach((qa: any, qaIndex: number) => {
                  if (qa?.accidentAttachmentFileId) {
                    const imageIdValue = qa.accidentAttachmentFileId;
                    // Handle comma-separated accidentAttachmentFileIds (multiple files for same question)
                    if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                      const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id);
                      console.log(`📸 [IMAGE DEBUG] Found multiple accidentAttachmentFileIds in accidentReportQuestionAnswers[${qaIndex}]:`, imageIds);
                      imageIds.forEach(id => {
                        const parsedId = parseInt(id, 10);
                        if (!isNaN(parsedId)) {
                          imageVideoFileIdsToUpload.add(parsedId);
                        }
                      });
                    } else {
                      const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                      if (!isNaN(imageId)) {
                        console.log(`📸 [IMAGE DEBUG] Found accidentAttachmentFileId in accidentReportQuestionAnswers[${qaIndex}]:`, imageId);
                        imageVideoFileIdsToUpload.add(imageId);
                      }
                    }
                  }
                });
              }
              
              console.log(`📸 [IMAGE DEBUG] Total imageVideoFileIds extracted: ${imageVideoFileIdsToUpload.size}`, Array.from(imageVideoFileIdsToUpload));
            } catch (error) {
              console.warn(`⚠️ [SIGN-OFF SYNC] Error extracting imageVideoFileIds from form data:`, error);
            }
          }
          
          console.log(`📸 [IMAGE DEBUG] Total file IDs to upload (including signatures): ${imageVideoFileIdsToUpload.size}`, Array.from(imageVideoFileIdsToUpload));
          
          // Compare retrieved vs extracted imageVideoFileIds
          if (formAnswerData) {
            try {
              const retrievedIds: number[] = [];
              let formattedSections = formAnswerData.formattedSections;
              if (typeof formattedSections === 'string') {
                formattedSections = JSON.parse(formattedSections);
              }
              if (Array.isArray(formattedSections)) {
                formattedSections.forEach((section: any) => {
                  if (section.answerData && Array.isArray(section.answerData)) {
                    section.answerData.forEach((answerData: any) => {
                      if (answerData?.answerData?.imageVideoFileId) {
                        const imageIdValue = answerData.answerData.imageVideoFileId;
                        // Handle comma-separated imageVideoFileIds
                        if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                          const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id).map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                          retrievedIds.push(...imageIds);
                        } else {
                          const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                          if (!isNaN(imageId)) {
                            retrievedIds.push(imageId);
                          }
                        }
                      }
                    });
                  }
                });
              }
              let questionAnswers = formAnswerData.questionAnswers;
              if (typeof questionAnswers === 'string') {
                questionAnswers = JSON.parse(questionAnswers);
              }
              if (Array.isArray(questionAnswers)) {
                questionAnswers.forEach((qa: any) => {
                  if (qa?.imageVideoFileId) {
                    const imageIdValue = qa.imageVideoFileId;
                    // Handle comma-separated imageVideoFileIds
                    if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                      const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id).map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                      retrievedIds.push(...imageIds);
                    } else {
                      const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                      if (!isNaN(imageId)) {
                        retrievedIds.push(imageId);
                      }
                    }
                  }
                });
              }
              let accidentReportQuestionAnswers = formAnswerData.accidentReportQuestionAnswers;
              if (typeof accidentReportQuestionAnswers === 'string') {
                accidentReportQuestionAnswers = JSON.parse(accidentReportQuestionAnswers);
              }
              if (Array.isArray(accidentReportQuestionAnswers)) {
                accidentReportQuestionAnswers.forEach((qa: any) => {
                  if (qa?.accidentAttachmentFileId) {
                    const imageIdValue = qa.accidentAttachmentFileId;
                    // Handle comma-separated accidentAttachmentFileIds
                    if (typeof imageIdValue === 'string' && imageIdValue.includes(',')) {
                      const imageIds = imageIdValue.split(',').map(id => id.trim()).filter(id => id).map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                      retrievedIds.push(...imageIds);
                    } else {
                      const imageId = typeof imageIdValue === 'string' ? parseInt(imageIdValue, 10) : imageIdValue;
                      if (!isNaN(imageId)) {
                        retrievedIds.push(imageId);
                      }
                    }
                  }
                });
              }
              
              const extractedIds = Array.from(imageVideoFileIdsToUpload);
              const missingIds = retrievedIds.filter(id => !extractedIds.includes(id));
              const extraIds = extractedIds.filter(id => !retrievedIds.includes(id) && id !== signOff.userSignaturePhotoImageVideoFileId && id !== signOff.digitalInkSignatureImageVideoFileId);
              
              console.log(`🔍 [COMPARE DEBUG] Retrieved imageVideoFileIds from DB:`, retrievedIds);
              console.log(`🔍 [COMPARE DEBUG] Extracted imageVideoFileIds for upload:`, extractedIds);
              if (missingIds.length > 0) {
                console.warn(`⚠️ [COMPARE DEBUG] MISSING imageVideoFileIds (in DB but not extracted):`, missingIds);
              }
              if (extraIds.length > 0) {
                console.warn(`⚠️ [COMPARE DEBUG] EXTRA imageVideoFileIds (extracted but not in DB):`, extraIds);
              }
              if (missingIds.length === 0 && extraIds.length === 0) {
                console.log(`✅ [COMPARE DEBUG] All imageVideoFileIds match between saved and retrieved data`);
              }
            } catch (e) {
              console.warn(`🔍 [COMPARE DEBUG] Error comparing imageVideoFileIds:`, e);
            }
          }
          
          // Upload all files (signatures + question attachments)
          if (imageVideoFileIdsToUpload.size > 0) {
            console.log(`📸 [IMAGE DEBUG] Starting file upload process for ${imageVideoFileIdsToUpload.size} file(s)...`);
            console.log(`📸 [IMAGE DEBUG] Looking for these imageVideoFileIds:`, Array.from(imageVideoFileIdsToUpload));
            const allFilesResult: any = await this.offlineManager.getAllFormSubmittedFiles();
            const allFiles: any[] = Array.isArray(allFilesResult) ? allFilesResult : [];
            console.log(`📸 [IMAGE DEBUG] Retrieved ${allFiles.length} files from local database`);
            console.log(`📸 [IMAGE DEBUG] Files in database:`, allFiles.map((f: any) => ({
              imageVideoFileId: f.imageVideoFileId,
              fileName: f.fileName
            })));
            
            // Separate signature files from other files (matching online flow)
            const signatureFileIds = new Set<number>();
            const otherFileIds = new Set<number>();
            
            if (signOff.userSignaturePhotoImageVideoFileId) {
              signatureFileIds.add(signOff.userSignaturePhotoImageVideoFileId);
            }
            if (signOff.digitalInkSignatureImageVideoFileId) {
              signatureFileIds.add(signOff.digitalInkSignatureImageVideoFileId);
            }
            
            imageVideoFileIdsToUpload.forEach((fileId) => {
              if (signatureFileIds.has(fileId)) {
                // This is a signature file
              } else {
                otherFileIds.add(fileId);
              }
            });
            
            console.log(`📝 [SIGNATURE UPLOAD] Separated files - Signatures: ${signatureFileIds.size}, Other files: ${otherFileIds.size}`);
            
            // Step 2a: Upload signature files via InductionSignatureUpload first (matching online flow)
            if (signatureFileIds.size > 0) {
              console.log(`📝 [SIGNATURE UPLOAD] Uploading ${signatureFileIds.size} signature file(s) via InductionSignatureUpload...`);
              for (const signatureFileId of signatureFileIds) {
                const fileData = allFiles.find((f: any) => f.imageVideoFileId === signatureFileId);
                if (fileData) {
                  try {
                    const offlineFileDirectory =
                      this.filehandlerService.offlineFilesDirectory() +
                      StaticDataService.offlineFilesFolderName +
                      '/' +
                      fileData.fileName;
                    console.log(`📝 [SIGNATURE UPLOAD] Preparing signature file ${signatureFileId}: ${offlineFileDirectory}`);
                    const response = await fetch(Capacitor.convertFileSrc(offlineFileDirectory));
                    const blob = await response.blob();
                    console.log(`📝 [SIGNATURE UPLOAD] Signature blob created, size: ${blob.size} bytes`);
                    
                    // Format filename for signature upload (just the generated name, not original|generated)
                    const fileExtension = fileData.fileName.split('.').pop() || 'jpeg';
                    const signatureFileName = 'signature' + this.utilService.getCurrentTimeStamp() + '.' + fileExtension;
                    
                    console.log(`📝 [SIGNATURE UPLOAD] Uploading signature ${signatureFileId} via InductionSignatureUpload: ${signatureFileName}`);
                    const signatureUploadResponse: any = await this.apiService.inductionSignatureUpload(blob, signatureFileName).toPromise();
                    
                    if (signatureUploadResponse?.Result) {
                      // InductionSignatureUpload returns just the uploaded filename (matching online flow)
                      const uploadedSignatureName = signatureUploadResponse.Result;
                      // Store just the uploaded filename for signatures (matching online format: "signature1773783663077.jpeg")
                      uploadedSignatureFiles[`imageVideoFileId_${signatureFileId}`] = uploadedSignatureName;
                      console.log(`✅ [SIGNATURE UPLOAD] Signature ${signatureFileId} uploaded successfully: ${uploadedSignatureName}`);
                    } else {
                      console.error(`❌ [SIGNATURE UPLOAD] Signature upload failed for ${signatureFileId}, response:`, signatureUploadResponse);
                    }
                  } catch (signatureError) {
                    console.error(`❌ [SIGNATURE UPLOAD] Error uploading signature ${signatureFileId}:`, signatureError);
                  }
                } else {
                  console.warn(`⚠️ [SIGNATURE UPLOAD] Signature file NOT found in local DB for imageVideoFileId: ${signatureFileId}`);
                }
              }
              console.log(`✅ [SIGNATURE UPLOAD] Completed uploading ${signatureFileIds.size} signature file(s)`);
            }
            
            // Step 2b: Upload remaining files (question attachments) via MultiplePhotosOrVideosUpload
            const filesToUpload: any[] = [];
            
            // Prepare remaining files for upload
            for (const imageVideoFileId of otherFileIds) {
              console.log(`📸 [IMAGE DEBUG] Searching for imageVideoFileId: ${imageVideoFileId}`);
              const fileData = allFiles.find(
                (f: any) => f.imageVideoFileId === imageVideoFileId
              );
              if (fileData) {
                console.log(`✅ [IMAGE DEBUG] Found file in local DB for imageVideoFileId ${imageVideoFileId}:`, {
                  fileName: fileData.fileName,
                  imageVideoFileId: fileData.imageVideoFileId,
                  fullFileData: fileData
                });
                
                if (isDocumentSignOff) {
                  console.log(`📸 [DOCUMENT SIGN-OFF] Preparing photo/video file:`, {
                    imageVideoFileId: imageVideoFileId,
                    fileName: fileData.fileName,
                    isUserSignature: imageVideoFileId === signOff.userSignaturePhotoImageVideoFileId,
                    isDigitalInk: imageVideoFileId === signOff.digitalInkSignatureImageVideoFileId
                  });
                }
                
                const offlineFileDirectory =
                  this.filehandlerService.offlineFilesDirectory() +
                  StaticDataService.offlineFilesFolderName +
                  '/' +
                  fileData.fileName;
                try {
                  console.log(`📸 [IMAGE DEBUG] Preparing file for upload: ${offlineFileDirectory}`);
                const response = await fetch(Capacitor.convertFileSrc(offlineFileDirectory));
                const blob = await response.blob();
                  console.log(`📸 [IMAGE DEBUG] File blob created, size: ${blob.size} bytes`);
                  
                  if (isDocumentSignOff) {
                    console.log(`📸 [DOCUMENT SIGN-OFF] File blob prepared:`, {
                      imageVideoFileId: imageVideoFileId,
                      blobSize: blob.size,
                      fileName: fileData.fileName
                    });
                  }
                  
                  // Format filename to match online mode: originalFilename|generatedUploadedFilename
                  // Use originalFileName from database if available, otherwise fall back to fileName (generated ID)
                  const originalFileName = fileData.originalFileName || fileData.fileName;
                  const fileExtension = originalFileName.split('.').pop() || 'jpg';
                  const generatedUploadedFilename = 'photo' + this.utilService.getCurrentTimeStamp() + '.' + fileExtension;
                  const formattedFileName = originalFileName + '|' + generatedUploadedFilename;
                  
                filesToUpload.push({
                    fileId: imageVideoFileId,
                  blob: blob,
                    fileName: formattedFileName
                  });
                  console.log(`✅ [IMAGE DEBUG] File ${imageVideoFileId} prepared for upload: ${formattedFileName}`);
                  
                  if (isDocumentSignOff) {
                    console.log(`✅ [DOCUMENT SIGN-OFF] File ready for upload:`, {
                      imageVideoFileId: imageVideoFileId,
                      formattedFileName: formattedFileName,
                      originalFileName: originalFileName
                    });
                  }
                } catch (fileError) {
                  console.error(`❌ [IMAGE DEBUG] Could not prepare file ${imageVideoFileId} for upload:`, fileError);
                }
              } else {
                console.warn(`⚠️ [IMAGE DEBUG] File NOT found in local DB for imageVideoFileId: ${imageVideoFileId}`);
              }
            }

            console.log(`📸 [IMAGE DEBUG] Prepared ${filesToUpload.length} non-signature file(s) for upload via MultiplePhotosOrVideosUpload`);
            
            if (isDocumentSignOff) {
              console.log(`📸 [DOCUMENT SIGN-OFF] Prepared ${filesToUpload.length} photo/video file(s) for upload`);
            }

            if (filesToUpload.length === 0 && otherFileIds.size > 0) {
              console.warn(`⚠️ [IMAGE DEBUG] No non-signature files prepared for upload! Expected ${otherFileIds.size} file(s) but got 0.`);
              console.warn(`⚠️ [IMAGE DEBUG] This means files were not found in database for imageVideoFileIds:`, Array.from(otherFileIds));
            } else if (filesToUpload.length === 0 && otherFileIds.size === 0) {
              console.log(`📸 [IMAGE DEBUG] No non-signature files to upload (only signatures were uploaded)`);
            }

            if (filesToUpload.length > 0) {
              const formData = new FormData();
              filesToUpload.forEach((fileData, index) => {
                console.log(`📸 [IMAGE DEBUG] Adding file ${index + 1}/${filesToUpload.length} to FormData:`, fileData.fileName);
                formData.append('files', fileData.blob, fileData.fileName);
              });

              if (isDocumentSignOff) {
                console.log(`📸 [DOCUMENT SIGN-OFF] Uploading ${filesToUpload.length} photo/video file(s) to MultiplePhotosOrVideosUpload...`);
              }
              
              console.log(`📸 [IMAGE DEBUG] Uploading ${filesToUpload.length} file(s) to server using MultiplePhotosOrVideosUpload (same as online)...`);
              const uploadResponse: any = await this.apiService.multiplePhotosOrVideosUpload(formData).toPromise();
              console.log(`📸 [IMAGE DEBUG] Upload response received:`, {
                hasResult: !!uploadResponse?.Result,
                resultCount: uploadResponse?.Result?.length || 0
              });
              
              if (isDocumentSignOff) {
                console.log(`📸 [DOCUMENT SIGN-OFF] Upload response:`, {
                  hasResult: !!uploadResponse?.Result,
                  resultCount: uploadResponse?.Result?.length || 0,
                  results: uploadResponse?.Result
                });
              }
              
              if (uploadResponse?.Result) {
                uploadResponse.Result.forEach((uploadedFile: any, index: number) => {
                  // Online API returns originalName and uploadName, not originalFileName and newFileName
                  // Process originalName same as online mode (replace commas with spaces)
                  let originalName = uploadedFile.originalName || uploadedFile.originalFileName;
                  if (originalName && typeof originalName === 'string') {
                    originalName = originalName.replace(/,/g, " ");
                  }
                  const uploadName = uploadedFile.uploadName || uploadedFile.newFileName;
                  
                  console.log(`📸 [IMAGE DEBUG] Processing upload response item ${index + 1}:`, {
                    originalName: originalName,
                    uploadName: uploadName,
                    fullItem: uploadedFile
                  });
                  
                  if (uploadName && index < filesToUpload.length) {
                    // Map back using array index since response order matches upload order
                    const imageVideoFileId = filesToUpload[index].fileId;
                    // Use same format as online mode: "originalName|uploadName"
                    const fileNameFormat = originalName ? `${originalName}|${uploadName}` : uploadName;
                    uploadedSignatureFiles[`imageVideoFileId_${imageVideoFileId}`] = fileNameFormat;
                    console.log(`✅ [IMAGE DEBUG] File ${index + 1} uploaded successfully:`, {
                      imageVideoFileId: imageVideoFileId,
                      originalName: originalName,
                      uploadName: uploadName,
                      fileNameFormat: fileNameFormat,
                      mappingKey: `imageVideoFileId_${imageVideoFileId}`
                    });
                    
                    if (isDocumentSignOff) {
                      console.log(`✅ [DOCUMENT SIGN-OFF] Photo/video uploaded:`, {
                        imageVideoFileId: imageVideoFileId,
                        uploadName: uploadName,
                        originalName: originalName,
                        isUserSignature: imageVideoFileId === signOff.userSignaturePhotoImageVideoFileId,
                        isDigitalInk: imageVideoFileId === signOff.digitalInkSignatureImageVideoFileId
                      });
                    }
                  } else {
                    console.warn(`⚠️ [IMAGE DEBUG] Missing uploadName or index out of bounds for response item:`, uploadedFile);
                  }
                });
                console.log(`📸 [IMAGE DEBUG] Final uploadedSignatureFiles mapping:`, uploadedSignatureFiles);
                
                if (isDocumentSignOff) {
                  console.log(`📸 [DOCUMENT SIGN-OFF] Final photo/video mapping summary:`, {
                    totalFilesUploaded: uploadResponse.Result.length,
                    uploadedSignatureFiles: uploadedSignatureFiles,
                    userSignaturePhotoFileName: uploadedSignatureFiles[`imageVideoFileId_${signOff.userSignaturePhotoImageVideoFileId}`] || 'N/A',
                    digitalInkSignatureFileName: uploadedSignatureFiles[`imageVideoFileId_${signOff.digitalInkSignatureImageVideoFileId}`] || 'N/A'
                  });
                }
              } else {
                console.warn(`⚠️ [IMAGE DEBUG] Upload response has no Result field`);
                if (isDocumentSignOff) {
                  console.warn(`⚠️ [DOCUMENT SIGN-OFF] Upload response has no Result field - photo/video files may not be uploaded`);
                }
              }
            } else {
              console.warn(`⚠️ [IMAGE DEBUG] No files prepared for upload, skipping upload step`);
              if (isDocumentSignOff) {
                console.warn(`⚠️ [DOCUMENT SIGN-OFF] No photo/video files prepared for upload`);
              }
            }
          } else {
            console.log(`📸 [IMAGE DEBUG] No files to upload (imageVideoFileIdsToUpload is empty)`);
          }

          if (formSubmittedOnline && hybridSignOffPayload) {
            formAnswerResult = {
              formAnswerHtmlString: hybridSignOffPayload.formContent || '',
              answerNotificationList: hybridSignOffPayload.answerNotificationList || [],
              groupUniqueID: hybridSignOffPayload.groupUniqueID || '',
              questionLogicActivityList: hybridSignOffPayload.createdActivityList || [],
              formScore: hybridSignOffPayload.formScore ?? 0,
              maxFormScore: hybridSignOffPayload.maxFormScore ?? 0,
              formPerScore: hybridSignOffPayload.formPerScore ?? 0,
              _FormSection: hybridSignOffPayload._FormSection || [],
            };
            console.log(
              `📤 [SIGN-OFF SYNC] Hybrid sign-off ${signOff.signOffDetailId} — form already on server, skipping saveFormAnswers`
            );
          } else if (formSubmittedOnline) {
            console.error(
              `❌ [SIGN-OFF SYNC] Skipping hybrid sign-off ${signOff.signOffDetailId} — missing signOffPayload`
            );
            try {
              await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
            } catch (clearError) {}
            continue;
          }
          
          // Step 3: Process form answer data
            if (formAnswerData) {
            console.log(`📸 [IMAGE DEBUG] Before replaceImageNameInFormData - uploadedSignatureFiles:`, uploadedSignatureFiles);
            
            // Log form data structure before replacement
            try {
              let formattedSectionsBefore = formAnswerData.formattedSections;
              if (typeof formattedSectionsBefore === 'string') {
                formattedSectionsBefore = JSON.parse(formattedSectionsBefore);
              }
              if (Array.isArray(formattedSectionsBefore)) {
                formattedSectionsBefore.forEach((section: any, sectionIndex: number) => {
                  if (section.answerData && Array.isArray(section.answerData)) {
                    section.answerData.forEach((answerData: any, answerIndex: number) => {
                      if (answerData?.answerData?.imageVideoFileId) {
                        console.log(`📸 [IMAGE DEBUG] Before replacement - formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, {
                          imageVideoFileId: answerData.answerData.imageVideoFileId,
                          hasImageVideoFileName: !!answerData.answerData.imageVideoFileName
                        });
                      }
                    });
                  }
                });
              }
            } catch (e) {
              console.warn(`📸 [IMAGE DEBUG] Error logging form data before replacement:`, e);
            }
            
              // Replace image file IDs with uploaded file names
              this.offlineManager.replaceImageNameInFormData(formAnswerData, uploadedSignatureFiles);
            
            console.log(`📸 [IMAGE DEBUG] After replaceImageNameInFormData called`);
            
            // Log form data structure after replacement
            try {
              let formattedSectionsAfter = formAnswerData.formattedSections;
              if (typeof formattedSectionsAfter === 'string') {
                formattedSectionsAfter = JSON.parse(formattedSectionsAfter);
              }
              if (Array.isArray(formattedSectionsAfter)) {
                formattedSectionsAfter.forEach((section: any, sectionIndex: number) => {
                  if (section.answerData && Array.isArray(section.answerData)) {
                    section.answerData.forEach((answerData: any, answerIndex: number) => {
                      if (answerData?.answerData) {
                        const hasImageVideoFileId = !!answerData.answerData.imageVideoFileId;
                        const hasImageVideoFileName = !!answerData.answerData.imageVideoFileName;
                        if (hasImageVideoFileId || hasImageVideoFileName) {
                          console.log(`📸 [IMAGE DEBUG] After replacement - formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, {
                            imageVideoFileId: answerData.answerData.imageVideoFileId,
                            imageVideoFileName: answerData.answerData.imageVideoFileName,
                            hasImageVideoFileId: hasImageVideoFileId,
                            hasImageVideoFileName: hasImageVideoFileName
                          });
                        }
                      }
                    });
                  }
                });
              }
            } catch (e) {
              console.warn(`📸 [IMAGE DEBUG] Error logging form data after replacement:`, e);
            }
              
              // Ensure all required fields are present and properly formatted
              // Arrays/objects stored as TEXT in SQLite need to be parsed from JSON strings
              
              // Construct entityId in format "L|{locationID}" or "P|{projectID}" or "I|{inventoryItemID}"
              let entityId = formAnswerData.entityId || formAnswerData.entityID || '';
              if (!entityId || entityId === '') {
                // Construct from sign-off location data if not present in form data
                if (signOff.locationID && signOff.locationID > 0) {
                  entityId = `L|${signOff.locationID}`;
                } else if (signOff.projectID && signOff.projectID > 0) {
                  entityId = `P|${signOff.projectID}`;
                } else if (signOff.inventoryItemID && signOff.inventoryItemID > 0) {
                  entityId = `I|${signOff.inventoryItemID}`;
                } else {
                  // Fallback: try to get from dedicated mode location use
                  const locationUse = this.sharedDataService.dedicatedModeLocationUse;
                  if (locationUse) {
                    entityId = this.utilService.getEntityIdFromId(locationUse);
                  }
                }
              }
              
              // Ensure numeric fields are numbers, not strings
              const toNumber = (val: any, defaultValue: number = 0): number => {
                if (val === null || val === undefined || val === '') return defaultValue;
                const num = typeof val === 'string' ? parseInt(val, 10) : Number(val);
                return isNaN(num) ? defaultValue : num;
              };
              
              const formattedFormData: any = {
                formId: toNumber(formAnswerData.formId || formAnswerData.formID, 0),
                formVersionId: toNumber(formAnswerData.formVersionId || formAnswerData.formVersionID, 0),
                formTypeId: toNumber(formAnswerData.formTypeId || formAnswerData.formTypeID, 0),
                userId: String(formAnswerData.userId || signOff.userId || ''),
                companyId: toNumber(formAnswerData.companyId || formAnswerData.companyID || this.sharedDataService.dedicatedModeDeviceDetailData?.companyID, 0),
                selectedLanguageID: toNumber(formAnswerData.selectedLanguageID || formAnswerData.selectedLanguageId, 0),
                entityId: String(entityId || ''),
                guest_FirstName: String(formAnswerData.guest_FirstName || ''),
                guest_MiddleName: String(formAnswerData.guest_MiddleName || ''),
                guest_LastName: String(formAnswerData.guest_LastName || ''),
                guest_Phone: String(formAnswerData.guest_Phone || ''),
              };
              
              // Parse arrays/objects from JSON strings if needed
              const parseField = (field: any, defaultValue: any = null) => {
                if (field === null || field === undefined || field === '') return defaultValue;
                if (Array.isArray(field)) return field;
                if (typeof field === 'string') {
                  try {
                    return JSON.parse(field);
                  } catch (e) {
                    return defaultValue;
                  }
                }
                return field;
              };
              
              formattedFormData.questionAnswers = parseField(formAnswerData.questionAnswers, []);
              formattedFormData.accidentReportQuestionAnswers = parseField(formAnswerData.accidentReportQuestionAnswers, []);
              formattedFormData.riskAssessmentAnswers = parseField(formAnswerData.riskAssessmentAnswers, []);
              formattedFormData.formattedSections = parseField(formAnswerData.formattedSections, []);
              formattedFormData.havAnswerDetail = parseField(formAnswerData.havAnswerDetail, null);
              formattedFormData.workPermitAnswer = parseField(formAnswerData.workPermitAnswer, null);
              
              // Parse workPermitDetails and accidentReport
              // Match online submission: always include these fields even if default/empty objects
              const workPermitDetails = parseField(formAnswerData.workPermitDetails, null);
              if (workPermitDetails && typeof workPermitDetails === 'object') {
                // Ensure formVersionID matches the actual formVersionId if it's 0
                if (workPermitDetails.formVersionID === 0 || !workPermitDetails.formVersionID) {
                  workPermitDetails.formVersionID = formattedFormData.formVersionId;
                }
                formattedFormData.workPermitDetails = workPermitDetails;
              } else {
                // Include default empty object to match online submission
                formattedFormData.workPermitDetails = {
                  workPermitId: 0,
                  totalScore: null,
                  hasExpirationDate: false,
                  hasExpiresOn: false,
                  expiresOnDate: null,
                  expiresOnTime: null,
                  hasExpiresAfter: false,
                  durationValue: null,
                  hasPermitIssuedNotification: false,
                  hasPermitNotIssuedNotification: false,
                  operatorTypeID: null,
                  durationTypeID: null,
                  permitIssuedNotificationID: null,
                  permitNotIssuedNotificationID: null,
                  formVersionID: formattedFormData.formVersionId,
                  whoDefinesDateType: null,
                  permitNotIssuedNotification: {
                    notifyUser: true,
                    notifyEntityRole: false,
                    selectedUserId: "00000000-0000-0000-0000-000000000000",
                    selectedUsers: [],
                    notifyGroup: false,
                    selectedGroupId: 0,
                    selectedEntityRoleId: 0,
                    selectedGroups: [],
                    isSystemNotification: true,
                    isEmailNotification: false,
                    isSMSNotification: false,
                    selectedUsersAndGroups: [],
                    selectedEntityRoles: []
                  },
                  permitIssuedNotification: {
                    notifyUser: true,
                    notifyEntityRole: false,
                    selectedUserId: "00000000-0000-0000-0000-000000000000",
                    selectedUsers: [],
                    notifyGroup: false,
                    selectedGroupId: 0,
                    selectedEntityRoleId: 0,
                    selectedGroups: [],
                    isSystemNotification: true,
                    isEmailNotification: false,
                    isSMSNotification: false,
                    selectedUsersAndGroups: [],
                    selectedEntityRoles: []
                  },
                  permitIssuedAvailableUsers: [],
                  permitIssuedAvailableUserGroups: [],
                  permitNotIssuedAvailableUsers: [],
                  permitNotIssuedAvailableUserGroups: []
                };
              }
              
              const accidentReport = parseField(formAnswerData.accidentReport, null);
              if (accidentReport && typeof accidentReport === 'object') {
                // Ensure formVersionID matches the actual formVersionId if it's 0
                if (accidentReport.formVersionID === 0 || !accidentReport.formVersionID) {
                  accidentReport.formVersionID = formattedFormData.formVersionId;
                }
                formattedFormData.accidentReport = accidentReport;
              } else {
                // Include default empty object to match online submission
                formattedFormData.accidentReport = {
                  accidentReportId: 0,
                  hasAccidentNotification: false,
                  notificationID: null,
                  formVersionID: formattedFormData.formVersionId,
                  accidentNotification: {
                    notifyUser: true,
                    notifyEntityRole: false,
                    selectedUserId: "00000000-0000-0000-0000-000000000000",
                    selectedUsers: [],
                    notifyGroup: false,
                    selectedGroupId: 0,
                    selectedEntityRoleId: 0,
                    selectedGroups: [],
                    isSystemNotification: true,
                    isEmailNotification: false,
                    isSMSNotification: false,
                    selectedUsersAndGroups: [],
                    selectedEntityRoles: []
                  },
                  availableUsers: [],
                  availableUserGroups: []
                };
              }
              
              formattedFormData.riskAssessmentAnswerDetails = parseField(formAnswerData.riskAssessmentAnswerDetails, null);
              formattedFormData.ioshRiskAssessmentAnswerDetails = parseField(formAnswerData.ioshRiskAssessmentAnswerDetails, null);
              
              // Match online submission: include fields even if null/empty (don't delete them)
              // Online submission includes these fields even when null or empty objects
              if (!formattedFormData.havAnswerDetail) {
                formattedFormData.havAnswerDetail = null;
              }
              if (!formattedFormData.workPermitAnswer) {
                formattedFormData.workPermitAnswer = null;
              }
              // Ensure riskAssessmentAnswerDetails is an object with taskAnswers array (even if empty)
              if (!formattedFormData.riskAssessmentAnswerDetails || 
                  (typeof formattedFormData.riskAssessmentAnswerDetails === 'object' && 
                   (!formattedFormData.riskAssessmentAnswerDetails.taskAnswers))) {
                formattedFormData.riskAssessmentAnswerDetails = { taskAnswers: [] };
              }
              // Ensure ioshRiskAssessmentAnswerDetails is an object with hazardAnswers array (even if empty)
              if (!formattedFormData.ioshRiskAssessmentAnswerDetails) {
                formattedFormData.ioshRiskAssessmentAnswerDetails = { hazardAnswers: [] };
              }
              // Populate IOSH hazardAnswers from formattedSections (offline capture) to match online mapper output
              try {
                const collectedHazards: any[] = [];
                if (Array.isArray(formattedFormData.formattedSections)) {
                  formattedFormData.formattedSections.forEach((section: any) => {
                    const isIOSHSection =
                      !!section?.isIOSHRiskAssessmentSection ||
                      (section?.sectionTranslations || []).some((t: any) =>
                        String(t?.sectionTranslationTitle || '').toLowerCase().includes('iosh')
                      );
                    if (!isIOSHSection) {
                      return;
                    }
                    // Prefer explicit IOSH container on the section if present
                    const secIosh = section?.ioshRiskAssessmentAnswerDetails;
                    if (secIosh && Array.isArray(secIosh.hazardAnswers)) {
                      secIosh.hazardAnswers.forEach((hz: any) => collectedHazards.push(hz));
                    }
                    // Fallback: section.answerData may contain IOSH hazard-shaped entries when offline
                    if (Array.isArray(section?.answerData)) {
                      section.answerData.forEach((ad: any) => {
                        const looksLikeHazard =
                          ad &&
                          (ad.IOSHHazardCategoryId !== undefined ||
                            ad.RiskRatingSeverityID !== undefined ||
                            ad.IOSHHazardAnswerTitle !== undefined);
                        if (looksLikeHazard) {
                          collectedHazards.push(ad);
                        }
                        // Some payloads nest an "answerData" again
                        const nested = ad?.answerData;
                        const nestedLooksLikeHazard =
                          nested &&
                          (nested.IOSHHazardCategoryId !== undefined ||
                            nested.RiskRatingSeverityID !== undefined ||
                            nested.IOSHHazardAnswerTitle !== undefined);
                        if (nestedLooksLikeHazard) {
                          collectedHazards.push(nested);
                        }
                      });
                    }
                  });
                }
                if (collectedHazards.length > 0) {
                  // De-duplicate hazards by a stable key derived from key fields
                  const dedupMap = new Map<string, any>();
                  collectedHazards.forEach((hz: any) => {
                    const keyParts = [
                      String(hz?.IOSHHazardAnswerId ?? 0),
                      String(hz?.IOSHHazardAnswerTitle ?? ''),
                      String(hz?.IOSHHazardCategoryId ?? hz?.IOSHHazardCategoryID ?? ''),
                      String(hz?.RiskRatingSeverityID ?? ''),
                      String(hz?.RiskRatingProbabilityID ?? ''),
                      String(hz?.ResidualRiskRatingSeverityID ?? ''),
                      String(hz?.ResidualRiskRatingProbabilityID ?? ''),
                    ];
                    const key = keyParts.join('|');
                    if (!dedupMap.has(key)) {
                      dedupMap.set(key, hz);
                    }
                  });
                  formattedFormData.ioshRiskAssessmentAnswerDetails.hazardAnswers = Array.from(dedupMap.values());
                }
              } catch (e) {
                console.warn('[OFFLINE IOSH] Failed to map IOSH hazard answers from formattedSections:', e);
              }
              
              // Process formattedSections to ensure score fields are properly formatted (matching online submission)
              // The API expects score fields to be numbers (0 when scoring is disabled), not null or strings
              if (formattedFormData.formattedSections && Array.isArray(formattedFormData.formattedSections)) {
                formattedFormData.formattedSections.forEach((section: any) => {
                  // Ensure score fields are numbers, not null or strings
                  if (section.formScore === null || section.formScore === undefined) {
                    section.formScore = 0;
                  } else if (typeof section.formScore === 'string') {
                    section.formScore = parseInt(section.formScore, 10) || 0;
                  }
                  
                  if (section.maxFormScore === null || section.maxFormScore === undefined) {
                    section.maxFormScore = 0;
                  } else if (typeof section.maxFormScore === 'string') {
                    section.maxFormScore = parseInt(section.maxFormScore, 10) || 0;
                  }
                  
                  if (section.formPerScore === null || section.formPerScore === undefined) {
                    section.formPerScore = 0;
                  } else if (typeof section.formPerScore === 'string') {
                    section.formPerScore = parseInt(section.formPerScore, 10) || 0;
                  }
                  
                  if (section.perSectionScore === null || section.perSectionScore === undefined) {
                    section.perSectionScore = 0;
                  } else if (typeof section.perSectionScore === 'string') {
                    section.perSectionScore = parseInt(section.perSectionScore, 10) || 0;
                  }
                  
                  if (section.maximumSectionScore === null || section.maximumSectionScore === undefined) {
                    section.maximumSectionScore = 0;
                  } else if (typeof section.maximumSectionScore === 'string') {
                    section.maximumSectionScore = parseInt(section.maximumSectionScore, 10) || 0;
                  }
                  
                  // achievedSectionScore should be a number, not a string
                  if (section.achievedSectionScore === null || section.achievedSectionScore === undefined) {
                    section.achievedSectionScore = 0;
                  } else if (typeof section.achievedSectionScore === 'string') {
                    section.achievedSectionScore = parseInt(section.achievedSectionScore, 10) || 0;
                  }
                  
                  // Handle NaN cases
                  if (isNaN(section.formScore)) section.formScore = 0;
                  if (isNaN(section.maxFormScore)) section.maxFormScore = 0;
                  if (isNaN(section.formPerScore)) section.formPerScore = 0;
                  if (isNaN(section.perSectionScore)) section.perSectionScore = 0;
                  if (isNaN(section.maximumSectionScore)) section.maximumSectionScore = 0;
                  if (isNaN(section.achievedSectionScore)) section.achievedSectionScore = 0;
                });
              }
              
              // Validate required fields before sending
              if (!formattedFormData.formId || formattedFormData.formId === 0) {
                console.error(`❌ [SIGN-OFF SYNC] Invalid formId: ${formattedFormData.formId}`);
                continue;
              }
              if (!formattedFormData.formVersionId || formattedFormData.formVersionId === 0) {
                console.error(`❌ [SIGN-OFF SYNC] Invalid formVersionId: ${formattedFormData.formVersionId}`);
                continue;
              }
              if (!formattedFormData.userId || formattedFormData.userId === '') {
                console.error(`❌ [SIGN-OFF SYNC] Invalid userId: ${formattedFormData.userId}`);
                continue;
              }
              if (!formattedFormData.entityId || formattedFormData.entityId === '') {
                console.error(`❌ [SIGN-OFF SYNC] Invalid entityId: ${formattedFormData.entityId}`);
                continue;
              }
              
            // Step 3b: Upload in-form signature images via InductionSignatureUpload
            if (formattedFormData.questionAnswers && Array.isArray(formattedFormData.questionAnswers)) {
              for (const qa of formattedFormData.questionAnswers) {
                if (qa.answerTypeID === 17 && qa.signatureAnswer?.signatureAnswers) {
                  for (const sig of qa.signatureAnswer.signatureAnswers) {
                    if (sig.sigImage && sig.signatureImage) {
                      try {
                        const base64Data = sig.sigImage.includes(',') ? sig.sigImage.split(',')[1] : sig.sigImage;
                        const byteChars = atob(base64Data);
                        const byteArr = new Uint8Array(byteChars.length);
                        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                        const blob = new Blob([byteArr], { type: 'image/jpeg' });
                        await this.apiService.inductionSignatureUpload(blob, sig.signatureImage).toPromise();
                        console.log(`✅ [IN-FORM SIG] Uploaded in-form signature: ${sig.signatureImage}`);
                      } catch (e) {
                        console.error(`❌ [IN-FORM SIG] Failed to upload in-form signature ${sig.signatureImage}:`, e);
                      }
                    }
                    delete sig.sigImage;
                  }
                }
              }
            }
            // Strip sigImage from formattedSections too
            if (formattedFormData.formattedSections && Array.isArray(formattedFormData.formattedSections)) {
              for (const section of formattedFormData.formattedSections) {
                for (const answerData of (section.answerData || [])) {
                  for (const sig of (answerData?.answerData?.signatureAnswer?.signatureAnswers || [])) {
                    delete sig.sigImage;
                  }
                }
              }
            }

            // Step 4: Submit form answers using online API (same as online submission)
            // Mark as submitting before calling saveFormAnswers to prevent duplicate form submissions
            await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 1 });
            console.log(`🔄 [SIGN-OFF SYNC] Marked sign-off ${signOff.signOffDetailId} as submitting before saveFormAnswers`);

              console.log(`📤 [SIGN-OFF SYNC] Calling saveFormAnswers API for sign-off ${signOff.signOffDetailId}...`);
              console.log(`📤 [SIGN-OFF SYNC] Form answer data structure:`, {
                formId: formattedFormData.formId,
                formVersionId: formattedFormData.formVersionId,
                formTypeId: formattedFormData.formTypeId,
                userId: formattedFormData.userId,
                companyId: formattedFormData.companyId,
                entityId: formattedFormData.entityId,
                hasQuestionAnswers: Array.isArray(formattedFormData.questionAnswers),
                questionAnswersCount: formattedFormData.questionAnswers?.length || 0,
                hasFormattedSections: Array.isArray(formattedFormData.formattedSections),
                formattedSectionsCount: formattedFormData.formattedSections?.length || 0,
              });
            
            // Log image file names in formattedSections before sending
            if (formattedFormData.formattedSections && Array.isArray(formattedFormData.formattedSections)) {
              console.log(`📸 [IMAGE DEBUG] Checking formattedSections in final payload before SaveFormAnswers...`);
              formattedFormData.formattedSections.forEach((section: any, sectionIndex: number) => {
                if (section.answerData && Array.isArray(section.answerData)) {
                  section.answerData.forEach((answerData: any, answerIndex: number) => {
                    if (answerData?.answerData) {
                      const hasImageVideoFileId = !!answerData.answerData.imageVideoFileId;
                      const hasImageVideoFileName = !!answerData.answerData.imageVideoFileName;
                      if (hasImageVideoFileId || hasImageVideoFileName) {
                        console.log(`📸 [IMAGE DEBUG] Final payload - formattedSections[${sectionIndex}].answerData[${answerIndex}]:`, {
                          imageVideoFileId: answerData.answerData.imageVideoFileId,
                          imageVideoFileName: answerData.answerData.imageVideoFileName,
                          hasImageVideoFileId: hasImageVideoFileId,
                          hasImageVideoFileName: hasImageVideoFileName
                        });
                      }
                    }
                  });
                }
              });
            }
            
              // Log payload summary instead of full JSON to avoid hanging on large payloads
              try {
                const payloadSize = JSON.stringify(formattedFormData).length;
                console.log(`📤 [SIGN-OFF SYNC] Form data payload size: ${payloadSize} bytes`);
                if (payloadSize > 1800000) {
                  console.log(`📤 [SIGN-OFF SYNC] Large payload detected, skipping full JSON log to prevent hanging`);
                } else {
                  console.log(`📤 [SIGN-OFF SYNC] Full form data payload:`, JSON.stringify(formattedFormData, null, 2));
                }
              } catch (jsonError) {
                console.warn(`⚠️ [SIGN-OFF SYNC] Could not stringify payload (may contain circular references):`, jsonError);
                console.log(`📤 [SIGN-OFF SYNC] Form data structure:`, {
                  hasFormattedSections: !!formattedFormData.formattedSections,
                  formattedSectionsCount: Array.isArray(formattedFormData.formattedSections) ? formattedFormData.formattedSections.length : 0,
                  hasQuestionAnswers: !!formattedFormData.questionAnswers,
                  questionAnswersCount: Array.isArray(formattedFormData.questionAnswers) ? formattedFormData.questionAnswers.length : 0
                });
              }
              
              try {
                // Add timeout to prevent indefinite hanging (180 seconds timeout)
                const timeoutPromise = new Promise((_, reject) => {
                  setTimeout(() => reject(new Error('Request timeout: saveFormAnswers API call exceeded 180 seconds')), 180000);
                });

                console.log(`📤 [SIGN-OFF SYNC] Starting saveFormAnswers API call with 180s timeout...`);
                const saveFormResponse: any = await Promise.race([
                  this.apiService.saveFormAnswers(formattedFormData).toPromise(),
                  timeoutPromise
                ]) as any;
                
                console.log(`📤 [SIGN-OFF SYNC] saveFormAnswers API response:`, {
                  StatusCode: saveFormResponse?.StatusCode,
                  hasResult: !!saveFormResponse?.Result,
                  Message: saveFormResponse?.Message,
                  ResponseException: saveFormResponse?.ResponseException
                });
                
                if (saveFormResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
                  formAnswerResult = saveFormResponse.Result;
                  console.log(`✅ [SIGN-OFF SYNC] Form answers submitted successfully, result:`, formAnswerResult);
                } else {
                  console.error(`❌ [SIGN-OFF SYNC] Failed to submit form answers. StatusCode: ${saveFormResponse?.StatusCode}, Message: ${saveFormResponse?.Message}`);
                  if (saveFormResponse?.ResponseException) {
                    console.error(`❌ [SIGN-OFF SYNC] Exception details:`, JSON.stringify(saveFormResponse.ResponseException, null, 2));
                  }
                  console.error(`❌ [SIGN-OFF SYNC] Request payload that failed:`, JSON.stringify(formattedFormData, null, 2));
                // Clear submitting flag on failure
                try {
                  await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
                } catch (clearError) {
                  console.warn(`⚠️ [SIGN-OFF SYNC] Could not clear submitting flag:`, clearError);
                }
                  continue; // Skip this sign-off if form answers submission failed
                }
              } catch (saveFormError) {
                console.error(`❌ [SIGN-OFF SYNC] Error calling saveFormAnswers API:`, saveFormError);
                if (saveFormError?.message && saveFormError.message.includes('timeout')) {
                  console.error(`❌ [SIGN-OFF SYNC] API call timed out after 180 seconds - this may indicate a server-side issue`);
                  console.error(`❌ [SIGN-OFF SYNC] Payload size: ${JSON.stringify(formattedFormData).length} bytes`);
                }
              // Clear submitting flag on error
              try {
                await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
              } catch (clearError) {
                console.warn(`⚠️ [SIGN-OFF SYNC] Could not clear submitting flag:`, clearError);
                }
                continue; // Skip this sign-off if API call fails
              }
            } else {
              console.warn(`⚠️ [SIGN-OFF SYNC] Form answer data not found for formSubmitDataId: ${signOff.formSubmitDataId}`);
              // Continue anyway - might be a document sign-off without form data
          }

          // Guard: if form answers were expected but failed to upload, skip sign-off to avoid corrupt server data
          // Network-cut payloads have full payload stored — they don't need formAnswerResult
          if (hasLocalFormSubmitData && !formAnswerResult && !formSubmittedOnline && !networkCutPayload) {
            console.error(`❌ [SIGN-OFF SYNC] Skipping sign-off ${signOff.signOffDetailId} — saveFormAnswers did not succeed (formAnswerResult is null)`);
            try {
              await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
            } catch (clearError) {}
            continue;
          }

          // Step 4: Submit sign-off details using online API (same as online submission)
          // Format signOffDate: convert ISO format to DD.MM.YYYY HH:mm:ss if needed
          let signOffDate = signOff.signOffDate || moment().utc().format('DD.MM.YYYY HH:mm:ss');
          // If date is in ISO format (e.g., "2026-02-13T14:31:31"), convert it
          if (signOffDate.includes('T')) {
            try {
              const dateObj = moment(signOffDate);
              if (dateObj.isValid()) {
                signOffDate = dateObj.format('DD.MM.YYYY HH:mm:ss');
              }
            } catch (e) {
              // If conversion fails, use current time
              signOffDate = moment().utc().format('DD.MM.YYYY HH:mm:ss');
            }
          }

          console.log(`📤 [SIGN-OFF SYNC] Sign-off type: ${isDocumentSignOff ? 'DOCUMENT' : 'FORM'} (formVersionID: ${signOff.formVersionID}, documentID: ${signOff.documentID}, formSubmitDataId: ${signOff.formSubmitDataId})`);

          // Log photo/video info for document signoffs
          if (isDocumentSignOff) {
            console.log(`📸 [DOCUMENT SIGN-OFF] Photo/Video file IDs:`, {
              userSignaturePhotoImageVideoFileId: signOff.userSignaturePhotoImageVideoFileId || 'N/A',
              digitalInkSignatureImageVideoFileId: signOff.digitalInkSignatureImageVideoFileId || 'N/A',
              documentID: signOff.documentID,
              documentVersionID: signOff.documentVersionID
            });
          }

          let signOffPostData: any = {};

          if (networkCutPayload) {
            // Network-cut: the full signOffDetailsPostData was snapshotted before the API call.
            // Signatures were already uploaded (online submit path), so use stored file names directly.
            signOffPostData = { ...networkCutPayload };
            delete (signOffPostData as any)._isNetworkCutFallback;
            delete (signOffPostData as any).formFilledOffline;
            console.log(`📤 [SIGN-OFF SYNC] Using network-cut full payload for sign-off ${signOff.signOffDetailId}`);
          } else if (isDocumentSignOff) {
            console.log(`📤 [SIGN-OFF SYNC] Building document sign-off payload (minimal fields only)`);
            // Document sign-off: minimal payload matching online submission
            signOffPostData = {
              documentID: signOff.documentID || 0,
              documentVersionID: signOff.documentVersionID || 0,
              formVersionID: 0,
              latitude: signOff.latitude || '',
              longitude: signOff.longitude || '',
              locationID: signOff.locationID || 0,
              projectID: signOff.projectID || 0,
              inventoryItemID: signOff.inventoryItemID || 0,
              userId: signOff.userId || '',
              signOffDate: signOffDate,
              formPerScore: 0,
              digitalInkSignature: uploadedSignatureFiles[`imageVideoFileId_${signOff.digitalInkSignatureImageVideoFileId}`] || '',
              userSignaturePhoto: uploadedSignatureFiles[`imageVideoFileId_${signOff.userSignaturePhotoImageVideoFileId}`] || '',
            };
          } else {
            // Form sign-off: include form-related fields
            // Extract score data from formAnswerResult if available
            let formScore = 0;
            let maxFormScore = 0;
            let formPerScore = 0;
            const _FormSection: any[] = [];
            
            // Try to get score data from formAnswerResult first, then from formAnswerData
            let sections = null;
            if (formAnswerResult?.formattedSections) {
              sections = formAnswerResult.formattedSections;
            } else if (formAnswerData?.formattedSections) {
              // Parse formattedSections from formAnswerData if it's a string
              try {
                sections = typeof formAnswerData.formattedSections === 'string' 
                  ? JSON.parse(formAnswerData.formattedSections) 
                  : formAnswerData.formattedSections;
              } catch (e) {
                sections = null;
              }
            }
            
            if (sections && Array.isArray(sections) && sections.length > 0) {
              // Get score from first section (sections typically have the same form-level scores)
              const firstSection = sections[0];
              formScore = firstSection.formScore || 0;
              maxFormScore = firstSection.maxFormScore || 0;
              formPerScore = firstSection.formPerScore || 0;
              
              // Build _FormSection array from sections
              sections.forEach((section: any) => {
                if (section.sectionId) {
                  _FormSection.push({
                    FormSection_: [{
                      FormVersionID: section.formVersionID || signOff.formVersionID || 0,
                      sectionID: section.sectionId,
                      isSectionScoreEnabled: section.isSectionScoreEnabled || false,
                      MaximumSectionScore: section.maximumSectionScore || 0,
                      AchievedSectionScore: section.achievedSectionScore || 0,
                      PerSectionScore: section.perSectionScore || 0,
                      ShowSectionInPDF: section.isShowSScoreInPDF || false
                    }]
                  });
                }
              });
            }

            if (formAnswerResult?.formScore != null) {
              formScore = formAnswerResult.formScore;
            }
            if (formAnswerResult?.maxFormScore != null) {
              maxFormScore = formAnswerResult.maxFormScore;
            }
            if (formAnswerResult?.formPerScore != null) {
              formPerScore = formAnswerResult.formPerScore;
            }
            if (
              formSubmittedOnline &&
              Array.isArray(formAnswerResult?._FormSection) &&
              formAnswerResult._FormSection.length > 0
            ) {
              _FormSection.length = 0;
              _FormSection.push(...formAnswerResult._FormSection);
            }
            
            signOffPostData = {
              userId: signOff.userId || '',
              formVersionID: signOff.formVersionID || 0,
              locationID: signOff.locationID || 0,
              projectID: signOff.projectID || 0,
              inventoryItemID: signOff.inventoryItemID || 0,
              latitude: signOff.latitude || '',
              longitude: signOff.longitude || '',
              signOffDate: signOffDate,
              digitalInkSignature: uploadedSignatureFiles[`imageVideoFileId_${signOff.digitalInkSignatureImageVideoFileId}`] || '',
              formContent: formAnswerResult?.formAnswerHtmlString || '',
              answerNotificationList: formAnswerResult?.answerNotificationList || [],
              groupUniqueID: formAnswerResult?.groupUniqueID || '',
              createdActivityList: formAnswerResult?.questionLogicActivityList || [],
              formScore: formScore,
              maxFormScore: maxFormScore,
              _FormSection: _FormSection,
              formPerScore: formPerScore,
            };

            // Add formSubmitDataId if available from form answer result
            if (formAnswerResult) {
              if (typeof formAnswerResult === 'number') {
                signOffPostData.formSubmitDataId = formAnswerResult;
              } else if (formAnswerResult.formSubmitDataId) {
                signOffPostData.formSubmitDataId = formAnswerResult.formSubmitDataId;
              }
            }

            if (formAnswerResult?.formScore != null) {
              formScore = formAnswerResult.formScore;
            }
            if (formAnswerResult?.maxFormScore != null) {
              maxFormScore = formAnswerResult.maxFormScore;
            }
            if (formAnswerResult?.formPerScore != null) {
              formPerScore = formAnswerResult.formPerScore;
            }
            if (
              formSubmittedOnline &&
              Array.isArray(formAnswerResult?._FormSection) &&
              formAnswerResult._FormSection.length > 0
            ) {
              _FormSection.length = 0;
              _FormSection.push(...formAnswerResult._FormSection);
            }

              // For FORM sign-off, omit document-specific fields and force _FormSection to empty to match online payload
              if (!isDocumentSignOff) {
                if (!formSubmittedOnline) {
                  signOffPostData._FormSection = [];
                }
                signOffPostData.userSignaturePhoto = uploadedSignatureFiles[`imageVideoFileId_${signOff.userSignaturePhotoImageVideoFileId}`] || '';
                delete (signOffPostData as any).documentID;
                delete (signOffPostData as any).documentVersionID;
                delete (signOffPostData as any).activityIndividualID;
                delete (signOffPostData as any).inFormSignatureSignoffList;
              } else {
                // For DOCUMENT sign-off, ensure required document fields are present
                (signOffPostData as any).documentID = signOff.documentID || 0;
                (signOffPostData as any).documentVersionID = signOff.documentVersionID || 0;
                (signOffPostData as any).activityIndividualID = signOff.activityIndividualID || 0;
                (signOffPostData as any).inFormSignatureSignoffList = [];
                (signOffPostData as any).userSignaturePhoto =
                  uploadedSignatureFiles[`imageVideoFileId_${signOff.userSignaturePhotoImageVideoFileId}`] || '';
              }
          }

          // For document signoffs (no form submission), mark as submitting before signoff API call
          if (isDocumentSignOff || formSubmittedOnline || !!networkCutPayload) {
            await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 1 });
            if (isDocumentSignOff) {
            console.log(`🔄 [SIGN-OFF SYNC] Marked document sign-off ${signOff.signOffDetailId} as submitting before insertPersonalModeSignOffDetails`);
            } else if (networkCutPayload) {
            console.log(`🔄 [SIGN-OFF SYNC] Marked network-cut sign-off ${signOff.signOffDetailId} as submitting before insertPersonalModeSignOffDetails`);
            } else {
            console.log(`🔄 [SIGN-OFF SYNC] Marked hybrid form sign-off ${signOff.signOffDetailId} as submitting before insertPersonalModeSignOffDetails`);
            }
          }

          console.log(`📤 [SIGN-OFF SYNC] Calling insertPersonalModeSignOffDetails API for sign-off ${signOff.signOffDetailId}...`);
          console.log(`📤 [SIGN-OFF SYNC] Sign-off post data:`, JSON.stringify(signOffPostData, null, 2));

          const idempotencyKey = signOff.idempotencyKey
            ? `InsertPersonalModeSignOffDetails_${signOffPostData.userId}_${signOff.idempotencyKey}`
            : undefined;

          try {
            const signOffTimeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Request timeout: insertPersonalModeSignOffDetails API call exceeded 180 seconds')), 180000);
            });

            const signOffResponse: any = await Promise.race([
              this.apiService.insertPersonalModeSignOffDetails(
                signOffPostData,
                idempotencyKey ? { 'x-idempotency-key': idempotencyKey } : undefined
              ).toPromise(),
              signOffTimeoutPromise
            ]) as any;

            console.log(`📤 [SIGN-OFF SYNC] insertPersonalModeSignOffDetails API response:`, {
              StatusCode: signOffResponse?.StatusCode,
              hasResult: !!signOffResponse?.Result,
              Message: signOffResponse?.Message
            });

            if (signOffResponse?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful) {
              console.log(`✅ [SIGN-OFF SYNC] Sign-off ${signOff.signOffDetailId} submitted successfully`);
              try {
                await this.offlineManager.emptyTable('SignOffDetails', `signOffDetailId = ${signOff.signOffDetailId}`);
                console.log(`✅ [SIGN-OFF SYNC] Deleted sign-off ${signOff.signOffDetailId} from SignOffDetails table`);
              } catch (deleteError) {
                console.error(`❌ [SIGN-OFF SYNC] Error deleting sign-off ${signOff.signOffDetailId} from local database:`, deleteError);
              }
              try {
                await this.offlineManager.emptyTable('DeviceArchivedDocuments', `signOffDetailId = ${signOff.signOffDetailId}`);
                await this.offlineManager.emptyTable('DeviceArchivedForms', `signOffDetailId = ${signOff.signOffDetailId}`);
                console.log(`✅ [SIGN-OFF SYNC] Deleted local archive rows for sign-off ${signOff.signOffDetailId}`);
              } catch (archiveError) {
                console.error(`❌ [SIGN-OFF SYNC] Error deleting local archive rows for sign-off ${signOff.signOffDetailId}:`, archiveError);
              }
            } else {
              console.error(`❌ [SIGN-OFF SYNC] Failed to submit sign-off ${signOff.signOffDetailId}. StatusCode: ${signOffResponse?.StatusCode}, Message: ${signOffResponse?.Message}`);
              try {
                await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
              } catch (clearError) {
                console.warn(`⚠️ [SIGN-OFF SYNC] Could not clear submitting flag:`, clearError);
              }
            }
          } catch (signOffApiError) {
            console.error(`❌ [SIGN-OFF SYNC] Error calling insertPersonalModeSignOffDetails API:`, signOffApiError);
            try {
              await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
            } catch (clearError) {
              console.warn(`⚠️ [SIGN-OFF SYNC] Could not clear submitting flag:`, clearError);
            }
          }
        } catch (signOffError) {
          console.error(`❌ [SIGN-OFF SYNC] Error processing sign-off ${signOff.signOffDetailId}:`, signOffError);
          // Clear submitting flag on error
          try {
            await this.offlineManager.updateSignOffDetail(signOff.signOffDetailId, { isSubmitting: 0 });
          } catch (clearError) {
            console.warn(`⚠️ [SIGN-OFF SYNC] Could not clear submitting flag:`, clearError);
          }
          // Continue with next sign-off even if this one fails
        }
        
        // Update progress even if signoff failed
        processedCount++;
        const progress = Math.round((processedCount / pendingSignOffs.length) * 100);
        this.updateSyncStatus({
          isSyncing: true,
          progress: progress,
          status: 'signoff_sync_started',
        });
      }

      console.log(`✅ [SIGN-OFF SYNC] Completed syncing pending sign-offs`);
      
      // Clear sync status when signoff sync completes
      this.updateSyncStatus({
        isSyncing: false,
        progress: 100,
        status: 'idle',
      });
    } catch (error) {
      console.error('❌ [SIGN-OFF SYNC] Error syncing pending sign-offs:', error);
      // Clear sync status on error
      this.updateSyncStatus({
        isSyncing: false,
        progress: 0,
        status: 'idle',
      });
      // Don't throw - allow sync to complete even if sign-off sync fails
    }
  }


  /**
   * Update sync status
   */
  private updateSyncStatus(status: Partial<SyncStatus>): void {
    const current = this.syncStatus$.value;
    this.syncStatus$.next({ ...current, ...status });
  }

  /**
   * Show success toast message
   */
  private async showSuccessMessage(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: 2000,
      position: 'bottom',
      color: 'success',
      cssClass: 'sync-toast'
    });
    await toast.present();
  }

  /**
   * Show error toast message
   */
  private async showErrorMessage(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: 2000,
      position: 'bottom',
      color: 'danger',
      cssClass: 'sync-toast'
    });
    await toast.present();
  }

  /**
   * Start periodic check for pending changes (every 3 minutes)
   * Only runs if database is not empty and device is online
   */
  private startPeriodicPendingChangesCheck(): void {
    // Clear existing interval if any
    this.stopPeriodicPendingChangesCheck();

    // Check if database is empty and device is online
    Promise.all([
      this.isDatabaseEmpty(),
      Network.getStatus()
    ]).then(([isEmpty, networkStatus]) => {
      if (!isEmpty && networkStatus.connected) {
        console.log('🔄 [PERIODIC SYNC] Starting periodic pending changes check (every 3 minutes)');

        // Check immediately, then every 3 minutes
        this.checkPendingChanges();
        
        this.pendingChangesInterval = setInterval(async () => {
          // Check network status before each check
          const currentNetworkStatus = await Network.getStatus();
          if (currentNetworkStatus.connected) {
            this.checkPendingChanges();
          } else {
            console.log('🔄 [PERIODIC SYNC] Device is offline, skipping periodic check');
          }
        }, 180000); // 180 seconds = 3 minutes
      } else {
        if (isEmpty) {
          console.log('🔄 [PERIODIC SYNC] Database is empty, skipping periodic check');
        }
        if (!networkStatus.connected) {
          console.log('🔄 [PERIODIC SYNC] Device is offline, skipping periodic check');
        }
      }
    }).catch((error) => {
      console.error('Error checking database/network for periodic sync:', error);
    });
  }

  /**
   * Stop periodic pending changes check
   */
  private stopPeriodicPendingChangesCheck(): void {
    if (this.pendingChangesInterval) {
      console.log('🛑 [PERIODIC SYNC] Stopping periodic pending changes check');
      clearInterval(this.pendingChangesInterval);
      this.pendingChangesInterval = null;
    }
  }

  /**
   * Initialize SignalR connection to /hubs/sync
   * Connects when online and listens for real-time changes
   */
  private async initializeSignalR(): Promise<void> {
    try {
      // Check if apiBaseUrl is available (might not be set yet in dedicated mode)
      const apiBaseUrl = this.sharedDataService.apiBaseUrl;
      if (!apiBaseUrl) {
        console.log('📡 [SIGNALR] apiBaseUrl not available yet, will retry in 2 seconds...');
        // Retry after a delay to allow apiBaseUrl to be set
        setTimeout(() => {
          this.initializeSignalR();
        }, 2000);
        return;
      }

      // Don't initialize if already connected
      if (this.signalRConnection && this.signalRConnection.state === signalR.HubConnectionState.Connected) {
        console.log('📡 [SIGNALR] Already connected, skipping initialization');
        return;
      }

      // Check network status
      const networkStatus = await Network.getStatus();
      if (!networkStatus.connected) {
        console.log('📡 [SIGNALR] Device is offline, SignalR connection will be established when online');
        // Only add listener if not already added (avoid multiple listeners)
        if (!this.signalRConnection) {
          Network.addListener('networkStatusChange', async (status) => {
            if (status.connected && !this.signalRConnection) {
              await this.connectSignalR();
            } else if (!status.connected && this.signalRConnection) {
              await this.disconnectSignalR();
            }
          });
        }
        return;
      }

      await this.connectSignalR();
    } catch (error) {
      console.error('❌ [SIGNALR] Error initializing SignalR:', error);
    }
  }

  /**
   * Manually trigger SignalR initialization (useful when apiBaseUrl becomes available later)
   * Can be called from configureAppForDedicatedMode or similar
   */
  public ensureSignalRConnection(): void {
    if (!this.signalRConnection) {
      console.log('📡 [SIGNALR] ensureSignalRConnection called, initializing...');
      this.signalRInitRetryCount = 0; // Reset retry count
      this.initializeSignalR();
    } else {
      // Check the actual connection state
      const state = this.signalRConnection.state;
      const stateNames = {
        0: 'Disconnected',
        1: 'Connecting',
        2: 'Connected',
        3: 'Disconnecting',
        4: 'Reconnecting'
      };
      const stateName = stateNames[state] || 'Unknown';
      
      console.log('📡 [SIGNALR] SignalR connection object exists');
      console.log('📡 [SIGNALR] Connection State:', stateName, `(${state})`);
      console.log('📡 [SIGNALR] Connection ID:', this.signalRConnection.connectionId || 'N/A');
      
      // If not connected, try to reconnect
      // HubConnectionState.Connected = 2
      if (state !== signalR.HubConnectionState.Connected) {
        console.log('📡 [SIGNALR] Connection is not active, attempting to reconnect...');
        this.signalRConnection = null; // Clear existing connection
        this.signalRInitRetryCount = 0; // Reset retry count
        this.initializeSignalR();
      } else {
        console.log('📡 [SIGNALR] ✅ Connection is active and ready!');
      }
    }
  }

  /**
   * Connect to SignalR hub
   */
  private async connectSignalR(): Promise<void> {
    try {
      // Don't connect if already connected
      if (this.signalRConnection && this.signalRConnection.state === signalR.HubConnectionState.Connected) {
        console.log('📡 [SIGNALR] Already connected, skipping connection attempt');
        return;
      }

      // Build hub URL from siteBaseUrl
      // Correct path: https://cg-dev-test-mm2.azurewebsites.net/hubs/sync
      const siteBaseUrl = this.sharedDataService.siteBaseUrl;
      if (!siteBaseUrl) {
        console.warn('⚠️ [SIGNALR] siteBaseUrl not available, skipping SignalR connection');
        return;
      }

      // Ensure siteBaseUrl doesn't have trailing slash, then append /hubs/sync
      const baseUrl = siteBaseUrl.endsWith('/') ? siteBaseUrl.slice(0, -1) : siteBaseUrl;
      const hubUrl = `${baseUrl}/hubs/sync`;
      
      console.log('📡 [SIGNALR] ========================================');
      console.log('📡 [SIGNALR] Attempting to connect to SignalR hub');
      console.log('📡 [SIGNALR] Hub URL:', hubUrl);
      console.log('📡 [SIGNALR] Site Base URL:', siteBaseUrl);
      console.log('📡 [SIGNALR] API Base URL:', this.sharedDataService.apiBaseUrl);
      console.log('📡 [SIGNALR] Dedicated Mode:', this.sharedDataService.dedicatedMode ? 'Yes' : 'No');
      console.log('📡 [SIGNALR] ========================================');

      // Configure SignalR connection
      // Note: withCredentials is set to false to avoid CORS issues with wildcard Access-Control-Allow-Origin
      // If your backend requires credentials, configure it to allow specific origins instead of wildcard
      // Get authentication headers if available (similar to other API calls)
      const accessID = this.sharedDataService.deviceUID;
      const token = localStorage.getItem(EnumService.LocalStorageKeys.API_TOKEN);
      
      const httpOptions: signalR.IHttpConnectionOptions = {
        withCredentials: false, // Disable credentials to work with wildcard CORS
        skipNegotiation: false, // Use negotiation (default)
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
      };

      // Add authentication headers if available
      if (accessID || token) {
        httpOptions.headers = {};
        if (accessID) {
          httpOptions.headers['accessID'] = accessID;
        }
        if (token) {
          httpOptions.headers['token'] = token;
        }
        console.log('📡 [SIGNALR] Adding authentication headers');
      }

      this.signalRConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, httpOptions)
        .withAutomaticReconnect({
          // Configure reconnection delays to reduce unnecessary reconnection attempts
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 10s, 30s, then 30s intervals
            if (retryContext.previousRetryCount === 0) return 0;
            if (retryContext.previousRetryCount === 1) return 2000;
            if (retryContext.previousRetryCount === 2) return 180000;
            return 30000; // Max 30 seconds between retries
          }
        })
        .build();

      // Listen for changes from managers mode
      // Event name: SyncRequired (as per backend SignalR hub)
      this.signalRConnection.on('SyncRequired', (data: any) => {
        console.log('📡 [SIGNALR] ========================================');
        console.log('📡 [SIGNALR] ✅ SyncRequired event received from managers mode!');
        console.log('📡 [SIGNALR] Data received:', JSON.stringify(data, null, 2));
        console.log('📡 [SIGNALR] Data type:', typeof data);
        console.log('📡 [SIGNALR] ========================================');
        
        // Trigger sync when SyncRequired is received
        // The data might contain information about what changed
        this.handleSyncRequired(data);
      });

      // Log when listeners are registered
      console.log('📡 [SIGNALR] Event listeners registered: SyncRequired');
      console.log('📡 [SIGNALR] Waiting for SyncRequired event from backend...');
      console.log('📡 [SIGNALR] To test: Make a change in managers mode and watch for the event');

      // Listen for connection events
      this.signalRConnection.onreconnecting((error) => {
        console.log('📡 [SIGNALR] ⚠️ Reconnecting...', error);
      });

      this.signalRConnection.onreconnected(async (connectionId) => {
        console.log('📡 [SIGNALR] ✅ Reconnected successfully. Connection ID:', connectionId);
        // Register device on reconnection
        const deviceUniqueId = this.sharedDataService.deviceUID;
        if (deviceUniqueId) {
          try {
            await this.signalRConnection.invoke('RegisterDevice', deviceUniqueId);
            console.log('📡 [SIGNALR] Device registered on reconnection:', deviceUniqueId);
          } catch (error) {
            console.error('📡 [SIGNALR] Error registering device on reconnection:', error);
          }
        }
      });

      this.signalRConnection.onclose((error) => {
        console.log('📡 [SIGNALR] ❌ Connection closed', error);
        this.signalRConnection = null;
      });

      // Start connection
      console.log('📡 [SIGNALR] Starting connection...');
      await this.signalRConnection.start();
      
      // Register device after connection starts
      const deviceUniqueId = this.sharedDataService.deviceUID;
      if (deviceUniqueId) {
        try {
          await this.signalRConnection.invoke('RegisterDevice', deviceUniqueId);
          console.log('📡 [SIGNALR] Device registered:', deviceUniqueId);
        } catch (error) {
          console.error('📡 [SIGNALR] Error registering device:', error);
        }
      }
      
      console.log('📡 [SIGNALR] ========================================');
      console.log('📡 [SIGNALR] ✅ CONNECTED SUCCESSFULLY!');
      console.log('📡 [SIGNALR] Connection ID:', this.signalRConnection.connectionId);
      console.log('📡 [SIGNALR] Connection State:', this.signalRConnection.state);
      console.log('📡 [SIGNALR] Hub URL:', hubUrl);
      console.log('📡 [SIGNALR] ========================================');
    } catch (error) {
      console.error('📡 [SIGNALR] ========================================');
      console.error('📡 [SIGNALR] ❌ ERROR CONNECTING TO SIGNALR HUB');
      console.error('📡 [SIGNALR] Error details:', error);
      console.error('📡 [SIGNALR] Error message:', error?.message || 'Unknown error');
      console.error('📡 [SIGNALR] Error stack:', error?.stack);
      console.error('📡 [SIGNALR] ========================================');
      this.signalRConnection = null;
    }
  }

  /**
   * Handle SyncRequired event from SignalR
   * This is called when backend sends SyncRequired event
   */
  private async handleSyncRequired(data: any): Promise<void> {
    try {
      console.log('🔄 [SYNC] SyncRequired received, triggering sync...');
      console.log('🔄 [SYNC] SyncRequired data:', data);
      
      // Trigger the existing sync mechanism
      // This will call GetPendingChanges and then GetSyncData
      await this.checkPendingChanges();
    } catch (error) {
      console.error('❌ [SYNC] Error handling SyncRequired:', error);
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  private async disconnectSignalR(): Promise<void> {
    try {
      if (this.signalRConnection) {
        await this.signalRConnection.stop();
        console.log('📡 [SIGNALR] Disconnected');
        this.signalRConnection = null;
      }
    } catch (error) {
      console.error('❌ [SIGNALR] Error disconnecting from SignalR hub:', error);
    }
  }

  /**
   * Perform full sync in background (similar to initial sync but triggered by changes)
   * Shows a header loader instead of full screen loader
   * Note: isSyncing is already set to true by startBackgroundSync(), so we don't check it here
   * @param changes - Optional array of changes from GetPendingChanges. If provided, uses POST /DeviceSync/GetSyncData with changes.
   */
  private async performFullSyncInBackground(changes?: Array<{
    syncLogID: number;
    changeType: string;
    entityType: string;
    entityID: number;
    createdDate: string;
  }>): Promise<void> {
    // Note: isSyncing is already set to true by startBackgroundSync() before calling this method
    // So we don't need to check or set it here
    
    // Update sync status to show header loader
    this.updateSyncStatus({
      isSyncing: true,
      progress: 0,
      status: 'background_sync_started',
    });

    try {
      // Set flag to indicate this is a background sync (no full screen loader)
      const wasBackgroundSync = this.isBackgroundSync;
      this.isBackgroundSync = true;
      
      try {
        // Perform comparison-based sync (compares API data with DB and only updates what changed)
        // Pass changes if available so it uses POST /DeviceSync/GetSyncData instead of GET /DeviceSync/offline/
        await this.performComparisonBasedSync(changes);
        
        // Update last sync timestamp
        const serverTime = moment().toISOString();
        this.lastSyncTimestamp = serverTime;
        localStorage.setItem(
          EnumService.LocalStorageKeys.SYNC_DATE_TIME,
          serverTime
        );
        
        this.updateSyncStatus({
          isSyncing: false,
          progress: 100,
          status: 'completed',
        });
      } finally {
        // Restore previous background sync flag
        this.isBackgroundSync = wasBackgroundSync;
      }
    } catch (error) {
      console.error('❌ [BACKGROUND SYNC] Error during background sync:', error);
      this.updateSyncStatus({
        isSyncing: false,
        progress: 0,
        status: 'failed',
        error: error?.message || 'Sync failed',
      });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Check for pending changes and sync if available
   */
  public async checkPendingChanges(): Promise<void> {
    // Don't check if already syncing
    if (this.isSyncing) {
      console.log('🔄 [PERIODIC SYNC] Sync already in progress, skipping periodic check');
      return;
    }

    // Check network
    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      console.log('🔄 [PERIODIC SYNC] No network connection, skipping check');
      return;
    }

    const deviceId = this.sharedDataService.deviceUID;
    const lastSyncDate = this.lastSyncTimestamp || moment().subtract(1, 'day').toISOString();

    try {
      console.log('🔄 [PERIODIC SYNC] Checking for pending changes...');
      let pendingChanges: any = await this.offlineApi
        .getPendingChanges(deviceId, lastSyncDate)
        .toPromise();

      // Normalize the response structure - handle both StatusCode/Result and statusCode/result
      if (pendingChanges) {
        // If response has lowercase properties, normalize to uppercase (API returns statusCode/result)
        if (pendingChanges.statusCode !== undefined && pendingChanges.StatusCode === undefined) {
          pendingChanges.StatusCode = pendingChanges.statusCode;
          console.log('🔄 [PERIODIC SYNC] Normalized statusCode -> StatusCode:', pendingChanges.StatusCode);
        }
        if (pendingChanges.result !== undefined && pendingChanges.Result === undefined) {
          pendingChanges.Result = pendingChanges.result;
          console.log('🔄 [PERIODIC SYNC] Normalized result -> Result');
        }
      }

      console.log('🔄 [PERIODIC SYNC] Response structure:', {
        StatusCode: pendingChanges?.StatusCode,
        hasResult: !!pendingChanges?.Result,
        resultType: typeof pendingChanges?.Result,
        resultKeys: pendingChanges?.Result ? Object.keys(pendingChanges.Result) : []
      });

      if (
        pendingChanges.StatusCode !==
        EnumService.ApiResponseCode.RequestSuccessful
      ) {
        console.error('🔄 [PERIODIC SYNC] Failed to get pending changes - StatusCode:', pendingChanges?.StatusCode);
        return;
      }

      // Check if isOfflineEnabled flipped from false to true
      const incomingOfflineEnabled = pendingChanges.Result?.isOfflineEnabled;
      if (incomingOfflineEnabled !== undefined) {
        const currentFlag = localStorage.getItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY);
        const wasDisabled = currentFlag === 'false';
        const isNowEnabled = incomingOfflineEnabled === true;
        if (wasDisabled && isNowEnabled) {
          console.log('🔄 [PERIODIC SYNC] isOfflineEnabled flipped to true — updating localStorage and triggering sync');
          localStorage.setItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY, 'true');
          await this.startBackgroundSync();
          return;
        }
        localStorage.setItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY, String(incomingOfflineEnabled));
      }

      // API returns: { result: { changes: [], serverTime: "..." } }
      // So we need to access Result.changes, not Result directly
      const changes = pendingChanges.Result?.changes || [];
      const serverTime = pendingChanges.Result?.serverTime;
      if (changes.length === 0) {
        console.log('🔄 [PERIODIC SYNC] No pending changes found');
        if (serverTime) {
          this.lastSyncTimestamp = serverTime;
          localStorage.setItem(EnumService.LocalStorageKeys.SYNC_DATE_TIME, serverTime);
        }
        return;
      }

      console.log(`🔄 [PERIODIC SYNC] Found ${changes.length} pending changes, triggering full sync`);
      await this.startBackgroundSync();
    } catch (error) {
      console.error('🔄 [PERIODIC SYNC] Error checking pending changes:', error);
    }
  }

  private getOfflineEntityKey(row: any): string | null {
    const uid = row?.userId;
    if (!uid) return null;
    const loc = parseInt(row?.locationID, 10);
    const proj = parseInt(row?.projectID, 10);
    const inv = parseInt(row?.inventoryItemID, 10);
    if (loc > 0) return `${uid}|L|${loc}`;
    if (proj > 0) return `${uid}|P|${proj}`;
    if (inv > 0) return `${uid}|I|${inv}`;
    return null;
  }

  /**
   * Fallback to old sync API when getSyncData fails
   * Uses the exact same logic as the original callOfflineApi method
   */
  private async performOldSync(): Promise<void> {
    console.log('🔄 [OLD SYNC] Starting legacy sync method...');
    
    // Use deviceUID (same as original callOfflineApi)
    const deviceUID = this.sharedDataService.deviceUID;
    
    if (!deviceUID) {
      throw new Error('Device UID not available for sync');
    }
    
    console.log('🔄 [OLD SYNC] Using device UID:', deviceUID);

    // Show loading indicator
    await this.utilService.presentLoadingWithOptions('Syncing data (legacy method)...');

    try {
      // Step 1: Get device offline details (exact same as original)
      const offlineDetails: any = await this.offlineApi
        .getDeviceOfflineDetails(deviceUID)
        .toPromise();

      if (
        offlineDetails.StatusCode !==
        EnumService.ApiResponseCode.RequestSuccessful
      ) {
        throw new Error('Failed to get device offline details');
      }

      const files = offlineDetails.Result || [];
      if (!files || files.length === 0) {
        throw new Error('No offline files available');
      }

      // Calculate total file sizes (exact same as original)
      let totalSizeWillBeDownloadB = 0;
      let totalSizeWillBeAfterExtract = 0;
      files.forEach((fileDetail) => {
        const zipFileSizeBytes = fileDetail.zipFileSize;
        totalSizeWillBeDownloadB += UtilService.formattedNumberToNumber(zipFileSizeBytes);

        const jsonFileSizeBytes = fileDetail.jsonFileSize;
        const totalFileSize = fileDetail.totalFileSize;
        if (jsonFileSizeBytes) {
          totalSizeWillBeAfterExtract += UtilService.formattedNumberToNumber(jsonFileSizeBytes);
        } else if (totalFileSize) {
          totalSizeWillBeAfterExtract += UtilService.formattedNumberToNumber(totalFileSize);
        }
      });

      // Step 2: Remove old directory (exact same as original)
      const offlineFilesFolderName = localStorage.getItem(
        EnumService.LocalStorageKeys.OFFLINE_FILES_FOLDER_NAME
      );
      
      if (offlineFilesFolderName) {
        await this.filehandlerService.removeDirectory(
          this.filehandlerService.offlineFilesDirectory(),
          offlineFilesFolderName
        ).catch(() => {
          // Ignore errors, continue anyway
        });
      }

      // Step 3: Check available space (exact same as original)
      // Note: Space check is done in the original component, but we'll skip it here
      // as it's not critical for the sync to work and the method is component-specific
      // The original code would check space here, but we'll proceed with download

      // Step 4: Download files (exact same as original)
      const jsonFiles: any = await this.offlineApi
        .getDeviceOfflineFile(files, (zipProgress) => {
          // Progress callback (same as original)
          // Original uses updateProgress(zipProgress, 2) which is 50-75% range
        });

      if (!jsonFiles || !Array.isArray(jsonFiles) || jsonFiles.length === 0) {
        throw new Error('No JSON files downloaded');
      }

      // Store JSON files in localStorage (exact same as original)
      localStorage.setItem(
        EnumService.LocalStorageKeys.OFFLINE_DATA_FILES,
        JSON.stringify(jsonFiles)
      );

      // Step 5: Empty all tables (exact same as original - after download)
      await new Promise<void>((emptyResolve) => {
        this.offlineManager.emptyAllTables(() => {
          emptyResolve();
        });
      });

      // Step 6: Read JSON files sequentially and insert data (exact same as original)
      const readJsonFileAndInsertInDb = async (fileIndex: number): Promise<void> => {
        if (fileIndex >= jsonFiles.length) {
          // All files processed, complete sync
          const deviceId = String(
            this.sharedDataService.dedicatedModeDeviceDetailData?.deviceID || deviceUID
          );
          
          // Step 7: Acknowledge sync (exact same as original)
          await this.offlineApi.synchDevice(deviceId).toPromise();

          // Update sync date time (exact same as original)
          localStorage.setItem(
            EnumService.LocalStorageKeys.SYNC_DATE_TIME,
            moment().toISOString(true)
          );

          // Hide loading indicator
          this.utilService.hideLoading();

          // Show success message
          await this.showSuccessMessage('Sync successful');

          this.updateSyncStatus({
            isSyncing: true,
            progress: 100,
            status: 'completed',
          });

          console.log('✅ [OLD SYNC] Legacy sync completed successfully');
          return;
        }

        const jsonFile = jsonFiles[fileIndex];
        
        // Read JSON file (exact same as original)
        const offlineData: any = await this.filehandlerService.readJsonFile(
          Capacitor.convertFileSrc(jsonFile),
          deviceUID
        );

        // Insert data (exact same as original)
        await new Promise<void>((resolve) => {
          let progressCount = 0;
          this.offlineManager.insertOfflineData(offlineData, (insertionDone) => {
            progressCount += 2;
            // Original uses updateProgress(progressCount, 3) which is 75-100% range
            if (insertionDone) {
              resolve();
            }
          });
        });

        // Process next file recursively (exact same as original)
        await readJsonFileAndInsertInDb(fileIndex + 1);
      };

      // Start processing files from index 0 (exact same as original)
      await readJsonFileAndInsertInDb(0);

    } catch (error) {
      console.error('Old sync error:', error);
      this.utilService.hideLoading();
      // Skip toast for network-abort errors (status 0) — user already sees the offline popup/toast
      if (error?.status !== 0) {
        await this.showErrorMessage(error?.message || 'Sync failed. Please try again.');
      }
      throw error;
    }
  }

  /**
   * Snapshot all pending offline rows from tables that emptyAllTables wipes but
   * insertOfflineData does NOT repopulate from the server.  Called just before the
   * wipe so the rows can be restored afterward with their original primary keys,
   * preserving all cross-table ID references (formSubmitDataId, imageVideoFileId, …).
   */
  private async savePendingOfflineData(): Promise<{ [tableName: string]: any[] }> {
    const result: { [tableName: string]: any[] } = {};

    const queryTable = async (tableName: string, condition?: string): Promise<any[]> => {
      try {
        const sql = condition
          ? `SELECT * FROM ${tableName} WHERE ${condition}`
          : `SELECT * FROM ${tableName}`;
        const res: any = await this.offlineManager.dbQuery(sql, []);
        const rows: any[] = [];
        if (res?.rows?.length > 0) {
          for (let i = 0; i < res.rows.length; i++) {
            const row = res.rows.item ? res.rows.item(i) : res.rows[i];
            rows.push({ ...row });
          }
        }
        return rows;
      } catch (e) {
        console.warn(`[SYNC-PRESERVE] Failed to read ${tableName}:`, e);
        return [];
      }
    };

    // Offline CI/CO rows that have never been pushed to the server (no userCheckInDetailID yet)
    const unSyncedCiCondition =
      `(isOfflineDone = 'true' OR isOfflineDone = 1 OR isOfflineDone = 'TRUE') ` +
      `AND (userCheckInDetailID IS NULL OR userCheckInDetailID = 0 OR userCheckInDetailID = '')`;
    result.DeviceUserCheckinDetails = await queryTable('DeviceUserCheckinDetails', unSyncedCiCondition);
    result.DeviceGuestUserCheckinDetails = await queryTable('DeviceGuestUserCheckinDetails', unSyncedCiCondition);

    // Form data, file references and sign-offs are never re-populated by insertOfflineData
    result.FormSubmitData = await queryTable('FormSubmitData');
    result.ImageVideoFiles = await queryTable('ImageVideoFiles');
    result.SignOffDetails = await queryTable('SignOffDetails');

    // Offline induction archives (forms) are linked to a local FormSubmitData row via formSubmitDataId;
    // server-synced archives have documentID set but no formSubmitDataId.
    result.DeviceArchivedForms = await queryTable(
      'DeviceArchivedForms',
      `formSubmitDataId IS NOT NULL AND formSubmitDataId != '' AND formSubmitDataId != '0' AND CAST(formSubmitDataId AS INTEGER) > 0`
    );

    // Offline induction/sign-off document archives are linked to a local SignOffDetails row
    // via signOffDetailId; server-synced document archives do not have signOffDetailId set.
    result.DeviceArchivedDocuments = await queryTable(
      'DeviceArchivedDocuments',
      `signOffDetailId IS NOT NULL AND signOffDetailId != '' AND signOffDetailId != '0' AND CAST(signOffDetailId AS INTEGER) > 0`
    );

    const totalRows = Object.values(result).reduce((sum, rows) => sum + rows.length, 0);
    console.log(
      `🔒 [SYNC-PRESERVE] Saved ${totalRows} pending offline rows before wipe:`,
      Object.keys(result).reduce((acc, k) => { acc[k] = result[k].length; return acc; }, {} as any)
    );

    return result;
  }

  /**
   * Re-insert rows that were saved by savePendingOfflineData after insertOfflineData
   * has run.  Uses INSERT OR IGNORE with the original primary key so that:
   *   – Internal FK-style references (formSubmitDataId, imageVideoFileId, etc.) remain valid.
   *   – Server rows (inserted by insertOfflineData with higher auto-increment IDs) are not
   *     disturbed, because SQLite AUTOINCREMENT is strictly monotonic and DELETE FROM does
   *     not reset sqlite_sequence, so server rows always get IDs above the old offline max.
   */
  private async restorePendingOfflineData(data: { [tableName: string]: any[] }): Promise<void> {
    const totalRows = Object.values(data).reduce((sum, rows) => sum + rows.length, 0);
    if (totalRows === 0) {
      console.log('ℹ️ [SYNC-PRESERVE] No pending offline data to restore');
      return;
    }
    console.log(`🔓 [SYNC-PRESERVE] Restoring ${totalRows} pending offline rows after sync insert...`);

    const restoreTable = async (tableName: string, rows: any[]): Promise<void> => {
      if (!rows?.length) return;
      let restored = 0;
      for (const row of rows) {
        try {
          const cols = Object.keys(row).filter(
            k => row[k] !== null && row[k] !== undefined && row[k] !== ''
          );
          if (!cols.length) continue;
          const placeholders = cols.map(() => '?').join(', ');
          const vals = cols.map(k => row[k]);
          const query = `INSERT OR IGNORE INTO ${tableName} (${cols.join(', ')}) VALUES (${placeholders})`;
          await this.offlineManager.dbQuery(query, vals);
          restored++;
        } catch (e) {
          console.warn(`[SYNC-PRESERVE] Failed to restore row in ${tableName}:`, e);
        }
      }
      console.log(`✅ [SYNC-PRESERVE] Restored ${restored}/${rows.length} rows in ${tableName}`);
    };

    // Restore in dependency order so that tables referenced by FKs are present first
    await restoreTable('ImageVideoFiles', data.ImageVideoFiles);
    await restoreTable('FormSubmitData', data.FormSubmitData);
    await restoreTable('SignOffDetails', data.SignOffDetails);
    await restoreTable('DeviceArchivedDocuments', data.DeviceArchivedDocuments);
    await restoreTable('DeviceArchivedForms', data.DeviceArchivedForms);
    await restoreTable('DeviceUserCheckinDetails', data.DeviceUserCheckinDetails);
    await restoreTable('DeviceGuestUserCheckinDetails', data.DeviceGuestUserCheckinDetails);
  }
}
